// ============================================================
// THE PORTAL'S OWN WORDS
//
// Every word the portal draws itself lives here in English,
// Spanish and French. Help's answers and the two report forms
// are not in this file: those arrive from the API already in
// the chosen language.
//
// The key is the exact English a screen shows today. A reviewer
// reads each key's Spanish and French side by side in
// translation/portal_words.csv, and a key with no entry falls
// back to the English, so a word added later shows in English
// rather than blank or as a code.
//
// Accented letters, opening marks and French's no-break spaces
// are written as \u escapes, so this file stays ASCII while the
// screen shows correct Spanish and French. The review sheet is
// the file written in real characters, because people read
// that one.
// ============================================================

// The language every screen reads. The root component sets it
// from the one piece of state Step 70 already keeps, during its
// own render, so the choice is in place before any screen below
// renders and choosing Spanish in Settings changes the whole
// portal at once with no reload. A module value rather than a
// context, because a good half of the portal's words are drawn
// by plain helper functions that cannot hold a hook.
let current = "en";

// Every language this file holds words for, each named in its own
// language, in the order the pickers draw them. A language added
// later is one more line here and its table below.
export const LANGUAGE_NAMES = { en: "English", es: "Espa\u00f1ol", fr: "Fran\u00e7ais" };

// The languages the portal offers. English and Spanish always; French
// only once the API lists it (Step 209), so until then no screen shows
// it and no call names it. The list the API last gave is kept on this
// phone, so the first frame and the small script at the top of
// public/index.html know what is offered before the API is asked.
export const LANGUAGES_KEY = "ocsa-staff-languages";
const ALWAYS = ["en", "es"];

// A list is kept when it names English and Spanish, and is cut to the
// languages this file holds words for, in the order it gives them.
// Anything else is null.
export function offeredFrom(list) {
  if (!Array.isArray(list) || list.indexOf("en") === -1 || list.indexOf("es") === -1) return null;
  const out = [];
  list.forEach((code) => {
    if (typeof code === "string" && Object.prototype.hasOwnProperty.call(LANGUAGE_NAMES, code) && out.indexOf(code) === -1) out.push(code);
  });
  return out;
}

function storedOffered() {
  try { return offeredFrom(JSON.parse(window.localStorage.getItem(LANGUAGES_KEY))) || ALWAYS; } catch (e) { return ALWAYS; }
}

let offered = storedOffered();

export function offeredLanguages() {
  return offered;
}

// What the API said it speaks. A list naming English and Spanish is
// offered from here and kept on this phone. null, the API saying it has
// no list, puts back English and Spanish and forgets a list kept from an
// earlier answer, so the next start does not offer what the API no
// longer lists. The language on the screen falls to English here when
// it is no longer offered; the portal then moves it to the phone's.
export function setOfferedLanguages(list) {
  const next = offeredFrom(list);
  offered = next || ALWAYS;
  try {
    if (next) window.localStorage.setItem(LANGUAGES_KEY, JSON.stringify(next));
    else if (window.localStorage.getItem(LANGUAGES_KEY) !== null) window.localStorage.removeItem(LANGUAGES_KEY);
  } catch (e) {}
  if (!isOffered(current)) current = "en";
}

export function isOffered(lang) {
  return offered.indexOf(lang) !== -1;
}

// A language that is not offered falls to English, the way anything
// but Spanish always has.
export function setWordsLanguage(lang) {
  current = isOffered(lang) ? lang : "en";
}

export function wordsLanguage() {
  return current;
}

// The language a call names: the one given, or with none the one on
// the screen, when it is offered, and English otherwise. Every path
// that sends a language asks here, so none can send one the API has
// not listed.
export function languageToSend(lang) {
  const v = lang === undefined ? current : lang;
  return isOffered(v) ? v : "en";
}

// English is the key, so there is no English table.
export const WORDS = {
  es: {
  // Sign in
  "2155550000 (no dashes needed)": "2155550000 (sin guiones)",
  "4 digits. Not all the same, not in a row like 1234, and not your badge number. The PIN you were given works until you save a new one.": "4 d\u00edgitos. No todos iguales, no seguidos como 1234 y no su n\u00famero de empleado. El PIN que le dieron funciona hasta que guarde uno nuevo.",
  "4-digit PIN": "PIN de 4 d\u00edgitos",
  "9001, 2155550101 or name@email.com": "9001, 2155550101 o nombre@correo.com",
  "Account Activation": "Activaci\u00f3n de cuenta",
  "Activate Account": "Activar cuenta",
  "Activating...": "Activando...",
  "After too many wrong tries, sign-in stops for a while. Ask your supervisor for help.": "Despu\u00e9s de demasiados intentos equivocados, el inicio de sesi\u00f3n se detiene por un tiempo. P\u00eddale ayuda a su supervisor.",
  "and can be used once.": "y se puede usar una sola vez.",
  "Ask your supervisor to confirm your badge number.": "Pida a su supervisor que confirme su n\u00famero de empleado.",
  "Back to Login": "Volver al inicio de sesi\u00f3n",
  "Back to Sign In": "Volver al inicio de sesi\u00f3n",
  "Badge Number": "N\u00famero de empleado",
  "Badge number": "N\u00famero de empleado",
  "Badge Number, Phone or Email": "N\u00famero de empleado, tel\u00e9fono o correo",
  "Checking your link...": "Revisando su enlace...",
  "Choose your new 4-digit PIN.": "Elija su nuevo PIN de 4 d\u00edgitos.",
  "Confirm PIN": "Confirmar PIN",
  "Confirm PIN *": "Confirmar PIN *",
  "Confirm your badge number and choose your 4-digit PIN.": "Confirme su n\u00famero de empleado y elija su PIN de 4 d\u00edgitos.",
  "Contact your supervisor to have a new link sent.": "Hable con su supervisor para que le env\u00eden un enlace nuevo.",
  "Could not reach OCSA. Check your connection and try again.": "No se pudo conectar con OCSA. Revise su conexi\u00f3n e intente de nuevo.",
  "Dark Mode": "Modo oscuro",
  "Email Address": "Correo electr\u00f3nico",
  "Email Address *": "Correo electr\u00f3nico *",
  "Enter the badge number, phone number or email address on your account and we will email you a link to choose a new PIN.": "Escriba el n\u00famero de empleado, tel\u00e9fono o correo de su cuenta y le enviaremos un enlace para elegir un PIN nuevo.",
  "Enter your badge number, phone number or email address.": "Escriba su n\u00famero de empleado, tel\u00e9fono o correo electr\u00f3nico.",
  "Fill in {field}.": "Complete {field}.",
  "First Name": "Nombre",
  "First name": "Nombre",
  "First Name *": "Nombre *",
  "Forgot your PIN?": "\u00bfOlvid\u00f3 su PIN?",
  "Forgotten your badge number? You can also sign in with your phone number or your email address.": "\u00bfOlvid\u00f3 su n\u00famero de empleado? Tambi\u00e9n puede entrar con su tel\u00e9fono o su correo electr\u00f3nico.",
  "Got a sign-in slip from OCSA? Go back and sign in with it.": "\u00bfLe dieron una hoja para iniciar sesi\u00f3n? Regrese e inicie sesi\u00f3n con ella.",
  "If that matches an account on file, a reset link is on its way. The link is good for one hour.": "Si eso coincide con una cuenta registrada, le enviamos un enlace. El enlace sirve por una hora.",
  "If you do not have an email address on file, no link can reach you. Contact your supervisor to have your PIN reset directly.": "Si no tiene un correo registrado, no hay c\u00f3mo enviarle el enlace. Hable con su supervisor para que le restablezca el PIN directamente.",
  "Language": "Idioma",
  "Last Name": "Apellido",
  "Last name": "Apellido",
  "Light Mode": "Modo claro",
  "Loading...": "Cargando...",
  "name@email.com": "nombre@correo.com",
  "New Employee? Register Here": "\u00bfEmpleado nuevo? Reg\u00edstrese aqu\u00ed",
  "New PIN (4 digits)": "PIN nuevo (4 d\u00edgitos)",
  "New Staff Registration": "Registro de personal nuevo",
  "No email on file? A link cannot reach you. Ask your supervisor to reset your PIN.": "\u00bfNo tiene correo registrado? No le puede llegar un enlace. Pida a su supervisor que restablezca su PIN.",
  "Not you? Sign out": "\u00bfNo es usted? Cerrar sesi\u00f3n",
  "Phone Number": "N\u00famero de tel\u00e9fono",
  "Phone Number *": "N\u00famero de tel\u00e9fono *",
  "PIN": "PIN",
  "PIN (4 digits)": "PIN (4 d\u00edgitos)",
  "PIN (4 digits) *": "PIN (4 d\u00edgitos) *",
  "PIN saved. Signing you in...": "PIN guardado. Iniciando su sesi\u00f3n...",
  "PIN set. Signing you in...": "PIN guardado. Iniciando su sesi\u00f3n...",
  "Register": "Registrarse",
  "Registering...": "Registrando...",
  "Request a New Link": "Pedir un enlace nuevo",
  "Reset Your PIN": "Restablecer su PIN",
  "Save PIN": "Guardar PIN",
  "Saving...": "Guardando...",
  "Send Reset Link": "Enviar enlace",
  "Sending...": "Enviando...",
  "Set your own PIN. The PIN you were given is known to your supervisor. Choose a new one that only you know.": "Elija su propio PIN. Su supervisor conoce el PIN que le dieron. Elija uno nuevo que solo usted sepa.",
  "Choose your PIN": "Elija su PIN",
  "Sign In": "Iniciar sesi\u00f3n",
  "Signing in will work once your account is active.": "Podr\u00e1 iniciar sesi\u00f3n cuando su cuenta est\u00e9 activa.",
  "Signing in...": "Iniciando sesi\u00f3n...",
  "Enter the code we emailed to {0}": "Escriba el c\u00f3digo que enviamos a {0}",
  "Verify": "Verificar",
  "Send a new code": "Enviar un c\u00f3digo nuevo",
  "Remember this device for 30 days": "Recordar este dispositivo por 30 d\u00edas",
  "{n} tries left": "Le quedan {n} intentos",
  "1 try left": "Le queda 1 intento",
  "Something went wrong on our end. Try again in a minute.": "Hubo un problema de nuestro lado. Intente de nuevo en un minuto.",
  "Staff Operations Portal": "Portal del Personal",
  "The number on the email we sent you.": "El n\u00famero que aparece en el correo que le enviamos.",
  "The two PINs do not match. Type the same 4 digits in both fields.": "Los dos PIN no coinciden. Escriba los mismos 4 d\u00edgitos en las dos casillas.",
  "This activation link is incomplete. Open the link from your email again.": "Este enlace de activaci\u00f3n est\u00e1 incompleto. Abra el enlace de su correo otra vez.",
  "This link is no longer valid. Links expire, and each one can only be used once.": "Este enlace ya no sirve. Los enlaces vencen y cada uno se puede usar una sola vez.",
  "This link works until": "Este enlace sirve hasta",
  "This reset link is incomplete. Open the link from your email again.": "Este enlace de restablecimiento est\u00e1 incompleto. Abra el enlace de su correo otra vez.",
  "Try Again": "Intentar de nuevo",
  "Welcome back, {name}.": "Bienvenido de nuevo, {name}.",
  "Welcome, {name}.": "Bienvenido, {name}.",
  "Your account is activated but not currently active. Contact your supervisor.": "Su cuenta est\u00e1 activada pero no est\u00e1 activa en este momento. Hable con su supervisor.",
  "Your PIN has been changed. Contact your supervisor about your account status.": "Su PIN fue cambiado. Hable con su supervisor sobre el estado de su cuenta.",
  "Your PIN has been saved. Signing in will work once your account is active.": "Su PIN se guard\u00f3. Podr\u00e1 iniciar sesi\u00f3n cuando su cuenta est\u00e9 activa.",
  "PIN must be exactly 4 digits.": "El PIN debe tener exactamente 4 d\u00edgitos.",
  "Enter the badge number from your email.": "Escriba el n\u00famero de empleado que aparece en su correo.",

  // App wide
  "A new version is ready": "Hay una versi\u00f3n nueva lista",
  "Add {app} to your home screen": "Agregue {app} a su pantalla de inicio",
  "Confirm on the next screen.": "Confirme en la siguiente pantalla.",
  "Copied": "Copiado",
  "Copy address": "Copiar la direcci\u00f3n",
  "Could not find your open shift. Reload and try again.": "No se encontr\u00f3 su turno abierto. Recargue e intente de nuevo.",
  "Don't show again": "No mostrar m\u00e1s",
  "Edit shortcuts": "Editar accesos directos",
  "End your shift at {site}?": "\u00bfTerminar su turno en {site}?",
  "Extra large": "M\u00e1s grande",
  "Install": "Instalar",
  "Issue reported": "Problema reportado",
  "It opens like an app, one tap from your home screen.": "Se abre como una aplicaci\u00f3n, con un toque desde su pantalla de inicio.",
  "Large": "Grande",
  "Largest": "El m\u00e1s grande",
  "Low stock alert!": "\u00a1Aviso de poco inventario!",
  "More": "M\u00e1s",
  "Not now": "Ahora no",
  "Notifications": "Notificaciones",
  "ON SITE": "EN SITIO",
  "Open the browser menu. It is usually three dots at the top right.": "Abra el men\u00fa del navegador. Casi siempre son tres puntos arriba a la derecha.",
  "PIN updated": "PIN actualizado",
  "Profile": "Perfil",
  "Reload": "Volver a cargar",
  "Request submitted": "Solicitud enviada",
  "Scroll down and tap Add to Home Screen.": "Baje y toque Agregar a la pantalla de inicio.",
  "Select a site first": "Primero elija un sitio",
  "Settings": "Ajustes",
  "Shift ended": "Turno terminado",
  "Shift ended at {time}": "Turno terminado a las {time}",
  "Shift started": "Turno iniciado",
  "Shortcuts saved": "Accesos directos guardados",
  "Sign out": "Cerrar sesi\u00f3n",
  "Something went wrong on this screen.": "Algo sali\u00f3 mal en esta pantalla.",
  "Standard": "Normal",
  "Tap Add or Install.": "Toque Agregar o Instalar.",
  "Tap Add to Home screen or Install app.": "Toque Agregar a la pantalla de inicio o Instalar aplicaci\u00f3n.",
  "Tap Add to Home Screen.": "Toque Agregar a la pantalla de inicio.",
  "Tap Add.": "Toque Agregar.",
  "Tap Install below.": "Toque Instalar abajo.",
  "Tap Share {share} at the bottom of the screen. If you do not see it, tap the three dots first, then Share.": "Toque Compartir {share} abajo en la pantalla. Si no lo ve, toque primero los tres puntos y luego Compartir.",
  "Tap the Share button {share} in the address bar.": "Toque el bot\u00f3n Compartir {share} en la barra de direcciones.",
  "Task completed": "Tarea completada",
  "Task unchecked": "Tarea desmarcada",
  "Task updated to {status}": "Estado de la tarea: {status}",
  "That sign-in did not match. Check your badge, phone or email and your PIN.": "Esos datos no coinciden. Revise su n\u00famero de empleado, tel\u00e9fono o correo y su PIN.",
  "This list did not load.": "Esta lista no se carg\u00f3.",
  "This page is open inside another app. Open it in Safari or Chrome first, then add it to your home screen.": "Esta p\u00e1gina est\u00e1 abierta dentro de otra aplicaci\u00f3n. \u00c1brala primero en Safari o Chrome y luego agr\u00e9guela a su pantalla de inicio.",
  "This shift was already ended": "Este turno ya estaba terminado",
  "this site": "este sitio",
  "Update now": "Actualizar ahora",
  "Welcome, {name}": "Bienvenido, {name}",
  "{count} unread notifications": "{count} notificaciones sin leer",
  "{brand} Staff Portal": "Portal del Personal de {brand}",
  "Could not read image": "No se pudo leer la imagen",
  "Compression failed": "No se pudo preparar la foto",
  "Custodian": "Conserje",
  "Custodial Lead": "L\u00edder de conserjes",
  "Lead": "L\u00edder",
  "Supervisor": "Supervisor",
  "Admin": "Administrador",
  "Custodial Laborer": "Conserje",
  "Day Porter": "Conserje de d\u00eda",
  "Contractor": "Contratista",
  "in progress": "en proceso",
  "resolved": "resuelta",
  "unable to resolve": "sin resolver",

  // Home
  "All Other Sites": "Todos los dem\u00e1s sitios",
  "Choose a Site to Start": "Elija un sitio para comenzar",
  "Drop requested": "Pidi\u00f3 dejarlo",
  "End Shift": "Terminar turno",
  "Ending...": "Terminando...",
  "Floor {n}": "Piso {n}",
  "Loading sites...": "Cargando sitios...",
  "No sites available yet.": "Todav\u00eda no hay sitios disponibles.",
  "requested": "solicitada",
  "Scheduled Today": "Programado para hoy",
  "Shift Open at {site}": "Turno abierto en {site}",
  "Start Shift": "Comenzar turno",
  "Start Shift at {site}": "Comenzar turno en {site}",
  "Started at {time}": "Comenz\u00f3 a las {time}",
  "Started {day} at {time}": "Comenz\u00f3 el {day} a las {time}",
  "Starting...": "Comenzando...",
  "Time off": "Tiempo libre",
  "Time on Site": "Tiempo en el sitio",
  "You asked to drop this shift. Waiting for a decision.": "Usted pidi\u00f3 dejar este turno. Espera una decisi\u00f3n.",
  "Your Assigned Sites": "Sus sitios asignados",
  "A shift is still open at {site}. End it before starting another.": "Todav\u00eda hay un turno abierto en {site}. Term\u00ednelo antes de comenzar otro.",
  "A shift is still open. End it before starting another.": "Todav\u00eda hay un turno abierto. Term\u00ednelo antes de comenzar otro.",
  "Your sites did not load.": "Sus sitios no se cargaron.",

  // Schedule
  " (live)": " (en curso)",
  " ({h}h)": " ({h} h)",
  "Any additional details...": "Cualquier detalle adicional...",
  "approved": "aprobado",
  "Approved": "Aprobada",
  "Asked": "Fecha de solicitud",
  "Bereavement and personal": "Duelo y asuntos personales",
  "Building": "Edificio",
  "Cancel": "Cancelar",
  "Cancel request": "Cancelar solicitud",
  "Cancel this time off request?": "\u00bfCancelar esta solicitud de tiempo libre?",
  "Cancelled": "Cancelada",
  "Choose a type": "Elija un tipo",
  "claimed": "tomado",
  "Close": "Cerrar",
  "Completed": "Completado",
  "Dates": "Fechas",
  "Decided": "Fecha de decisi\u00f3n",
  "Denied": "Denegada",
  "Describe the reason": "Describa el motivo",
  "Drop Request": "Solicitud para dejar el turno",
  "Drop request failed": "No se pudo enviar la solicitud",
  "Drop request sent. Your supervisor will review it.": "Solicitud enviada. Su supervisor la revisar\u00e1.",
  "Duration": "Duraci\u00f3n",
  "Emergency": "Emergencia",
  "End": "Fin",
  "First day": "Primer d\u00eda",
  "Floor": "Piso",
  "For example, 8": "Por ejemplo, 8",
  "From": "Desde",
  "Hours": "Horas",
  "Hours (optional)": "Horas (opcional)",
  "Jury duty": "Servicio de jurado",
  "Keep it": "Conservarla",
  "Last day": "\u00daltimo d\u00eda",
  "Military leave": "Licencia militar",
  "Month": "Mes",
  "My Schedule": "Mi horario",
  "My time off": "Mi tiempo libre",
  "N/A": "No disponible",
  "No note": "Sin nota",
  "No reason given": "No dio un motivo",
  "No time off requests yet.": "Todav\u00eda no tiene solicitudes de tiempo libre.",
  "Not given": "No indicado",
  "Note": "Nota",
  "Note: {note}": "Nota: {note}",
  "Notes": "Notas",
  "Notes (optional)": "Notas (opcional)",
  "Nothing scheduled this day.": "No hay nada programado este d\u00eda.",
  "On Site": "En sitio",
  "Only you and the person who approves time off can read this.": "Solo usted y la persona que aprueba el tiempo libre pueden leer esto.",
  "Other": "Otro",
  "Paid sick leave": "Licencia por enfermedad pagada",
  "Part of the day": "Parte del d\u00eda",
  "Personal": "Asunto personal",
  "Pickup": "Turnos libres",
  "Pickup Shift": "Turno tomado",
  "Reason": "Motivo",
  "Reason (optional)": "Motivo (opcional)",
  "Request time off": "Solicitar tiempo libre",
  "Request to Drop This Shift": "Pedir dejar este turno",
  "Requested": "Solicitada",
  "Scheduled": "Programado",
  "Scheduled Shift": "Turno programado",
  "Scheduling Conflict": "Conflicto de horario",
  "Send request": "Enviar solicitud",
  "Service": "Servicio",
  "Shift End": "Fin del turno",
  "Shift Start": "Inicio del turno",
  "Shifts on these days": "Turnos en estos d\u00edas",
  "Showing your latest 50 requests.": "Mostrando sus 50 solicitudes m\u00e1s recientes.",
  "Sick": "Enfermedad",
  "Site": "Sitio",
  "Specify Reason": "Indique el motivo",
  "Start": "Inicio",
  "Status": "Estado",
  "Still on site": "Todav\u00eda en sitio",
  "Submit Request": "Enviar solicitud",
  "Time": "Hora",
  "Time off request": "Solicitud de tiempo libre",
  "Time off request cancelled.": "Solicitud de tiempo libre cancelada.",
  "Time off requested. You get a notice when it is decided.": "Tiempo libre solicitado. Recibir\u00e1 un aviso cuando se decida.",
  "To": "Hasta",
  "Today": "Hoy",
  "Type": "Tipo",
  "Unpaid time off": "Tiempo libre sin pago",
  "Week": "Semana",
  "Worked": "Trabajado",
  "Worked Shift": "Turno trabajado",
  "You get a notice in the app when your request is decided.": "Recibir\u00e1 un aviso en la aplicaci\u00f3n cuando se decida su solicitud.",
  "Your supervisor will review this request. If approved, the shift will be opened for pickup or reassigned.": "Su supervisor revisar\u00e1 esta solicitud. Si la aprueba, el turno se abrir\u00e1 para que otra persona lo tome o se reasignar\u00e1.",
  "{h}h {m}m": "{h} h {m} min",
  "{n} hour": "{n} hora",
  "{n} hours": "{n} horas",
  "{start} to {end}": "{start} a {end}",
  "{start} to {end}, {year}": "{start} a {end}, {year}",
  "Pending": "Pendiente",
  "Claimed": "Tomado",
  "Filled": "Cubierto",

  // Tasks
  "Back to checklist": "Volver a la lista",
  "Due Date:": "Fecha l\u00edmite:",
  "Instructions": "Instrucciones",
  "Loading tasks...": "Cargando tareas...",
  "Mark Complete": "Marcar como completada",
  "Mark {name} done": "Marcar {name} como completada",
  "Mark {name} not done": "Desmarcar {name}",
  "No checklist is set up for this building yet.": "Este edificio todav\u00eda no tiene una lista de tareas.",
  "No tasks loaded. Start your shift at a site to see your checklist.": "No hay tareas. Comience su turno en un sitio para ver su lista.",
  "PRIORITY": "PRIORIDAD",
  "Reference Photo": "Foto de referencia",
  "Reference Video": "Video de referencia",
  "Start your shift to see and check off your tasks.": "Comience su turno para ver y marcar sus tareas.",
  "Task reference": "Referencia de la tarea",
  "Time:": "Hora:",
  "Try again": "Intentar de nuevo",
  "Uncheck Task": "Desmarcar tarea",
  "Your Assignment": "Su asignaci\u00f3n",
  "Your tasks did not load.": "Sus tareas no cargaron.",
  "General": "General",
  "Which shift are you working?": "\u00bfQu\u00e9 turno est\u00e1 trabajando?",
  "Chosen by the time you started. Change it if it is wrong.": "Elegido seg\u00fan la hora en que empez\u00f3. C\u00e1mbielo si no es correcto.",
  "Use this shift": "Usar este turno",
  "Change shift": "Cambiar turno",
  "This week": "Esta semana",
  "Every two weeks": "Cada dos semanas",
  "This month": "Este mes",
  "This quarter": "Este trimestre",
  "This season": "Esta temporada",
  "As needed": "Seg\u00fan se necesite",
  "{done} of {total} done": "{done} de {total} hechas",
  "Done today by {firstName}": "Hecho hoy por {firstName}",
  "Done yesterday by {firstName}": "Hecho ayer por {firstName}",
  "Done {weekday} by {firstName}": "Hecho el {weekday} por {firstName}",
  "Done {date} by {firstName}": "Hecho el {date} por {firstName}",
  "Checked by {firstName}": "Marcado por {firstName}",
  "Only the person who checked this can uncheck it.": "Solo la persona que marc\u00f3 esta tarea puede desmarcarla.",

  // Chat
  "Admin (Private)": "Administraci\u00f3n (privado)",
  "No chats are set up for you yet. Ask your supervisor.": "Todav\u00eda no hay chats para usted. Pregunte a su supervisor.",
  "No messages yet.": "Todav\u00eda no hay mensajes.",
  "No one else can read this chat.": "Nadie m\u00e1s puede leer este chat.",
  "Only you and management can see these messages": "Solo usted y la administraci\u00f3n ven estos mensajes",
  "Pick a chat at the top first.": "Primero elija un chat arriba.",
  "Pick a chat to start.": "Elija un chat para empezar.",
  "Private chats": "Chats privados",
  "Private conversation with admin.": "Conversaci\u00f3n privada con la administraci\u00f3n.",
  "Private message to admin...": "Mensaje privado a la administraci\u00f3n...",
  "Tag someone": "Etiquetar a alguien",
  "Type a message...": "Escriba un mensaje...",
  "You cannot send messages in this chat.": "No puede enviar mensajes en este chat.",
  "Your chats did not load. Try again in a minute.": "Sus chats no cargaron. Intente de nuevo en un minuto.",
  "Your message was not sent.": "Su mensaje no fue enviado.",
  "New message": "Nuevo mensaje",
  "Office": "Oficina",
  "That chat did not open. Try again.": "Ese chat no se abri\u00f3. Intente de nuevo.",
  "Direct messages": "Mensajes directos",

  // Help
  "Add a photo": "Agregar una foto",
  "Based on": "Seg\u00fan",
  "Picture of the screen": "Imagen de la pantalla",
  "Describe what happened": "Describa lo que pas\u00f3",
  "Discard": "Descartar",
  "Discard this report? It will not be sent.": "\u00bfDescartar este reporte? No se enviar\u00e1.",
  "Fill in form": "Llenar formulario",
  "general cleaning guidance": "una gu\u00eda general de limpieza",
  "Not sent.": "No se envi\u00f3.",
  "Remove photo": "Quitar foto",
  "Report discarded.": "Reporte descartado.",
  "Report in progress": "Reporte en proceso",
  "Report submitted.": "Reporte enviado.",
  "Resume": "Continuar",
  "Retry": "Reintentar",
  "Send": "Enviar",
  "Still needed before you can submit:": "Falta esto antes de poder enviar:",
  "Submit report": "Enviar reporte",
  "Submitting...": "Enviando...",
  "Tell me what happened and I will tell you what to do.": "Cu\u00e9nteme qu\u00e9 pas\u00f3 y le digo qu\u00e9 hacer.",
  "Thanks. This helps Help get better.": "Gracias. Esto ayuda a mejorar la Ayuda.",
  "the ADP guide": "la gu\u00eda de ADP",
  "the app guide": "la gu\u00eda de la aplicaci\u00f3n",
  "The connection dropped. Your answer is saved.": "Se perdi\u00f3 la conexi\u00f3n. La respuesta qued\u00f3 guardada.",
  "This photo could not be read here. Choose a JPEG or PNG, or take a screenshot of it.": "Esta foto no se pudo leer aqu\u00ed. Elija un JPEG o PNG, o tome una captura de pantalla.",
  "Unfinished reports": "Reportes sin terminar",
  "Unfinished reports did not load.": "Los reportes sin terminar no se cargaron.",
  "Uploading...": "Subiendo...",
  "Was this helpful?": "\u00bfLe sirvi\u00f3?",
  "What was missing?": "\u00bfQu\u00e9 falt\u00f3?",
  "Working from the written procedure only right now.": "Por ahora solo se est\u00e1 usando el procedimiento escrito.",
  "x": "x",
  "You can send up to 3 photos with one message.": "Puede enviar hasta 3 fotos en un mensaje.",
  "{answered} of {total} answered": "{answered} de {total} respondidas",

  // Assigned
  "A photo of the completed task is required": "Se necesita una foto de la tarea completada",
  "Assigned": "Asignadas",
  "Assigned by": "Asignada por",
  "Assigned Tasks": "Tareas asignadas",
  "at {time}": "a las {time}",
  "Attached Photo": "Foto adjunta",
  "Attached Video": "Video adjunto",
  "Back to assigned tasks": "Volver a las tareas asignadas",
  "Cannot Resolve": "No se puede resolver",
  "Describe the issue preventing completion...": "Describa lo que impide completarla...",
  "Describe the steps you took...": "Describa los pasos que sigui\u00f3...",
  "Describe what you did to complete this task": "Describa lo que hizo para completar esta tarea",
  "Description": "Descripci\u00f3n",
  "Due Date": "Fecha l\u00edmite",
  "Due:": "Para:",
  "Explain why this cannot be completed *": "Explique por qu\u00e9 no se puede completar *",
  "In Progress": "En proceso",
  "ISSUE": "PROBLEMA",
  "Mark as Resolved": "Marcar como resuelta",
  "No assigned tasks right now.": "No hay tareas asignadas en este momento.",
  "Photo must be under 10MB": "La foto debe pesar menos de 10 MB",
  "Photo of completed task *": "Foto de la tarea completada *",
  "Please provide a reason": "Indique un motivo",
  "Preview": "Vista previa",
  "Reported by": "Reportada por",
  "Required to verify completion": "Se necesita para comprobar que se complet\u00f3",
  "Resolved": "Resuelta",
  "Submit": "Enviar",
  "Submit Resolution": "Enviar resoluci\u00f3n",
  "Take Photo of Completed Task": "Tomar foto de la tarea completada",
  "Task": "Tarea",
  "What did you do to complete this? *": "\u00bfQu\u00e9 hizo para completarla? *",
  "When a supervisor assigns a task to you, it will appear here.": "Cuando un supervisor le asigne una tarea, aparecer\u00e1 aqu\u00ed.",
  "Unable to resolve": "Sin resolver",
  "Medium": "Media",
  "Critical": "Cr\u00edtica",

  // Report
  "+ Report": "+ Reportar",
  "Additional details...": "Detalles adicionales...",
  "Brief description": "Descripci\u00f3n breve",
  "Details": "Detalles",
  "e.g. Restroom, Lobby": "por ejemplo: ba\u00f1o, recepci\u00f3n",
  "Enter issue title": "Escriba un t\u00edtulo para el problema",
  "High": "Alta",
  "Issues": "Problemas",
  "JPG, PNG up to 10MB": "JPG o PNG hasta 10 MB",
  "Low": "Baja",
  "Med": "Media",
  "No issues reported yet.": "Todav\u00eda no se ha reportado ning\u00fan problema.",
  "Photo": "Foto",
  "Photo attached:": "Foto adjunta:",
  "Report an Issue": "Reportar un problema",
  "Select a site": "Elija un sitio",
  "Select site...": "Elija un sitio...",
  "Severity": "Gravedad",
  "Submit Issue": "Enviar problema",
  "Take Photo or Choose from Gallery": "Tomar foto o elegir de la galer\u00eda",
  "Title": "T\u00edtulo",
  "Zone": "Zona",
  "Open": "Abierto",
  "Closed": "Cerrado",

  // Supplies
  "+ Request": "+ Solicitar",
  "Damage Report": "Reporte de da\u00f1o",
  "Describe the request...": "Describa la solicitud...",
  "Enter the item name": "Escriba el nombre del art\u00edculo",
  "Item": "Art\u00edculo",
  "Item Name": "Nombre del art\u00edculo",
  "Log Usage": "Registrar uso",
  "Log usage or submit a request": "Registre el uso o env\u00ede una solicitud",
  "LOW": "POCO",
  "New Gear": "Equipo nuevo",
  "No supplies are set up for this site.": "No hay suministros registrados para este sitio.",
  "One less": "Uno menos",
  "One more": "Uno m\u00e1s",
  "New Supply": "Suministro nuevo",
  "Normal": "Normal",
  "QR": "QR",
  "Refill": "Reposici\u00f3n",
  "Request Type": "Tipo de solicitud",
  "Select a request type": "Elija un tipo de solicitud",
  "Start your shift to log usage. Requests can be submitted anytime.": "Comience su turno para registrar el uso. Las solicitudes se pueden enviar en cualquier momento.",
  "Supplies": "Suministros",
  "Supply Item": "Art\u00edculo",
  "Supply Tracking": "Control de suministros",
  "Supply/Gear Request": "Solicitud de suministros o equipo",
  "This Shift's Log": "Registro de este turno",
  "Urgency": "Urgencia",
  "Urgent": "Urgente",
  "What do you need?": "\u00bfQu\u00e9 necesita?",

  // Pickup
  "Approved. You are scheduled for this shift.": "Aprobado. Est\u00e1 programado para este turno.",
  "Available": "Disponibles",
  "Bldg:": "Edificio:",
  "Callout": "Ausencia",
  "Check back later for available pickup shifts at your assigned sites.": "Vuelva m\u00e1s tarde para ver turnos libres en los sitios que tiene asignados.",
  "Claim This Shift": "Tomar este turno",
  "Claiming...": "Tomando...",
  "Extra Coverage": "Cobertura adicional",
  "Floor:": "Piso:",
  "My Pickups": "Los m\u00edos",
  "New Shift": "Turno nuevo",
  "No claimed shifts": "No tiene turnos tomados",
  "No open shifts right now": "No hay turnos libres en este momento",
  "No-Show": "No se present\u00f3",
  "Release Shift": "Soltar turno",
  "Release this shift? It will go back to the open pool for someone else to claim.": "\u00bfSoltar este turno? Volver\u00e1 a la lista abierta para que otra persona lo tome.",
  "Shift claimed (overtime warning: {hours}h this week)": "Turno tomado (aviso de horas extra: {hours} h esta semana)",
  "Shift claimed successfully!": "\u00a1Turno tomado!",
  "Shift Pickup Board": "Turnos libres",
  "Shift released": "Turno soltado",
  "Shifts you claim will appear here.": "Los turnos que tome aparecer\u00e1n aqu\u00ed.",
  "Start your shift at the normal time and your daily tasks will load automatically.": "Comience su turno a la hora de siempre y sus tareas del d\u00eda cargar\u00e1n solas.",
  "URGENT": "URGENTE",
  "Voluntary Drop": "Turno dejado",
  "Waiting for manager approval": "Esperando la aprobaci\u00f3n del gerente",

  // Inspect
  "+ Schedule": "+ Programar",
  "Attach Photo": "Adjuntar foto",
  "General observations, follow-ups needed, etc.": "Observaciones generales, seguimientos pendientes, etc.",
  "Inspection scheduled": "Inspecci\u00f3n programada",
  "Inspection sent. {n} problem reported for fixing.": "Inspecci\u00f3n enviada. {n} problema reportado para arreglar.",
  "Inspection sent. {n} problems reported for fixing.": "Inspecci\u00f3n enviada. {n} problemas reportados para arreglar.",
  "Inspection submitted": "Inspecci\u00f3n enviada",
  "Inspections assigned to you will appear here.": "Las inspecciones que le asignen aparecer\u00e1n aqu\u00ed.",
  "My Inspections": "Mis inspecciones",
  "Needs a fix": "Necesita arreglo",
  "No inspections pending": "No hay inspecciones pendientes",
  "Not due yet": "Todav\u00eda no corresponde",
  "Not reported: {0}": "Sin reportar: {0}",
  "Notes for this item (optional)": "Notas para este punto (opcional)",
  "Overall Notes": "Notas generales",
  "Photo attached": "Foto adjunta",
  "pts": "pts",
  "Running total": "Total acumulado",
  "Say what needs fixing": "Diga qu\u00e9 hay que arreglar",
  "Schedule Inspection": "Programar inspecci\u00f3n",
  "Scheduled Date *": "Fecha programada *",
  "Scheduling...": "Programando...",
  "Scored": "Calificados",
  "Select template...": "Elija una plantilla...",
  "Show {0} scored items": "Mostrar {0} puntos calificados",
  "Sections": "Secciones",
  "{scored} of {total} scored": "{scored} de {total} calificados",
  "Site *": "Sitio *",
  "Submit Inspection": "Enviar inspecci\u00f3n",
  "Photos": "Fotos",
  "Photos of the whole inspection": "Fotos de toda la inspecci\u00f3n",
  "Below 70 percent: add a photo of it.": "Menos del 70 por ciento: agregue una foto.",
  "Signed as {name}, {date}": "Firmado como {name}, {date}",
  "Sign before you send.": "Firme antes de enviar.",
  "Tap an inspection to begin scoring.": "Toque una inspecci\u00f3n para empezar a calificar.",
  "Tap to start scoring": "Toque para empezar a calificar",
  "Template *": "Plantilla *",
  "Template, site, and date are required": "Se necesitan la plantilla, el sitio y la fecha",

  // Speak Up
  "Anyone you pick below will not be able to see this report.": "Nadie que usted elija abajo podr\u00e1 ver este reporte.",
  "Is this about someone in management?": "\u00bfEs sobre alguien de la gerencia?",
  "No": "No",
  "No one matches that name.": "Nadie tiene ese nombre.",
  "Nothing you write here is kept. If you leave this screen before you send, it is gone.": "Nada de lo que escriba aqu\u00ed se guarda. Si sale de esta pantalla antes de enviar, se pierde.",
  "Optional": "Opcional",
  "Please try again.": "Intente de nuevo.",
  "Quote this if you follow up.": "Menci\u00f3nelo si vuelve a preguntar.",
  "Report a problem with someone": "Reportar un problema con alguien",
  "Search by name": "Buscar por nombre",
  "Someone will be in touch with you within 72 hours.": "Alguien se comunicar\u00e1 con usted en un plazo de 72 horas.",
  "The staff list did not load. Try again in a minute.": "La lista del personal no carg\u00f3. Intente de nuevo en un minuto.",
  "We got your report.": "Recibimos su reporte.",
  "What happened": "Qu\u00e9 pas\u00f3",
  "Who is involved?": "\u00bfQui\u00e9n est\u00e1 involucrado?",
  "Who is it about? Pick at least one person.": "\u00bfSobre qui\u00e9n es? Elija al menos una persona.",
  "Write what happened in your own words. One sentence is enough.": "Escriba lo que pas\u00f3 con sus propias palabras. Con una frase basta.",
  "Yes": "S\u00ed",
  "You can write up to {max} characters.": "Puede escribir hasta {max} caracteres.",
  "Your reference": "Su n\u00famero de referencia",
  "Your report was not sent.": "Su reporte no se envi\u00f3.",
  "{used} of {max} characters used.": "{used} de {max} caracteres usados.",

  // Forms
  "Add row": "Agregar fila",
  "Answered": "Respondida",
  "Back": "Atr\u00e1s",
  "Name": "Nombre",
  "Clear": "Borrar",
  "Continue": "Continuar",
  "Done": "Listo",
  "Edit": "Editar",
  "Forms": "Formularios",
  "Forms could not load. Check your signal and try again.": "Los formularios no cargaron. Revise su se\u00f1al e intente de nuevo.",
  "Keep filling": "Seguir llenando",
  "Leave": "Salir",
  "Leave this report? Your saved answers stay, and you can continue from Forms or Help.": "\u00bfSalir de este reporte? Sus respuestas guardadas se quedan y puede continuar desde Formularios o Ayuda.",
  "Loading forms": "Cargando formularios",
  "Next": "Siguiente",
  "No forms to fill right now.": "No hay formularios para llenar ahora.",
  "Not answered": "Sin responder",
  "Not saved yet. Check your signal and tap Next again.": "Todav\u00eda no se guard\u00f3. Revise su se\u00f1al y toque Siguiente otra vez.",
  "Not sent yet. Check your signal and tap Submit report again.": "Todav\u00eda no se envi\u00f3. Revise su se\u00f1al y toque Enviar reporte otra vez.",
  "Not signed": "Sin firmar",
  "Not signed yet. Check your signal and tap Sign again.": "Todav\u00eda no se firm\u00f3. Revise su se\u00f1al y toque Firmar otra vez.",
  "1 photo": "1 foto",
  "{n} photos": "{n} fotos",
  "Not yet": "Todav\u00eda no",
  "Opening": "Abriendo",
  "Remove row": "Quitar fila",
  "Report": "Reportar",
  "Report sent. The people who handle these reports have been told.": "Reporte enviado. Ya se avis\u00f3 a las personas que atienden estos reportes.",
  "Required": "Obligatorio",
  "Review": "Revisar",
  "Role": "Puesto",
  "Row {n}": "Fila {n}",
  "Save signature": "Guardar firma",
  "Saving": "Guardando",
  "Section {n}": "Secci\u00f3n {n}",
  "Section {n} of {total}": "Secci\u00f3n {n} de {total}",
  "Send it": "Enviarlo",
  "Send this report? You cannot change it after it is sent.": "\u00bfEnviar este reporte? No lo podr\u00e1 cambiar despu\u00e9s de enviarlo.",
  "Sending": "Enviando",
  "Sign": "Firmar",
  "Sign with your finger": "Firme con el dedo",
  "Signed by {name} on {date} at {time}": "Firmado por {name} el {date} a las {time}",
  "Start report": "Comenzar reporte",
  "Which site is this for?": "\u00bfPara qu\u00e9 sitio es este reporte?",
  "Take photo or choose from gallery": "Tomar foto o elegir de la galer\u00eda",
  "These still need an answer": "Estas todav\u00eda necesitan respuesta",
  "This question cannot be answered here yet. Your supervisor will finish it.": "Esta pregunta todav\u00eda no se puede responder aqu\u00ed. Su supervisor la completar\u00e1.",
  "This photo could not be read. Try another one.": "No se pudo leer esta foto. Intente con otra.",
  "This question is full.": "Esta pregunta est\u00e1 llena.",
  "This report was already sent.": "Este reporte ya se hab\u00eda enviado.",
  "Thank you. {company} has your form.": "Gracias. {company} recibi\u00f3 su formulario.",
  "Thank you. We have your form.": "Gracias. Recibimos su formulario.",
  "Your name": "Su nombre",
  "Your role": "Su puesto",
  "This table is full.": "Esta tabla est\u00e1 llena.",
  "Untitled report": "Reporte sin t\u00edtulo",
  "{answered} answered": "{answered} respondidas",
  "{answered} answered, {remaining} to go": "{answered} respondidas, faltan {remaining}",

  // Acknowledgement page
  "Acknowledge this report": "Confirmar recibo de este reporte",
  "Comments": "Comentarios",
  "Not sent yet. Check your signal and tap Acknowledge this report again.": "Todav\u00eda no se envi\u00f3. Revise su se\u00f1al y toque Confirmar recibo de este reporte otra vez.",
  "Rate this month": "Calificar este mes",
  "Signature": "Firma",
  "Thank you. Your acknowledgement is recorded.": "Gracias. Su confirmaci\u00f3n qued\u00f3 registrada.",
  "This report could not load. Check your signal and try again.": "Este reporte no carg\u00f3. Revise su se\u00f1al e intente de nuevo.",

  // Shortcuts
  "Add to bar": "Agregar a la barra",
  "Add to bar {name}": "Agregar {name} a la barra",
  "Always first": "Siempre primero",
  "Always last": "Siempre al final",
  "Everything is on your bar.": "Todo est\u00e1 en su barra.",
  "Home": "Inicio",
  "Move down": "Bajar",
  "Move down {name}": "Bajar {name}",
  "Move up": "Subir",
  "Move up {name}": "Subir {name}",
  "On your bar": "En su barra",
  "Pick something for this spot": "Elija algo para este lugar",
  "Put the bar back the way it came?": "\u00bfDejar la barra como estaba al principio?",
  "Remove": "Quitar",
  "Remove {name}": "Quitar a {name}",
  "Replace which one?": "\u00bfCu\u00e1l quiere reemplazar?",
  "Reset": "Restablecer",
  "Reset to default": "Volver a lo original",
  "Shortcuts": "Accesos directos",
  "Under More": "En M\u00e1s",
  "will take the place of the one you pick.": "tomar\u00e1 el lugar del que elija.",
  "Your bar is full.": "Su barra est\u00e1 llena.",
  "Remove {name} from the bar": "Quitar {name} de la barra",

  // Notifications
  "Announcement": "Anuncio",
  "From {name}": "De {name}",
  "Load more": "Cargar m\u00e1s",
  "Mark all read": "Marcar todas como le\u00eddas",
  "Marking...": "Marcando...",
  "Nothing yet.": "Todav\u00eda no hay nada.",
  "Notifications did not load.": "Las notificaciones no cargaron.",
  "Open in the dashboard": "Abrir en el panel",

  // Settings
  "A": "A",
  "Appearance": "Apariencia",
  "Change PIN": "Cambiar PIN",
  "Choose how the app looks.": "Elija c\u00f3mo se ve la aplicaci\u00f3n.",
  "Choose what sits on your bottom bar. Everything else is under More.": "Elija qu\u00e9 va en su barra de abajo. Todo lo dem\u00e1s queda en M\u00e1s.",
  "Confirm New PIN": "Confirmar PIN nuevo",
  "Could not update your PIN.": "No se pudo actualizar su PIN.",
  "Current PIN": "PIN actual",
  "Dark": "Oscuro",
  "Light": "Claro",
  "Makes everything in the app bigger on this phone.": "Agranda todo en la aplicaci\u00f3n en este tel\u00e9fono.",
  "Makes everything in the app bigger.": "Agranda todo en la aplicaci\u00f3n.",
  "New PIN": "PIN nuevo",
  "Text size": "Tama\u00f1o del texto",
  "The app, Help and report forms use this language.": "La aplicaci\u00f3n, Ayuda y los formularios usan este idioma.",
  "Update PIN": "Actualizar PIN",
  "Your PIN is 4 digits. Choose one that only you know.": "Su PIN es de 4 d\u00edgitos. Elija uno que solo usted sepa.",
  "Enter your current 4-digit PIN.": "Escriba su PIN actual de 4 d\u00edgitos.",

  // Phone alerts
  "Phone alerts": "Alertas en el tel\u00e9fono",
  "Turn on alerts on this phone": "Activar alertas en este tel\u00e9fono",
  "Alerts are on for this phone.": "Las alertas est\u00e1n activadas en este tel\u00e9fono.",
  "Turn off on this phone": "Desactivar en este tel\u00e9fono",
  "This phone blocked alerts for this app. Turn them on in the phone's settings.": "Este tel\u00e9fono bloque\u00f3 las alertas de esta aplicaci\u00f3n. Act\u00edvelas en la configuraci\u00f3n del tel\u00e9fono.",
  "On an iPhone, add the app to your Home Screen first, then open it from there to turn on alerts.": "En un iPhone, primero agregue la aplicaci\u00f3n a su pantalla de inicio y \u00e1brala desde ah\u00ed para activar las alertas.",
  "This phone cannot receive alerts.": "Este tel\u00e9fono no puede recibir alertas.",
  "Chat messages": "Mensajes del chat",
  "Every message": "Cada mensaje",
  "Only when I'm tagged": "Solo cuando me etiquetan",
  "Off": "Apagado",
  "Schedule and time off": "Horario y tiempo libre",
  "Shift pickups and drops": "Turnos libres y turnos soltados",
  "Supply requests": "Pedidos de suministros",
  "Problems reported": "Problemas reportados",
  "Forms filed": "Formularios presentados",
  "Announcements from the office always come through.": "Los anuncios de la oficina siempre llegan.",
  "Turn on": "Activar",
  "Your settings did not save.": "Sus ajustes no se guardaron.",

  // Profile
  "Address": "Direcci\u00f3n",
  "Address Line 1": "Direcci\u00f3n l\u00ednea 1",
  "Address Line 2": "Direcci\u00f3n l\u00ednea 2",
  "Apt, suite, etc.": "Apartamento, suite, etc.",
  "Birthday": "Fecha de nacimiento",
  "City": "Ciudad",
  "Emergency Contact": "Contacto de emergencia",
  "Emergency Phone": "Tel\u00e9fono de emergencia",
  "Employee ID": "ID de empleado",
  "Employee ID not assigned. Ask your supervisor.": "No tiene ID de empleado asignado. Preg\u00fantele a su supervisor.",
  "Full name": "Nombre completo",
  "Loading profile...": "Cargando perfil...",
  "No shift": "Sin turno",
  "Not set": "Sin poner",
  "Personal Information": "Informaci\u00f3n personal",
  "Phone number": "N\u00famero de tel\u00e9fono",
  "Photo must be under 20MB": "La foto debe pesar menos de 20 MB",
  "Photo updated": "Foto actualizada",
  "Profile updated": "Perfil actualizado",
  "Save": "Guardar",
  "Site Assignments": "Sitios asignados",
  "Staff": "Personal",
  "State": "Estado",
  "Street address": "Calle y n\u00famero",
  "Your profile did not load.": "Su perfil no se carg\u00f3.",
  "Zip": "C\u00f3digo postal",

  // API message
  "A part day needs both a start and an end time, on one day": "Parte de un d\u00eda necesita hora de inicio y hora de fin, en un solo d\u00eda",
  "A photo could not be read. Upload it again and resend it.": "Una foto no se pudo leer. S\u00fabala otra vez y vuelva a enviarla.",
  "A photo is larger than 5 MB. Send a smaller photo.": "Una foto pesa m\u00e1s de 5 MB. Env\u00ede una foto m\u00e1s peque\u00f1a.",
  "A photo must be a JPEG, PNG or WebP image.": "La foto debe ser una imagen JPEG, PNG o WebP.",
  "A sign-off is made with its own button": "La firma se hace con su propio bot\u00f3n",
  "Choose a type of time off": "Elija un tipo de tiempo libre",
  "Dates must be YYYY-MM-DD": "Las fechas deben ser YYYY-MM-DD",
  "Draft not found": "No se encontr\u00f3 el reporte",
  "Enter a real date": "Escriba una fecha real",
  "Enter a real time": "Escriba una hora real",
  "Form not found": "No se encontr\u00f3 el formulario",
  "Hours must be a number from 0 to 999.99": "Las horas deben ser un n\u00famero de 0 a 999.99",
  "Keep the reason to 1000 characters or fewer.": "Escriba el motivo en 1000 caracteres o menos.",
  "language must be en or es": "el idioma debe ser en o es",
  "One of the people picked is not on the staff list": "Una de las personas elegidas no est\u00e1 en la lista del personal",
  "Photo upload failed": "No se pudo subir la foto",
  "Pick at least one person this is about": "Elija al menos a una persona de quien trata este reporte",
  "Pick up to 10 people": "Elija hasta 10 personas",
  "Request failed": "La solicitud fall\u00f3",
  "Request not found": "No se encontr\u00f3 la solicitud",
  "Required fields are unanswered": "Faltan campos obligatorios por responder",
  "Say whether this is about someone in management": "Indique si esto es sobre alguien de la gerencia",
  "Send answers as an object of key and value": "Env\u00ede las respuestas como un objeto de clave y valor",
  "Send at most 3 photos with one message.": "Env\u00ede como m\u00e1ximo 3 fotos en un mensaje.",
  "Server error": "Error del servidor",
  "Session expired": "Su sesi\u00f3n venci\u00f3",
  "shortcuts must be four different tab ids": "los accesos directos deben ser cuatro pesta\u00f1as distintas",
  "Some answers are not valid": "Algunas respuestas no son v\u00e1lidas",
  "textSize must be standard, large, xlarge or largest": "el tama\u00f1o del texto debe ser standard, large, xlarge o largest",
  "That column is not on this form": "Esa columna no est\u00e1 en este formulario",
  "That is not a sign-off on this form": "Eso no es una firma de este formulario",
  "That photo was not uploaded by you. Upload the photo again and resend it.": "Esa foto no la subi\u00f3 usted. Suba la foto otra vez y vuelva a enviarla.",
  "That row is not on this form": "Esa fila no est\u00e1 en este formulario",
  "That table has no room for another row": "Esa tabla no tiene espacio para otra fila",
  "The last day cannot be before the first day": "El \u00faltimo d\u00eda no puede ser anterior al primer d\u00eda",
  "theme must be light or dark": "el tema debe ser light o dark",
  "These fields cannot be answered here": "Estos campos no se pueden responder aqu\u00ed",
  "This form is not available to you": "Este formulario no est\u00e1 disponible para usted",
  "This part is already signed": "Esta parte ya est\u00e1 firmada",
  "This report is already submitted": "Este reporte ya est\u00e1 enviado",
  "This report was already submitted": "Este reporte ya se hab\u00eda enviado",
  "This report was voided": "Este reporte fue anulado",
  "This request was already approved": "Esta solicitud ya estaba aprobada",
  "This request was already cancelled": "Esta solicitud ya estaba cancelada",
  "This request was already denied": "Esta solicitud ya estaba denegada",
  "Time off can start at most 30 days ago": "El tiempo libre puede comenzar 30 d\u00edas atr\u00e1s como m\u00e1ximo",
  "Time off can start at most one year ahead": "El tiempo libre puede comenzar un a\u00f1o adelante como m\u00e1ximo",
  "Times must be HH:MM, from 00:00 to 23:59": "Las horas deben ser HH:MM, de 00:00 a 23:59",
  "Upload failed": "No se pudo subir el archivo",
  "User not found": "No se encontr\u00f3 el usuario",
  "You already have time off requested or approved for those days": "Ya tiene tiempo libre solicitado o aprobado para esos d\u00edas",
  "You cannot pick yourself": "No puede elegirse a s\u00ed mismo",
  "You cannot sign this part of the form": "Usted no puede firmar esta parte del formulario",

  // Bar and More
  "Chat": "Mensajes",
  "Help": "Ayuda",
  "Inspect": "Inspecci\u00f3n",

  // notifAgo
  "now": "ahora",

  // Bar and More
  "Schedule": "Horario",
  "Speak Up": "Confianza",
  "Tasks": "Tareas",

  // notifAgo
  "{n}d": "{n} d",
  "{n}h": "{n} h",
  "{n}m": "{n} min",
  // Safety data sheets
  "Safety data sheets": "Hojas de datos de seguridad",
  "Search by product": "Buscar por producto",
  "Revised {date}": "Revisada el {date}",
  "No sheet matches that product.": "Ninguna hoja coincide con ese producto.",
  "Every sheet is also in the printed binder at the site.": "Todas las hojas tambi\u00e9n est\u00e1n en la carpeta impresa del sitio.",
  "Saved on this phone. Last read {when}.": "Guardada en este tel\u00e9fono. Le\u00edda por \u00faltima vez: {when}.",
  "All sheets": "Todas las hojas",
  "This sheet is in English, as the manufacturer wrote it. Help in the app can explain any part of it in your language.": "Esta hoja est\u00e1 en ingl\u00e9s, como la escribi\u00f3 el fabricante. Ayuda, en la aplicaci\u00f3n, puede explicarle cualquier parte en su idioma.",
  "Safety data sheets are not available here yet. The printed binder at the site holds every sheet.": "Las hojas de datos de seguridad todav\u00eda no est\u00e1n disponibles aqu\u00ed. La carpeta impresa del sitio tiene todas las hojas.",
  "This sheet did not open. Check your signal, or use the printed binder at the site.": "Esta hoja no se abri\u00f3. Revise su se\u00f1al o use la carpeta impresa del sitio.",
  // Workspace
  "Workspace": "Espacio de trabajo",
  "My assignments": "Mis asignaciones",
  "Projects": "Proyectos",
  "Nothing is assigned to you right now.": "Por ahora no tiene nada asignado.",
  "You are not in any project yet.": "Todav\u00eda no est\u00e1 en ning\u00fan proyecto.",
  "Mark this to-do done?": "\u00bfMarcar este pendiente como hecho?",
  "Marked done.": "Marcado como hecho.",
  "That to-do was not marked done. Try again.": "Ese pendiente no se marc\u00f3 como hecho. Intente de nuevo.",
  "Due {date}": "Vence el {date}",
  "Overdue. Due {date}": "Vencido. Venc\u00eda el {date}",
  "Message Board": "Tablero de mensajes",
  "To-dos": "Pendientes",
  "Files": "Archivos",
  "This project did not open. Try again.": "Este proyecto no se abri\u00f3. Intente de nuevo.",
  "This project's chat is not on your chat list yet. Try again in a minute.": "El chat de este proyecto todav\u00eda no est\u00e1 en su lista de chats. Intente de nuevo en un minuto.",
  "Post a message": "Publicar un mensaje",
  "No messages on the board yet.": "Todav\u00eda no hay mensajes en el tablero.",
  "Pinned": "Fijado",
  "1 comment": "1 comentario",
  "{n} comments": "{n} comentarios",
  "Message": "Mensaje",
  "Post": "Publicar",
  "Posting...": "Publicando...",
  "Give the message a title.": "Escriba un t\u00edtulo para el mensaje.",
  "Write the message first.": "Primero escriba el mensaje.",
  "Message posted.": "Mensaje publicado.",
  "Your message was not posted. Try again.": "Su mensaje no se public\u00f3. Intente de nuevo.",
  "All messages": "Todos los mensajes",
  "This message did not open. Try again.": "Este mensaje no se abri\u00f3. Intente de nuevo.",
  "No comments yet.": "Todav\u00eda no hay comentarios.",
  "Add a comment": "Agregar un comentario",
  "Your comment was not added. Try again.": "Su comentario no se agreg\u00f3. Intente de nuevo.",
  "This project has no to-do lists yet.": "Este proyecto todav\u00eda no tiene listas de pendientes.",
  "Nothing on this list yet.": "Todav\u00eda no hay nada en esta lista.",
  "Add a to-do": "Agregar un pendiente",
  "Assigned to": "Asignado a",
  "Nobody yet": "Nadie todav\u00eda",
  "Due date": "Fecha de vencimiento",
  "Give the to-do a title.": "Escriba un t\u00edtulo para el pendiente.",
  "To-do added.": "Pendiente agregado.",
  "That to-do was not added. Try again.": "Ese pendiente no se agreg\u00f3. Intente de nuevo.",
  "Opened again.": "Abierto de nuevo.",
  "That to-do was not opened again. Try again.": "Ese pendiente no se abri\u00f3 de nuevo. Intente de nuevo.",
  "No files in this project yet.": "Todav\u00eda no hay archivos en este proyecto.",
  "Open file": "Abrir archivo",
  "Opening...": "Abriendo...",
  "That file did not open. Try again.": "Ese archivo no se abri\u00f3. Intente de nuevo.",
  "{n} KB": "{n} KB",
  "{n} MB": "{n} MB",
  // Workspace, part two
  "All to-dos": "Todos los pendientes",
  "Not done yet": "Todav\u00eda no est\u00e1 hecho",
  "No due date": "Sin fecha de vencimiento",
  "This to-do did not open. Try again.": "Este pendiente no se abri\u00f3. Intente de nuevo.",
  "New list": "Nueva lista",
  "Give the list a name.": "Escriba un nombre para la lista.",
  "List added.": "Lista agregada.",
  "That list was not added. Try again.": "Esa lista no se agreg\u00f3. Intente de nuevo.",
  "Add a file": "Agregar un archivo",
  "Choose a document": "Elegir un documento",
  "Documents, images and PDFs, up to 25 MB each.": "Documentos, im\u00e1genes y PDF, de hasta 25 MB cada uno.",
  "Note (optional)": "Nota (opcional)",
  "Add file": "Agregar archivo",
  "Choose a file first.": "Primero elija un archivo.",
  "{0} is over 25 MB. Files up to 25 MB can be added.": "{0} pasa de 25 MB. Se pueden agregar archivos de hasta 25 MB.",
  "File added.": "Archivo agregado.",
  "That file was not added. Try again.": "Ese archivo no se agreg\u00f3. Intente de nuevo.",
  "Saved on this phone. It sends when you have signal.": "Guardado en este tel\u00e9fono. Se env\u00eda cuando tenga se\u00f1al.",
  "Weekly": "Semanal",
  "Monthly": "Mensual",
  "Quarterly": "Trimestral",
  "Seasonal": "De temporada",
  "Equipment": "Equipo",
  "In service": "En servicio",
  "Out of service": "Fuera de servicio",
  "Retired": "Dado de baja",
  "Next service": "Pr\u00f3ximo mantenimiento",
  "None set": "Sin fecha",
  "Recent": "Reciente",
  "Nothing recorded yet.": "A\u00fan no hay nada registrado.",
  "Checked, all good": "Revisado, todo bien",
  "Serviced": "Mantenimiento hecho",
  "Repaired": "Reparado",
  "Tagged out": "Marcado fuera de servicio",
  "Back in service": "De nuevo en servicio",
  "Moved": "Trasladado",
  "Tag out": "Marcar fuera de servicio",
  "What is wrong": "Qu\u00e9 est\u00e1 mal",
  "Record that this item was checked and is in good order?": "\u00bfRegistrar que este equipo se revis\u00f3 y est\u00e1 en buen estado?",
  "Check recorded.": "Revisi\u00f3n registrada.",
  "Say what is wrong before you tag it out.": "Diga qu\u00e9 est\u00e1 mal antes de marcarlo fuera de servicio.",
  "Tag this item out of service? The office is told.": "\u00bfMarcar este equipo fuera de servicio? Se avisa a la oficina.",
  "Tagged out. The office has been told.": "Marcado fuera de servicio. Se avis\u00f3 a la oficina.",
  "The photo did not upload. Try again, or tag it out without it.": "La foto no se subi\u00f3. Intente de nuevo o m\u00e1rquelo fuera de servicio sin ella.",
  "That was not recorded. Try again.": "No se registr\u00f3. Intente de nuevo.",
  "This item did not open. Try again.": "Este equipo no se abri\u00f3. Intente de nuevo.",
  "This label does not match any equipment.": "Esta etiqueta no corresponde a ning\u00fan equipo.",
  "This item is retired. Nothing can be recorded on it.": "Este equipo est\u00e1 dado de baja. No se puede registrar nada en \u00e9l.",
  "This item is tagged out. Do not use it until the office puts it back in service.": "Este equipo est\u00e1 fuera de servicio. No lo use hasta que la oficina lo vuelva a poner en servicio.",
  "Thank you. Your reference is {ref}.": "Gracias. Su referencia es {ref}.",
  "We will reply within five working days.": "Le responderemos en un plazo de cinco d\u00edas h\u00e1biles.",
  "A copy is on its way to your email.": "Le enviamos una copia a su correo electr\u00f3nico.",
  "Sent by {who}.": "Enviado por {who}.",
  "Field kit": "Kit de campo",
  "You have no sites to work on.": "No tiene sitios en los que trabajar.",
  "Issue PPE": "Entregar EPP",
  "Periodic work": "Trabajo peri\u00f3dico",
  "Awaiting review": "Por revisar",
  "Hand out protective gear. The person signs for it here.": "Entregue equipo de protecci\u00f3n. La persona firma aqu\u00ed al recibirlo.",
  "What is overdue, due and done at this site.": "Lo vencido, lo pendiente y lo hecho en este sitio.",
  "Each item's status and next service.": "El estado y el pr\u00f3ximo mantenimiento de cada equipo.",
  "Inspections waiting for a review signature.": "Inspecciones que esperan una firma de revisi\u00f3n.",
  "Person": "Persona",
  "Choose a person": "Elija una persona",
  "No one is assigned to this site.": "Nadie est\u00e1 asignado a este sitio.",
  "Choose an item": "Elija un art\u00edculo",
  "{item}, {n} in stock": "{item}, {n} en existencia",
  "Something else (type it)": "Otra cosa (escr\u00edbala)",
  "What you are handing out": "Lo que entrega",
  "This site has no PPE in stock. Type what you are handing out.": "Este sitio no tiene EPP en existencia. Escriba lo que entrega.",
  "Size": "Talla",
  "Quantity": "Cantidad",
  "Fits well?": "\u00bfLe queda bien?",
  "Signature of the person receiving it": "Firma de quien lo recibe",
  "Hand the phone to the person. They sign with a finger.": "Entregue el tel\u00e9fono a la persona. Firma con el dedo.",
  "Choose the person you are handing it to.": "Elija a la persona a quien se lo entrega.",
  "Choose an item, or type what you are handing out.": "Elija un art\u00edculo o escriba lo que entrega.",
  "The person signs before you send.": "La persona firma antes de enviar.",
  "PPE issued. The signature is kept with it.": "EPP entregado. La firma queda guardada con la entrega.",
  "This form did not load.": "Este formulario no se carg\u00f3.",
  "This was not sent. Try again.": "No se envi\u00f3. Intente de nuevo.",
  "Issued at this site": "Entregado en este sitio",
  "No PPE has been issued at this site yet.": "A\u00fan no se ha entregado EPP en este sitio.",
  "{item} x{n}": "{item} x{n}",
  "Size {size}": "Talla {size}",
  "Fits well": "Le queda bien",
  "Does not fit": "No le queda bien",
  "Signed": "Firmado",
  "Issued by {name}, {when}": "Entregado por {name}, {when}",
  "Show more": "Mostrar m\u00e1s",
  "This site has no periodic work.": "Este sitio no tiene trabajo peri\u00f3dico.",
  "Read only. Work is checked off on the checklist.": "Solo lectura. El trabajo se marca en la lista.",
  "Overdue": "Vencido",
  "Due": "Pendiente",
  "On the checklist under {section}": "En la lista, en {section}",
  "Last done {date} by {name}": "Hecho por \u00faltima vez el {date} por {name}",
  "Last done {date}": "Hecho por \u00faltima vez el {date}",
  "Was due by {date}": "Deb\u00eda hacerse a m\u00e1s tardar el {date}",
  "Due by {date}": "Hacer a m\u00e1s tardar el {date}",
  "Next due by {date}": "La pr\u00f3xima vez, a m\u00e1s tardar el {date}",
  "This site has no equipment on the register.": "Este sitio no tiene equipos en el registro.",
  "Service due {date}": "Mantenimiento pendiente desde el {date}",
  "Next service {date}": "Pr\u00f3ximo mantenimiento el {date}",
  "No service date": "Sin fecha de mantenimiento",
  "Last: {what}, {when}": "\u00daltimo: {what}, {when}",
  "No inspection at this site is waiting for review.": "Ninguna inspecci\u00f3n de este sitio espera revisi\u00f3n.",
  "Inspection": "Inspecci\u00f3n",
  "{score} of {max}, {pct}%": "{score} de {max}, {pct}\u00a0%",
  "This inspection did not open. Try again.": "Esta inspecci\u00f3n no se abri\u00f3. Intente de nuevo.",
  "Read only. Only the review lines can be signed here.": "Solo lectura. Aqu\u00ed solo se pueden firmar las l\u00edneas de revisi\u00f3n.",
  "Scores": "Puntajes",
  "{score} of {max}": "{score} de {max}",
  "Not scored": "Sin puntaje",
  "Signatures": "Firmas",
  "Inspector": "Inspector",
  "Signed by {name}, {when}": "Firmado por {name}, {when}",
  "Waiting for the person who signs this line.": "Espera a la persona que firma esta l\u00ednea.",
  "Sign this line": "Firmar esta l\u00ednea",
  "Photo {n} of {count}": "Foto {n} de {count}",
  "Signed.": "Firmado.",
  "This was not signed. Try again.": "No se firm\u00f3. Intente de nuevo.",
  "Touchpoint": "Punto de contacto",
  "Critical touchpoint": "Punto de contacto cr\u00edtico",
  // The request page, Client requests and the supply page (Step 252)
  "Where in the building?": "\u00bfEn qu\u00e9 parte del edificio?",
  "Say where in the building.": "Diga en qu\u00e9 parte del edificio.",
  "Pick one of these first.": "Primero elija una de estas opciones.",
  "Anything we should know?": "\u00bfAlgo que debamos saber?",
  "Add photos": "Agregar fotos",
  "Email me updates (optional)": "Env\u00edenme novedades por correo (opcional)",
  "Report a concern instead": "Informar un problema en su lugar",
  "Call the office": "Llamar a la oficina",
  "This page could not load. Check your signal and try again.": "Esta p\u00e1gina no se pudo cargar. Revise su se\u00f1al e intente de nuevo.",
  "Your request was not sent. Check your signal and tap Try again.": "Su solicitud no se envi\u00f3. Revise su se\u00f1al y toque Intentar de nuevo.",
  "Client requests": "Solicitudes de clientes",
  "Waiting for approval": "En espera de aprobaci\u00f3n",
  "Yours": "Suyas",
  "{ago} ago": "hace {ago}",
  "Respond by {when}": "Responder antes de {when}",
  "Due {when}": "Vence {when}",
  "Due soon": "Vence pronto",
  "Approve and assign": "Aprobar y asignar",
  "Assign": "Asignar",
  "Me": "Yo",
  "On shift": "En turno",
  "No one can be assigned at this site.": "No hay nadie a quien asignar en este sitio.",
  "Approved and assigned.": "Aprobada y asignada.",
  "{name} already took care of this.": "{name} ya se encarg\u00f3 de esto.",
  "Someone": "Alguien",
  "Decline": "Rechazar",
  "Decline this request": "Rechazar esta solicitud",
  "This reason is emailed to the person who asked, if they left an email.": "Este motivo se env\u00eda por correo a la persona que lo pidi\u00f3, si dej\u00f3 un correo.",
  "Declined.": "Rechazada.",
  "I'm on it": "Ya voy",
  "Started.": "Iniciada.",
  "Mark this request done": "Marcar esta solicitud como lista",
  "Done. The person who asked is told if they left an email.": "Listo. Se avisa a la persona que lo pidi\u00f3 si dej\u00f3 un correo.",
  "Can't finish": "No puedo terminar",
  "Can't finish this request": "No puedo terminar esta solicitud",
  "Say what is in the way. Your supervisors are told.": "Diga qu\u00e9 lo impide. Se avisa a sus supervisores.",
  "Your supervisors have been told.": "Se avis\u00f3 a sus supervisores.",
  "Write a note first.": "Primero escriba una nota.",
  "That did not go through. Try again.": "No se pudo completar. Intente de nuevo.",
  "The photo did not upload. Try again, or send without it.": "La foto no se subi\u00f3. Intente de nuevo o env\u00ede sin ella.",
  "1 client request needs you": "1 solicitud de cliente lo necesita",
  "{n} client requests need you": "{n} solicitudes de clientes lo necesitan",
  "Safety sheet": "Hoja de seguridad",
  "No safety sheet on file. Ask your supervisor.": "No hay hoja de seguridad registrada. Pregunte a su supervisor.",
  "Sign in to record use": "Iniciar sesi\u00f3n para registrar uso",
  "Record use": "Registrar uso",
  "None of your sites stocks this supply.": "Ninguno de sus sitios tiene este suministro.",
  "Used one": "Us\u00e9 uno",
  "Used {n}": "Us\u00e9 {n}",
  "Recorded.": "Registrado.",
  "Running low": "Se est\u00e1 acabando",
  "This did not load. Try again.": "No se carg\u00f3. Intente de nuevo.",
  "That was not sent. Try again.": "No se envi\u00f3. Intente de nuevo.",
  // Inspection findings (Step 255)
  "This will open a finding.": "Esto abrir\u00e1 un hallazgo.",
  "Owner": "Responsable",
  "No owner yet": "Sin responsable todav\u00eda",
  "Meets the standard.": "Cumple el est\u00e1ndar.",
  "Below standard. Corrected within three working days.": "Por debajo del est\u00e1ndar. Se corrige en tres d\u00edas h\u00e1biles.",
  "Failed. A corrective action and a re-inspection within ten working days.": "Reprobada. Acci\u00f3n correctiva y reinspecci\u00f3n en diez d\u00edas h\u00e1biles.",
  "Serious. The officers are told the same day.": "Grave. Se informa a los directivos el mismo d\u00eda.",
  "Inspection sent. 1 finding opened.": "Inspecci\u00f3n enviada. 1 hallazgo abierto.",
  "Inspection sent. {n} findings opened.": "Inspecci\u00f3n enviada. {n} hallazgos abiertos.",
  "A corrective action is required. The office has been told.": "Se requiere una acci\u00f3n correctiva. Se avis\u00f3 a la oficina.",
  "Owner: {name}": "Responsable: {name}",
  "Inspection findings": "Hallazgos de inspecci\u00f3n",
  "Fixed": "Arreglado",
  "Mark this finding fixed": "Marcar este hallazgo como arreglado",
  "A second person checks the fix before the finding closes.": "Otra persona verifica el arreglo antes de cerrar el hallazgo.",
  "Fixed. A second person checks it.": "Arreglado. Otra persona lo verifica.",
  "Waiting for a check": "Esperando verificaci\u00f3n",
  // My training (Step 258)
  "My training": "Mi capacitaci\u00f3n",
  "To do": "Pendiente",
  "Coming due": "Por vencer",
  "History": "Historial",
  "Expired {date}": "Venci\u00f3 el {date}",
  "Refresher due at {site}": "Repaso pendiente en {site}",
  "Refresher due": "Repaso pendiente",
  "Expires {date}": "Vence el {date}",
  "Done {date}": "Hecha el {date}",
  "Nothing is required for your role yet.": "Todav\u00eda no se requiere nada para su puesto.",
  "Given in English": "Dada en ingl\u00e9s",
  "Given in Spanish": "Dada en espa\u00f1ol",
  "Given in French": "Dada en franc\u00e9s",
  "Take the course online": "Tomar el curso en l\u00ednea",
  "When you finish, give your certificate to the office.": "Cuando termine, entregue su certificado a la oficina.",
  // Online lessons and their sign-off (Step 261).
  "In progress": "En curso",
  "Waiting for your trainer": "Esperando a su capacitador",
  "Waiting for your trainer. Show them you can do it, and they sign it off.": "Esperando a su capacitador. Demu\u00e9strele que sabe hacerlo y lo firmar\u00e1.",
  "Start the lesson": "Comenzar la lecci\u00f3n",
  "Continue the lesson": "Continuar la lecci\u00f3n",
  "Ask your supervisor for an in-person session.": "P\u00eddale a su supervisor una sesi\u00f3n en persona.",
  "This lesson did not open. Try again.": "Esta lecci\u00f3n no se abri\u00f3. Intente de nuevo.",
  "Your answers were not sent. Try again.": "Sus respuestas no se enviaron. Intente de nuevo.",
  "Question {n} of {count}": "Pregunta {n} de {count}",
  "Your score: {score}%": "Su puntaje: {score}%",
  "Pass mark: {pass}%": "Puntaje para aprobar: {pass}%",
  "Not passed.": "No aprobado.",
  "You missed 1 question.": "Fall\u00f3 1 pregunta.",
  "You missed {n} questions.": "Fall\u00f3 {n} preguntas.",
  "Read it again": "Leerla de nuevo",
  "You passed.": "Aprob\u00f3.",
  "Your signature": "Su firma",
  "Done. It is on your record.": "Listo. Ya est\u00e1 en su expediente.",
  "Back to My training": "Volver a Mi capacitaci\u00f3n",
  "Sign off training": "Firmar capacitaciones",
  "Lessons passed and signed, waiting for your sign-off after a demonstration.": "Lecciones aprobadas y firmadas, en espera de su firma tras una demostraci\u00f3n.",
  "Nobody is waiting for a sign-off at this site.": "Nadie est\u00e1 esperando una firma en este sitio.",
  "Score {score}%, signed {when}": "Puntaje {score}%, firmado {when}",
  "Signed {when}": "Firmado {when}",
  "Sign off": "Firmar",
  "I watched them do it": "Lo vi hacerlo",
  "Tick I watched them do it first.": "Primero marque Lo vi hacerlo.",
  "Signed off. It is on their record.": "Firmado. Ya est\u00e1 en su expediente.",
  "1 training to do": "1 capacitaci\u00f3n pendiente",
  "{n} trainings to do": "{n} capacitaciones pendientes",
  "1 training to sign off": "1 capacitaci\u00f3n por firmar",
  "{n} trainings to sign off": "{n} capacitaciones por firmar",
  // Sessions, watched sign-offs, Your first trainings and documents to sign (Step 264).
  "Join a session": "Unirse a una sesi\u00f3n",
  "Session code": "C\u00f3digo de la sesi\u00f3n",
  "The code on your trainer's screen": "El c\u00f3digo en la pantalla de su capacitador",
  "Join": "Unirse",
  "Training session": "Sesi\u00f3n de capacitaci\u00f3n",
  "Trainer: {name}": "Capacitador: {name}",
  "Topics": "Temas",
  "This session is not open.": "Esta sesi\u00f3n no est\u00e1 abierta.",
  "Ask your trainer for the code on their screen.": "P\u00eddale a su capacitador el c\u00f3digo en su pantalla.",
  "I understood this training": "Entend\u00ed esta capacitaci\u00f3n",
  "Tick I understood this training first.": "Primero marque Entend\u00ed esta capacitaci\u00f3n.",
  "Sign in to this session": "Firmar mi entrada a esta sesi\u00f3n",
  "You are signed in. Your trainer closes the session.": "Ya firm\u00f3 su entrada. Su capacitador cierra la sesi\u00f3n.",
  "You already signed in to this session.": "Ya firm\u00f3 su entrada a esta sesi\u00f3n.",
  "Version {n}": "Versi\u00f3n {n}",
  "You signed version {n}. This one is new.": "Firm\u00f3 la versi\u00f3n {n}. Esta es nueva.",
  "Read and sign": "Leer y firmar",
  "Certificate on file": "Certificado archivado",
  "This document changed while you read it. Read it again.": "Este documento cambi\u00f3 mientras lo le\u00eda. L\u00e9alo de nuevo.",
  "This document is shown in English.": "Este documento se muestra en ingl\u00e9s.",
  "Signed. It is on your record.": "Firmado. Ya est\u00e1 en su expediente.",
  "Contents": "Contenido",
  "Section {n} of {count}": "Secci\u00f3n {n} de {count}",
  "Documents to sign": "Documentos por firmar",
  "Your first trainings": "Sus primeras capacitaciones",
  "Type a title.": "Escriba un t\u00edtulo.",
  "Choose at least one topic.": "Elija al menos un tema.",
  "Choose 1 to 10 topics.": "Elija de 1 a 10 temas.",
  "The training list did not load. Try again.": "La lista de capacitaciones no se carg\u00f3. Intente de nuevo.",
  "Start the session": "Iniciar la sesi\u00f3n",
  "Code": "C\u00f3digo",
  "Scan the QR, or type the code under My training, Join a session.": "Escanee el QR o escriba el c\u00f3digo en Mi capacitaci\u00f3n, Unirse a una sesi\u00f3n.",
  "Signed in so far": "Han firmado hasta ahora",
  "Nobody has signed in yet.": "Nadie ha firmado todav\u00eda.",
  "Remove {name} from this session?": "\u00bfQuitar a {name} de esta sesi\u00f3n?",
  "Close the session": "Cerrar la sesi\u00f3n",
  "Your signature closes the session and writes a record for each person signed in, on each topic.": "Su firma cierra la sesi\u00f3n y registra una capacitaci\u00f3n por cada persona que firm\u00f3, en cada tema.",
  "Cancel the session": "Cancelar la sesi\u00f3n",
  "Cancel this session? Nothing is written for it.": "\u00bfCancelar esta sesi\u00f3n? No se registra nada.",
  "Session cancelled. Nothing was written.": "Sesi\u00f3n cancelada. No se registr\u00f3 nada.",
  "Closed. 1 record saved.": "Cerrada. Se guard\u00f3 1 registro.",
  "Closed. {n} records saved.": "Cerrada. Se guardaron {n} registros.",
  "Already had it today: {names}": "Ya la ten\u00edan hoy: {names}",
  "{n} already had it today.": "{n} ya la ten\u00edan hoy.",
  "Start another session": "Iniciar otra sesi\u00f3n",
  "Start a session on this site, show its code, and close it with your signature.": "Inicie una sesi\u00f3n en este sitio, muestre su c\u00f3digo y ci\u00e9rrela con su firma.",
  "Watch and sign off": "Observar y firmar",
  "Watch a person do a task, tick each step, and sign it off with them.": "Observe a una persona hacer una tarea, marque cada paso y f\u00edrmela con ella.",
  "Checklist": "Lista de pasos",
  "No checklist is waiting for {name}.": "No hay ninguna lista pendiente para {name}.",
  "Start the checklist": "Iniciar la lista",
  "Steps": "Pasos",
  "Hand the phone to {name} to sign": "Pase el tel\u00e9fono a {name} para firmar",
  "Not ticked yet: {steps}": "Sin marcar: {steps}",
  "Signature of {name}": "Firma de {name}",
  "Now you sign.": "Ahora firma usted.",
  "Watch another": "Observar otra",
  // The training portal (Step 267).
  "{done} of {required} trainings done": "{done} de {required} capacitaciones hechas",
  "Continue where you left off": "Continuar donde lo dej\u00f3",
  "{done} of {required} done": "{done} de {required} hechas",
  "Up next: {name}": "Siguiente: {name}",
  "Needs an in-person session": "Necesita una sesi\u00f3n en persona",
  "Expires soon": "Vence pronto",
  "Your trainer signs this off in the {name} checklist.": "Su capacitador lo firma en la lista {name}.",
  "Your trainer goes over this one with you.": "Su capacitador repasa esta con usted.",
  // Sign on your own phone, and My company property (Step 271).
  "1 to sign": "1 por firmar",
  "{n} to sign": "{n} por firmar",
  "To sign": "Por firmar",
  "Nothing is waiting for your signature.": "Nada espera su firma.",
  "Waiting for your signature": "Esperando su firma",
  "Not right": "No es correcto",
  "Open the warning": "Abrir la amonestaci\u00f3n",
  "This is not right": "Esto no es correcto",
  "Tell the office what is not right. They will follow up with you.": "D\u00edgale a la oficina qu\u00e9 no es correcto. Le dar\u00e1n seguimiento.",
  "Send to the office": "Enviar a la oficina",
  "Sent back to the office. They will follow up.": "Devuelto a la oficina. Le dar\u00e1n seguimiento.",
  "I will not sign": "No voy a firmar",
  "You can say why. The office is told either way, and the warning stays on file.": "Puede decir por qu\u00e9. La oficina se entera de todos modos y la amonestaci\u00f3n queda en su expediente.",
  "Yes, I will not sign": "S\u00ed, no voy a firmar",
  "Your answer was sent to the office.": "Su respuesta se envi\u00f3 a la oficina.",
  "This is already done.": "Esto ya est\u00e1 resuelto.",
  "This request did not open. Try again.": "Esta solicitud no se abri\u00f3. Intente de nuevo.",
  "Your note was not sent. Try again.": "Su nota no se envi\u00f3. Intente de nuevo.",
  "My company property": "Mi propiedad de la empresa",
  "You hold": "Lo que tiene",
  "Returned": "Devuelto",
  "Returned {date}": "Devuelto el {date}",
  "Issued {date}": "Entregado el {date}",
  "You hold no company property.": "No tiene propiedad de la empresa.",
  "Key": "Llave",
  "Badge": "Gafete",
  "Fob": "Llavero",
  "Uniform shirt": "Camisa de uniforme",
  "Other uniform": "Otro uniforme",
  "The warning did not open. Try again.": "La amonestaci\u00f3n no se abri\u00f3. Intente de nuevo.",
  "Written warning": "Amonestaci\u00f3n por escrito",
  "Final written warning": "Amonestaci\u00f3n final por escrito",
  // Step 281: a supply request with many items
  "Fulfilled": "Entregada",
  "My requests": "Mis solicitudes",
  "Quantity: {n}": "Cantidad: {n}",
  "Approved {n} of {m}": "Aprobado: {n} de {m}",
  "Showing your latest 10 requests.": "Mostrando sus 10 solicitudes m\u00e1s recientes.",
  "Item {n}": "Art\u00edculo {n}",
  "Remove item {n}": "Quitar art\u00edculo {n}",
  "Add item": "Agregar art\u00edculo",
  "Choose a supply": "Elija un art\u00edculo",
  "Enter a quantity from 1 to 999.": "Escriba una cantidad de 1 a 999.",
  "A request holds up to 30 items.": "Una solicitud lleva hasta 30 art\u00edculos.",
  // Step 285: sign-in made simple and closed. The two PIN rules are the
  // API's own sentences (helpers/words.js, auth.pinWeak and
  // auth.pinUnchanged), so the phone and the API say the same.
  "Choose a PIN that is not repeated digits, a sequence, or your badge number": "Elija un PIN que no sea un mismo d\u00edgito repetido, una secuencia ni su n\u00famero de empleado",
  "New PIN must be different from your current PIN": "El PIN nuevo debe ser distinto de su PIN actual",
  "You are still signed in. The app tries again on its own.": "Su sesi\u00f3n sigue abierta. La aplicaci\u00f3n vuelve a intentarlo por su cuenta.",
  // Step 290: the round. The handbook reader, Signed documents, the
  // searchable pickers, App support, the sign-in line and PTO.
  "Part {n}: {title}": "Parte {n}: {title}",
  "Part {n}": "Parte {n}",
  "Start reading": "Empezar a leer",
  "See the designed version": "Ver la versi\u00f3n dise\u00f1ada",
  "The designed version did not open. Try again.": "La versi\u00f3n dise\u00f1ada no se abri\u00f3. Intente de nuevo.",
  "Document number": "N\u00famero de documento",
  "Version": "Versi\u00f3n",
  "Download my signed page": "Descargar mi p\u00e1gina firmada",
  "Your signed page did not open. Try again.": "Su p\u00e1gina firmada no se abri\u00f3. Intente de nuevo.",
  "Position and job class": "Puesto y clase de trabajo",
  "Assigned sites": "Sitios asignados",
  "Date": "Fecha",
  "Signed documents": "Documentos firmados",
  "Signed {date}": "Firmado el {date}",
  "Signed in English": "Firmado en ingl\u00e9s",
  "Signed in Spanish": "Firmado en espa\u00f1ol",
  "Signed in French": "Firmado en franc\u00e9s",
  "You signed version {version} on {date}.": "Usted firm\u00f3 la versi\u00f3n {version} el {date}.",
  "A new version is waiting for you under Documents to sign.": "Una versi\u00f3n nueva lo espera en Documentos por firmar.",
  "No supply matches that name.": "Ning\u00fan art\u00edculo coincide con ese nombre.",
  "App support": "Soporte de la aplicaci\u00f3n",
  "Tell the office about a problem with the app, an idea, or information that is wrong.": "Avise a la oficina de un problema con la aplicaci\u00f3n, una idea o informaci\u00f3n incorrecta.",
  "What is it about?": "\u00bfDe qu\u00e9 se trata?",
  "Something is not working": "Algo no funciona",
  "An idea for the app": "Una idea para la aplicaci\u00f3n",
  "Information that is wrong": "Informaci\u00f3n incorrecta",
  "Help could not answer": "Ayuda no pudo responder",
  "Trouble signing in": "Problemas para iniciar sesi\u00f3n",
  "Choose what it is about.": "Elija de qu\u00e9 se trata.",
  "Write what happened.": "Escriba lo que pas\u00f3.",
  "Say what you were doing and what you saw.": "Diga qu\u00e9 estaba haciendo y qu\u00e9 vio.",
  "Screenshot": "Captura de pantalla",
  "Add a screenshot": "Agregar una captura de pantalla",
  "Sent with your ticket": "Se env\u00eda con su reporte",
  "Screen": "Pantalla",
  "App version": "Versi\u00f3n de la aplicaci\u00f3n",
  "Phone": "Tel\u00e9fono",
  "Send to App support": "Enviar a Soporte de la aplicaci\u00f3n",
  "Sent. The office will look at it.": "Enviado. La oficina lo revisar\u00e1.",
  "Your ticket was not sent. Try again.": "Su reporte no se envi\u00f3. Intente de nuevo.",
  "My tickets": "Mis reportes",
  "You have not sent a ticket yet.": "Todav\u00eda no ha enviado ning\u00fan reporte.",
  "New": "Nuevo",
  "Working on it": "En proceso",
  "Won't do": "No se har\u00e1",
  "Note from the office: {note}": "Nota de la oficina: {note}",
  "Ticket for App support": "Reporte para Soporte de la aplicaci\u00f3n",
  "Sent to App support. You can follow it under My tickets.": "Enviado a Soporte de la aplicaci\u00f3n. Puede seguirlo en Mis reportes.",
  "Can't sign in? Email {email}": "\u00bfNo puede iniciar sesi\u00f3n? Escriba a {email}",
  "PTO (paid time off)": "PTO (tiempo libre pagado)",
  "Choose your 4-digit PIN and pick your language.": "Elija su PIN de 4 d\u00edgitos y su idioma.",
  "4 digits. Not all the same, not in a row like 1234, and not your badge number.": "4 d\u00edgitos. No todos iguales, no seguidos como 1234 y no su n\u00famero de empleado.",
  "Get an alert when someone messages you or assigns you an inspection?": "\u00bfQuiere recibir una alerta cuando alguien le escriba o le asigne una inspecci\u00f3n?",
  "This inspection was cancelled.": "Esta inspecci\u00f3n se cancel\u00f3.",
  "This inspection is already done.": "Esta inspecci\u00f3n ya est\u00e1 hecha.",
  "This inspection is not on your list anymore.": "Esta inspecci\u00f3n ya no est\u00e1 en su lista.",
  "Assigned to you": "Asignado a usted",
  "{n} assigned to you": "Asignados a usted: {n}",
  "Issue": "Problema",
  "Inspection finding": "Hallazgo de inspecci\u00f3n",
  "Client request": "Solicitud de cliente",
  "Not assigned yet": "Sin asignar todav\u00eda",
  "What was done": "Lo que se hizo",
  "Reassign": "Reasignar",
  "Assigned to {name}.": "Asignado a {name}.",
  "Escalated": "Escalado",
  "This issue did not load.": "Este problema no se carg\u00f3.",
  "Unfinished forms ({n})": "Formularios sin terminar ({n})",
  "Save and finish later": "Guardar y terminar despu\u00e9s",
  "Saved. You will get a reminder until it is sent.": "Guardado. Recibir\u00e1 un recordatorio hasta que lo env\u00ede.",
  "Library": "Biblioteca",
  "Search by number, title or words in the text": "Buscar por n\u00famero, t\u00edtulo o palabras del texto",
  "Also in Spanish": "Tambi\u00e9n en espa\u00f1ol",
  "In English only": "Solo en ingl\u00e9s",
  "The library is loading. Check back soon.": "La biblioteca se est\u00e1 cargando. Vuelva a revisar pronto.",
  "No document matches that search.": "Ning\u00fan documento coincide con esa b\u00fasqueda.",
  "All folders": "Todas las carpetas",
  "This document is waiting for your signature.": "Este documento espera su firma.",
  "Open in Documents to sign": "Abrir en Documentos por firmar",
  "Open {docCode}": "Abrir {docCode}",
  "Approve supplies": "Aprobar suministros",
  "Supply requests to approve ({n})": "Solicitudes de suministros por aprobar ({n})",
  "Waiting for a decision": "Esperan una decisi\u00f3n",
  "Signed, not sent yet": "Firmadas, sin enviar todav\u00eda",
  "Ordered": "Pedidas",
  "Asked by {name}, {when}": "Pedido por {name}, {when}",
  "Ready to sign": "Lista para firmar",
  "No supply requests are waiting for you.": "No hay solicitudes de suministros que lo esperen.",
  "This request did not load.": "Esta solicitud no se carg\u00f3.",
  "Quantity to approve": "Cantidad por aprobar",
  "Approve": "Aprobar",
  "Deny": "Denegar",
  "Approve all": "Aprobar todo",
  "Deny all": "Denegar todo",
  "Change": "Cambiar",
  "Only the people who approve supply requests can decide this.": "Solo las personas que aprueban las solicitudes de suministros pueden decidir esto.",
  "You asked for these supplies, so someone else decides them.": "Usted pidi\u00f3 estos suministros, as\u00ed que otra persona decide.",
  "Sign and order": "Firmar y pedir",
  "Vendor": "Proveedor",
  "Choose a vendor": "Elija un proveedor",
  "Choose a vendor first.": "Elija primero un proveedor.",
  "Contact": "Contacto",
  "Email": "Correo",
  "Deliver to": "Entregar en",
  "Left blank, the site's address is used.": "Si se deja en blanco, se usa la direcci\u00f3n del sitio.",
  "Ask the office to add the vendor and set it to approved.": "Pida a la oficina que agregue el proveedor y lo marque como aprobado.",
  "The vendor list did not load.": "La lista de proveedores no se carg\u00f3.",
  "Purchase order": "Orden de compra",
  "Signed by {name}, {date}": "Firmado por {name}, {date}",
  "Open the purchase order": "Abrir la orden de compra",
  "The purchase order did not open. Try again.": "La orden de compra no se abri\u00f3. Intente de nuevo.",
  "Send to {email}": "Enviar a {email}",
  "Email to send it to": "Correo al cual enviarla",
  "Enter a valid email address.": "Escriba un correo v\u00e1lido.",
  "Sent to {email}": "Enviada a {email}",
  "Ordered {date}, sent to {email}": "Pedida el {date}, enviada a {email}",
  "Send again": "Enviar de nuevo",
  "Site checklist": "Lista del sitio",
  "Safety walk": "Recorrido de seguridad",
  "Next: Safety walk": "Siguiente: recorrido de seguridad",
  "Sign and send": "Firmar y enviar",
  "This signature signs the site checklist and the safety walk.": "Esta firma firma la lista del sitio y el recorrido de seguridad.",
  "Severity {s}": "Gravedad {s}",
  "Now": "Ahora",
  "Residents' meal": "Comida de los residentes",
  "Empty, full access": "Vac\u00edo, acceso completo",
  "When there is free time": "Cuando haya tiempo libre",
  "Start of shift": "Inicio del turno",
  "End of shift": "Fin del turno",
  "The space is in use. No steps in this time.": "El espacio est\u00e1 en uso. No hay pasos en este horario.",
  "Start an inspection": "Comenzar una inspecci\u00f3n",
  "For today, assigned to you.": "Para hoy, asignada a usted.",
  "Template": "Plantilla",
  "Which template?": "\u00bfQu\u00e9 plantilla?",
  "Include the safety walk": "Incluir el recorrido de seguridad",
  "Start the inspection": "Comenzar la inspecci\u00f3n",
  "No site matches that name.": "Ning\u00fan sitio coincide con ese nombre.",
  "No inspection template is set up for this site yet. Ask the office.": "Todav\u00eda no hay una plantilla de inspecci\u00f3n para este sitio. Pregunte a la oficina.",
  "Choose a site first.": "Elija primero un sitio.",
  "Choose a template first.": "Elija primero una plantilla.",
  "Inspection started": "Inspecci\u00f3n iniciada",
  "Task list for this site": "Lista de tareas de este sitio",
  "Nothing to check off here. This list shows the work our staff do at this site.": "Aqu\u00ed no hay nada que marcar. Esta lista muestra el trabajo que nuestro personal hace en este sitio.",
  "This session did not open. Try again.": "Esta sesi\u00f3n no se abri\u00f3. Intente de nuevo.",
  "This document did not open. Try again.": "Este documento no se abri\u00f3. Intente de nuevo.",
  "This session did not start. Try again.": "Esta sesi\u00f3n no se inici\u00f3. Intente de nuevo.",
  "The session was not closed. Try again.": "La sesi\u00f3n no se cerr\u00f3. Intente de nuevo.",
  "This checklist did not start. Try again.": "Esta lista no se inici\u00f3. Intente de nuevo.",
  "Issue reported. Its photo did not attach.": "Problema reportado. Su foto no se adjunt\u00f3.",
  "Already done by {firstName}": "Ya lo hizo {firstName}",
  "Already done": "Ya est\u00e1 hecho",
  "The PIN the office gave you has expired. Call the office for a new one, or tap Forgot your PIN? to choose your own by email.": "El PIN que le dio la oficina ya venci\u00f3. Llame a la oficina para pedir uno nuevo, o toque \u00bfOlvid\u00f3 su PIN? para elegir el suyo por correo.",
  "Too many tries today, so your account is locked until the office unlocks it. Call the office, or tap Forgot your PIN? to reset it by email.": "Demasiados intentos hoy, as\u00ed que su cuenta est\u00e1 bloqueada hasta que la oficina la desbloquee. Llame a la oficina, o toque \u00bfOlvid\u00f3 su PIN? para restablecerlo por correo.",
  "OCSA could not check your sign-in just now. Try again in a moment.": "OCSA no pudo comprobar su sesi\u00f3n en este momento. Intente de nuevo en un momento.",
  "This photo could not be saved. Try again, and tell the office if it happens again.": "No se pudo guardar esta foto. Intente de nuevo y avise a la oficina si vuelve a pasar.",
  "This report could not be sent as it was. Tap Submit Issue again.": "Este reporte no se pudo enviar as\u00ed. Toque Enviar problema otra vez.",
  "This task is already finished or marked as unable to finish, so there is nothing more to send.": "Esta tarea ya est\u00e1 terminada o marcada como que no se puede terminar, as\u00ed que no hay nada m\u00e1s que enviar.",
  "The problem behind this task is already closed, so there is nothing more to send.": "El problema de esta tarea ya est\u00e1 cerrado, as\u00ed que no hay nada m\u00e1s que enviar.",
  "You have time off approved that day, so this shift cannot be yours. Pick a shift on another day.": "Usted tiene tiempo libre aprobado ese d\u00eda, as\u00ed que este turno no puede ser suyo. Elija un turno de otro d\u00eda.",
  "Your shift end is saved on this phone, with the time you tapped it. It sends when you have signal.": "El fin de su turno est\u00e1 guardado en este tel\u00e9fono, con la hora en que lo toc\u00f3. Se env\u00eda cuando tenga se\u00f1al.",
  "Case": "Caso",
  "Read only. The case is worked on the dashboard.": "Solo para leer. El caso se trabaja en el panel.",
  "This case did not open. Try again.": "Este caso no se abri\u00f3. Intente de nuevo.",
  "Case open": "Caso abierto",
  "Case in review": "Caso en revisi\u00f3n",
  "Case escalated": "Caso escalado",
  "Case resolved": "Caso resuelto",
  "Case closed": "Caso cerrado",
  "Opened": "Abierto",
  "Filed by": "Presentado por",
  "About": "Sobre",
  "Escalated to": "Escalado a",
  "What was reported": "Lo que se report\u00f3",
  "Resolution": "Resoluci\u00f3n",
  "Updates": "Novedades",
  "Client concern": "Inquietud del cliente",
  "Read only. The office answers the client from the dashboard.": "Solo para leer. La oficina le responde al cliente desde el panel.",
  "This report did not open. Try again.": "Este reporte no se abri\u00f3. Intente de nuevo.",
  // Part 356: the others on shift at this site now, at the top of the checklist.
  "Also here: {names}": "Tambi\u00e9n aqu\u00ed: {names}",
  // Part 359: My reports on Speak Up (API-63).
  "My reports": "Mis reportes de Confianza",
  "Each report you sent here, or the office opened for you, and where it stands.": "Cada reporte que envi\u00f3 aqu\u00ed, o que la oficina abri\u00f3 por usted, y c\u00f3mo va.",
  "Your reports did not load.": "Sus reportes no cargaron.",
  "You have no reports yet.": "Todav\u00eda no tiene reportes.",
  "Sent {date}": "Enviado el {date}",
  "Updated {date}": "Actualizado el {date}",
  "Report received": "Reporte recibido",
  "Being looked into": "En revisi\u00f3n",
  "Report resolved": "Reporte resuelto",
  "Report closed": "Reporte cerrado",
  // Part 359: a filed report opened from its notice (API-83).
  "Filed report": "Reporte enviado",
  "Read only. The report is worked on the dashboard.": "Solo para leer. El reporte se trabaja desde el panel.",
  // Part 359: Register (API-117).
  "Thank you. A supervisor reviews each registration, and OCSA emails you once you can sign in.": "Gracias. Un supervisor revisa cada registro, y OCSA le env\u00eda un correo cuando ya pueda iniciar sesi\u00f3n.",
  // Part 359: the refusals new since Round 2.
  "This shift already has a drop request, or it is already posted for pickup. There is nothing more to send.": "Este turno ya tiene una solicitud para dejarlo, o ya est\u00e1 publicado como turno libre. No hay nada m\u00e1s que enviar.",
  "What you sent is too large. Use fewer or smaller photos, or shorter text, then try again.": "Lo que envi\u00f3 es demasiado grande. Use menos fotos o fotos m\u00e1s peque\u00f1as, o un texto m\u00e1s corto, y vuelva a intentarlo.",
  "What you sent could not be read. Try again, and tell the office if it happens again.": "No se pudo leer lo que envi\u00f3. Intente de nuevo y avise a la oficina si vuelve a pasar.",
  "This file is over {mb} MB. Choose a smaller photo or file.": "Este archivo pesa m\u00e1s de {mb} MB. Elija una foto o un archivo m\u00e1s peque\u00f1o.",
  "This file is too large. Choose a smaller photo or file.": "Este archivo es demasiado grande. Elija una foto o un archivo m\u00e1s peque\u00f1o.",
  "This question takes {max} photos at most. Remove one to add another.": "Esta pregunta acepta como m\u00e1ximo {max} fotos. Quite una para agregar otra.",
  "This photo was already removed. The question now shows the photos that are kept.": "Esta foto ya se hab\u00eda quitado. La pregunta ahora muestra las fotos que se guardan.",
  "Your shift did not load.": "Su turno no se carg\u00f3.",
  "Time off did not load.": "El tiempo libre no se carg\u00f3.",
  "Your time off requests did not load.": "Sus solicitudes de tiempo libre no se cargaron.",
  "Your latest answers did not save. Keep filling to try again, or leave without them.": "Sus \u00faltimas respuestas no se guardaron. Siga llenando para intentarlo de nuevo, o salga sin ellas.",
  "Leave without saving": "Salir sin guardar",
  "Alerts need iOS 16.4 or later. Update this iPhone in Settings, General, Software Update.": "Las alertas necesitan iOS 16.4 o posterior. Actualice este iPhone en Ajustes, General, Actualizaci\u00f3n de software.",
  "On an iPhone, alerts come only to the app on your Home Screen. Add it with Share, then Add to Home Screen, and open it from there to turn alerts on.": "En un iPhone, las alertas llegan solo a la aplicaci\u00f3n en su pantalla de inicio. Agr\u00e9guela con Compartir y luego Agregar a la pantalla de inicio, y \u00e1brala desde ah\u00ed para activar las alertas.",
  "Alerts on an iPhone": "Alertas en un iPhone",
  "Got it": "Entendido",
  "Alerts on an iPhone come only to the app added this way.": "En un iPhone, las alertas llegan solo a la aplicaci\u00f3n agregada de esta forma.",
  "You have a new notice. Tap the bell to read it.": "Tiene un aviso nuevo. Toque la campana para leerlo.",
  "This shift is no longer on your schedule.": "Este turno ya no est\u00e1 en su horario.",
  "This photo could not be sent with the task. Take the photo again, then send it.": "No se pudo enviar esta foto con la tarea. Tome la foto otra vez y luego env\u00edela.",
  "The note is too long. Keep it to {max} characters.": "La nota es demasiado larga. Escriba {max} caracteres como m\u00e1ximo.",
  "This request was not sent.": "Esta solicitud no se envi\u00f3.",
  "Sent to the building as {reference}.": "Se envi\u00f3 al edificio como {reference}.",
  "Send to building maintenance": "Enviar a mantenimiento del edificio",
  "Floor {floor}": "Piso {floor}",
  "Sent today": "Enviada hoy",
  "Sent 1 day ago": "Enviada hace 1 d\u00eda",
  "Sent {n} days ago": "Enviada hace {n} d\u00edas",
  "The photos kept with this request are no longer on this phone. Take them again, then send it.": "Las fotos guardadas con esta solicitud ya no est\u00e1n en este tel\u00e9fono. T\u00f3melas otra vez y luego env\u00edela.",
  "Sent to the building.": "Se envi\u00f3 al edificio.",
  "New request": "Solicitud nueva",
  "This request was not sent. {reason}": "Esta solicitud no se envi\u00f3. {reason}",
  "Change and send": "Cambiar y enviar",
  "Requests at this site": "Solicitudes en este sitio",
  "No requests sent to the building yet.": "Todav\u00eda no se han enviado solicitudes al edificio.",
  "No answer yet": "Sin respuesta todav\u00eda",
  "A request takes {max} photos at most.": "Una solicitud acepta como m\u00e1ximo {max} fotos.",
  "Choose what kind of problem it is.": "Elija qu\u00e9 tipo de problema es.",
  "Choose how soon it needs fixing.": "Elija qu\u00e9 tan pronto hay que repararlo.",
  "Say what is wrong.": "Diga qu\u00e9 est\u00e1 mal.",
  "New request to building maintenance": "Solicitud nueva a mantenimiento del edificio",
  "This issue was already sent to the building as {reference}.": "Este problema ya se envi\u00f3 al edificio como {reference}.",
  "Open the request": "Abrir la solicitud",
  "The request form did not load. It needs signal the first time.": "El formulario de la solicitud no se carg\u00f3. La primera vez necesita se\u00f1al.",
  "Area": "\u00c1rea",
  "Kind of problem": "Tipo de problema",
  "Choose": "Elegir",
  "Send the issue's photos too:": "Enviar tambi\u00e9n las fotos del problema:",
  "{n} of {max} photos": "{n} de {max} fotos",
  "It goes to": "Se env\u00eda a",
  "Who it goes to did not load.": "No se carg\u00f3 a qui\u00e9n se env\u00eda.",
  "This site has no maintenance contact yet. Ask the office to add one before sending.": "Este sitio todav\u00eda no tiene un contacto de mantenimiento. Pida a la oficina que agregue uno antes de enviar.",
  "This list is the one this phone last read. The request goes to whoever is on the site's list when it is sent.": "Esta es la lista que este tel\u00e9fono ley\u00f3 la \u00faltima vez. La solicitud va a quien est\u00e9 en la lista del sitio cuando se env\u00ede.",
  "Choose how the building answered.": "Elija c\u00f3mo respondi\u00f3 el edificio.",
  "Choose where the request stands now.": "Elija c\u00f3mo va la solicitud ahora.",
  "Write the note first.": "Escriba primero la nota.",
  "Nothing changed. The request is already at that status.": "No cambi\u00f3 nada. La solicitud ya tiene ese estado.",
  "Saved.": "Guardado.",
  "Follow-up sent to the building.": "Se envi\u00f3 un seguimiento al edificio.",
  "Sent to the building again.": "Se envi\u00f3 otra vez al edificio.",
  "Maintenance request": "Solicitud de mantenimiento",
  "Sent {date} by {name}": "Enviada el {date} por {name}",
  "No answer from the building yet. It was asked to answer by {date}.": "El edificio todav\u00eda no ha respondido. Se le pidi\u00f3 responder a m\u00e1s tardar el {date}.",
  "The building is asked to answer by {date}.": "Se pide al edificio que responda a m\u00e1s tardar el {date}.",
  "Scheduled for {date}": "Programada para el {date}",
  "Emailed to the building {when}.": "Se envi\u00f3 por correo al edificio {when}.",
  "The email to the building did not go.": "El correo al edificio no se envi\u00f3.",
  "Sent to": "Enviada a",
  "Record a response": "Anotar una respuesta",
  "Change the status": "Cambiar el estado",
  "Add a note": "Agregar una nota",
  "Send the email again": "Enviar el correo otra vez",
  "Send a follow-up": "Enviar un seguimiento",
  "How the building answered": "C\u00f3mo respondi\u00f3 el edificio",
  "Where it stands now": "C\u00f3mo va ahora",
  "The day they will come": "El d\u00eda que vendr\u00e1n",
  "What they said": "Lo que dijeron",
  "Building maintenance": "Mantenimiento del edificio",
  "Send the building a repair request, and keep track of what it answers.": "Env\u00ede al edificio una solicitud de reparaci\u00f3n y siga lo que responde.",
  "That did not save. Try again.": "Eso no se guard\u00f3. Intente de nuevo.",
  "{n} day": "{n} d\u00eda",
  "{n} days": "{n} d\u00edas",
  "{kind}: {hours} went back when the office took back the approval.": "{kind}: se devolvieron {hours} cuando la oficina retir\u00f3 la aprobaci\u00f3n.",
  "{kind} balance now: {hours}.": "Saldo de {kind} ahora: {hours}.",
  "{kind}: this time off took {hours}.": "{kind}: este tiempo libre us\u00f3 {hours}.",
  "{kind}: this request is {hours}, and would leave {left}.": "{kind}: esta solicitud es de {hours} y dejar\u00eda {left}.",
  "Your request is sent. The person who approves time off decides.": "Su solicitud se envi\u00f3. La persona que aprueba el tiempo libre decide.",
  "Leave balances": "Saldos de tiempo libre",
  "You can use it from {date}.": "Lo puede usar a partir del {date}.",
  "Your leave balances did not load.": "Sus saldos de tiempo libre no se cargaron.",
  "Once you send it, the request shows what it would leave.": "Una vez enviada, la solicitud muestra cu\u00e1nto le dejar\u00eda.",
  },
};

// French, key for key with the Spanish above. It is added here
// rather than inside the literal so the table above still ends
// where the audit suite's reader looks for its end. French sets a
// no-break space (\u00a0) before ? ! : ; and inside the
// guillemets that name a button, so a line never breaks between
// a word and its mark.
WORDS.fr = {
  // Sign in
  "2155550000 (no dashes needed)": "2155550000 (sans tirets)",
  "4 digits. Not all the same, not in a row like 1234, and not your badge number. The PIN you were given works until you save a new one.": "4 chiffres. Pas tous identiques, pas \u00e0 la suite comme 1234, et pas votre num\u00e9ro de badge. Le code PIN qu'on vous a donn\u00e9 fonctionne jusqu'\u00e0 ce que vous en enregistriez un nouveau.",
  "4-digit PIN": "4 chiffres",
  "9001, 2155550101 or name@email.com": "9001, 2155550101 ou nom@courriel.com",
  "Account Activation": "Activation du compte",
  "Activate Account": "Activer le compte",
  "Activating...": "Activation...",
  "After too many wrong tries, sign-in stops for a while. Ask your supervisor for help.": "Apr\u00e8s trop d'essais incorrects, la connexion est bloqu\u00e9e pendant un moment. Demandez de l'aide \u00e0 votre superviseur.",
  "and can be used once.": "et ne peut servir qu'une fois.",
  "Ask your supervisor to confirm your badge number.": "Demandez \u00e0 votre superviseur de confirmer votre num\u00e9ro de badge.",
  "Back to Login": "Retour \u00e0 la connexion",
  "Back to Sign In": "Retour \u00e0 la connexion",
  "Badge Number": "Num\u00e9ro de badge",
  "Badge number": "Num\u00e9ro de badge",
  "Badge Number, Phone or Email": "Num\u00e9ro de badge, t\u00e9l\u00e9phone ou courriel",
  "Checking your link...": "V\u00e9rification de votre lien...",
  "Choose your new 4-digit PIN.": "Choisissez votre nouveau code PIN \u00e0 4 chiffres.",
  "Confirm PIN": "Confirmer le code PIN",
  "Confirm PIN *": "Confirmer le code PIN *",
  "Confirm your badge number and choose your 4-digit PIN.": "Confirmez votre num\u00e9ro de badge et choisissez votre code PIN \u00e0 4 chiffres.",
  "Contact your supervisor to have a new link sent.": "Demandez \u00e0 votre superviseur de vous envoyer un nouveau lien.",
  "Could not reach OCSA. Check your connection and try again.": "Impossible de joindre OCSA. V\u00e9rifiez votre connexion et r\u00e9essayez.",
  "Dark Mode": "Mode sombre",
  "Email Address": "Adresse courriel",
  "Email Address *": "Adresse courriel *",
  "Enter the badge number, phone number or email address on your account and we will email you a link to choose a new PIN.": "Entrez le num\u00e9ro de badge, le t\u00e9l\u00e9phone ou le courriel de votre compte et nous vous enverrons un lien pour choisir un nouveau code PIN.",
  "Enter your badge number, phone number or email address.": "Entrez votre num\u00e9ro de badge, votre t\u00e9l\u00e9phone ou votre courriel.",
  "Fill in {field}.": "Remplissez {field}.",
  "First Name": "Pr\u00e9nom",
  "First name": "Pr\u00e9nom",
  "First Name *": "Pr\u00e9nom *",
  "Forgot your PIN?": "Code PIN oubli\u00e9\u00a0?",
  "Forgotten your badge number? You can also sign in with your phone number or your email address.": "Num\u00e9ro de badge oubli\u00e9\u00a0? Vous pouvez aussi vous connecter avec votre t\u00e9l\u00e9phone ou votre courriel.",
  "Got a sign-in slip from OCSA? Go back and sign in with it.": "Vous avez re\u00e7u une fiche de connexion d'OCSA\u00a0? Revenez en arri\u00e8re et connectez-vous avec.",
  "If that matches an account on file, a reset link is on its way. The link is good for one hour.": "Si cela correspond \u00e0 un compte enregistr\u00e9, un lien est en route. Le lien est valable une heure.",
  "If you do not have an email address on file, no link can reach you. Contact your supervisor to have your PIN reset directly.": "Si vous n'avez pas de courriel enregistr\u00e9, aucun lien ne peut vous parvenir. Demandez \u00e0 votre superviseur de r\u00e9initialiser votre code PIN directement.",
  "Language": "Langue",
  "Last Name": "Nom",
  "Last name": "Nom",
  "Light Mode": "Mode clair",
  "Loading...": "Chargement...",
  "name@email.com": "nom@courriel.com",
  "New Employee? Register Here": "Nouvel employ\u00e9\u00a0? Inscrivez-vous ici",
  "New PIN (4 digits)": "Nouveau code PIN (4 chiffres)",
  "New Staff Registration": "Inscription du personnel",
  "No email on file? A link cannot reach you. Ask your supervisor to reset your PIN.": "Pas de courriel enregistr\u00e9\u00a0? Aucun lien ne peut vous parvenir. Demandez \u00e0 votre superviseur de r\u00e9initialiser votre code PIN.",
  "Not you? Sign out": "Ce n'est pas vous\u00a0? Se d\u00e9connecter",
  "Phone Number": "Num\u00e9ro de t\u00e9l\u00e9phone",
  "Phone Number *": "Num\u00e9ro de t\u00e9l\u00e9phone *",
  "PIN": "Code PIN",
  "PIN (4 digits)": "Code PIN (4 chiffres)",
  "PIN (4 digits) *": "Code PIN (4 chiffres) *",
  "PIN saved. Signing you in...": "Code PIN enregistr\u00e9. Connexion en cours...",
  "PIN set. Signing you in...": "Code PIN enregistr\u00e9. Connexion en cours...",
  "Register": "S'inscrire",
  "Registering...": "Inscription...",
  "Request a New Link": "Demander un nouveau lien",
  "Reset Your PIN": "R\u00e9initialiser votre code PIN",
  "Save PIN": "Enregistrer le code PIN",
  "Saving...": "Enregistrement...",
  "Send Reset Link": "Envoyer le lien",
  "Sending...": "Envoi...",
  "Set your own PIN. The PIN you were given is known to your supervisor. Choose a new one that only you know.": "Choisissez votre propre code PIN. Votre superviseur conna\u00eet le code PIN qu'on vous a donn\u00e9. Choisissez-en un nouveau que personne d'autre ne conna\u00eet.",
  "Choose your PIN": "Choisissez votre code PIN",
  "Sign In": "Se connecter",
  "Signing in will work once your account is active.": "Vous pourrez vous connecter quand votre compte sera actif.",
  "Signing in...": "Connexion...",
  "Enter the code we emailed to {0}": "Saisissez le code que nous avons envoy\u00e9 \u00e0 {0}",
  "Verify": "V\u00e9rifier",
  "Send a new code": "Envoyer un nouveau code",
  "Remember this device for 30 days": "Se souvenir de cet appareil pendant 30 jours",
  "{n} tries left": "Il vous reste {n} essais",
  "1 try left": "Il vous reste 1 essai",
  "Something went wrong on our end. Try again in a minute.": "Un probl\u00e8me est survenu de notre c\u00f4t\u00e9. R\u00e9essayez dans une minute.",
  "Staff Operations Portal": "Portail du personnel",
  "The number on the email we sent you.": "Le num\u00e9ro indiqu\u00e9 dans le courriel que nous vous avons envoy\u00e9.",
  "The two PINs do not match. Type the same 4 digits in both fields.": "Les deux codes PIN ne correspondent pas. Tapez les m\u00eames 4 chiffres dans les deux champs.",
  "This activation link is incomplete. Open the link from your email again.": "Ce lien d'activation est incomplet. Ouvrez de nouveau le lien de votre courriel.",
  "This link is no longer valid. Links expire, and each one can only be used once.": "Ce lien n'est plus valable. Les liens expirent et chacun ne peut servir qu'une fois.",
  "This link works until": "Ce lien est valable jusqu'au",
  "This reset link is incomplete. Open the link from your email again.": "Ce lien de r\u00e9initialisation est incomplet. Ouvrez de nouveau le lien de votre courriel.",
  "Try Again": "R\u00e9essayer",
  "Welcome back, {name}.": "Bon retour, {name}.",
  "Welcome, {name}.": "Bienvenue, {name}.",
  "Your account is activated but not currently active. Contact your supervisor.": "Votre compte est activ\u00e9 mais il n'est pas actif pour le moment. Contactez votre superviseur.",
  "Your PIN has been changed. Contact your supervisor about your account status.": "Votre code PIN a \u00e9t\u00e9 chang\u00e9. Contactez votre superviseur au sujet de l'\u00e9tat de votre compte.",
  "Your PIN has been saved. Signing in will work once your account is active.": "Votre code PIN est enregistr\u00e9. Vous pourrez vous connecter quand votre compte sera actif.",
  "PIN must be exactly 4 digits.": "Le code PIN doit contenir exactement 4 chiffres.",
  "Enter the badge number from your email.": "Entrez le num\u00e9ro de badge indiqu\u00e9 dans votre courriel.",
  // App wide
  "A new version is ready": "Une nouvelle version est pr\u00eate",
  "Add {app} to your home screen": "Ajoutez {app} \u00e0 votre \u00e9cran d'accueil",
  "Confirm on the next screen.": "Confirmez sur l'\u00e9cran suivant.",
  "Copied": "Copi\u00e9",
  "Copy address": "Copier l'adresse",
  "Could not find your open shift. Reload and try again.": "Votre service ouvert est introuvable. Rechargez la page et r\u00e9essayez.",
  "Don't show again": "Ne plus afficher",
  "Edit shortcuts": "Modifier les raccourcis",
  "End your shift at {site}?": "Terminer votre service \u00e0 {site}\u00a0?",
  "Extra large": "Tr\u00e8s grand",
  "Install": "Installer",
  "Issue reported": "Probl\u00e8me signal\u00e9",
  "It opens like an app, one tap from your home screen.": "Elle s'ouvre comme une application, en un geste depuis votre \u00e9cran d'accueil.",
  "Large": "Grand",
  "Largest": "Le plus grand",
  "Low stock alert!": "Alerte\u00a0: stock bas\u00a0!",
  "More": "Plus",
  "Not now": "Pas maintenant",
  "Notifications": "Notifications",
  "ON SITE": "SUR PLACE",
  "Open the browser menu. It is usually three dots at the top right.": "Ouvrez le menu du navigateur. Ce sont souvent trois points en haut \u00e0 droite.",
  "PIN updated": "Code PIN mis \u00e0 jour",
  "Profile": "Profil",
  "Reload": "Recharger",
  "Request submitted": "Demande envoy\u00e9e",
  "Scroll down and tap Add to Home Screen.": "Faites d\u00e9filer vers le bas et appuyez sur \u00ab\u00a0Sur l'\u00e9cran d'accueil\u00a0\u00bb.",
  "Select a site first": "Choisissez d'abord un site",
  "Settings": "Param\u00e8tres",
  "Shift ended": "Service termin\u00e9",
  "Shift ended at {time}": "Service termin\u00e9 \u00e0 {time}",
  "Shift started": "Service commenc\u00e9",
  "Shortcuts saved": "Raccourcis enregistr\u00e9s",
  "Sign out": "Se d\u00e9connecter",
  "Something went wrong on this screen.": "Un probl\u00e8me est survenu sur cet \u00e9cran.",
  "Standard": "Normal",
  "Tap Add or Install.": "Appuyez sur \u00ab\u00a0Ajouter\u00a0\u00bb ou \u00ab\u00a0Installer\u00a0\u00bb.",
  "Tap Add to Home screen or Install app.": "Appuyez sur \u00ab\u00a0Ajouter \u00e0 l'\u00e9cran d'accueil\u00a0\u00bb ou \u00ab\u00a0Installer l'application\u00a0\u00bb.",
  "Tap Add to Home Screen.": "Appuyez sur \u00ab\u00a0Sur l'\u00e9cran d'accueil\u00a0\u00bb.",
  "Tap Add.": "Appuyez sur \u00ab\u00a0Ajouter\u00a0\u00bb.",
  "Tap Install below.": "Appuyez sur \u00ab\u00a0Installer\u00a0\u00bb ci-dessous.",
  "Tap Share {share} at the bottom of the screen. If you do not see it, tap the three dots first, then Share.": "Appuyez sur \u00ab\u00a0Partager\u00a0\u00bb {share} en bas de l'\u00e9cran. Si vous ne le voyez pas, appuyez d'abord sur les trois points, puis sur \u00ab\u00a0Partager\u00a0\u00bb.",
  "Tap the Share button {share} in the address bar.": "Appuyez sur le bouton \u00ab\u00a0Partager\u00a0\u00bb {share} dans la barre d'adresse.",
  "Task completed": "T\u00e2che termin\u00e9e",
  "Task unchecked": "T\u00e2che d\u00e9coch\u00e9e",
  "Task updated to {status}": "Statut de la t\u00e2che\u00a0: {status}",
  "That sign-in did not match. Check your badge, phone or email and your PIN.": "Ces informations ne correspondent pas. V\u00e9rifiez votre badge, votre t\u00e9l\u00e9phone ou votre courriel, et votre code PIN.",
  "This list did not load.": "Cette liste ne s'est pas charg\u00e9e.",
  "This page is open inside another app. Open it in Safari or Chrome first, then add it to your home screen.": "Cette page est ouverte dans une autre application. Ouvrez-la d'abord dans Safari ou Chrome, puis ajoutez-la \u00e0 votre \u00e9cran d'accueil.",
  "This shift was already ended": "Ce service \u00e9tait d\u00e9j\u00e0 termin\u00e9",
  "this site": "ce site",
  "Update now": "Mettre \u00e0 jour",
  "Welcome, {name}": "Bienvenue, {name}",
  "{count} unread notifications": "{count} notifications non lues",
  "{brand} Staff Portal": "Portail du personnel {brand}",
  "Could not read image": "Impossible de lire l'image",
  "Compression failed": "Impossible de pr\u00e9parer la photo",
  "Custodian": "Agent d'entretien",
  "Custodial Lead": "Chef d'\u00e9quipe d'entretien",
  "Lead": "Chef d'\u00e9quipe",
  "Supervisor": "Superviseur",
  "Admin": "Administrateur",
  "Custodial Laborer": "Agent d'entretien",
  "Day Porter": "Agent d'entretien de jour",
  "Contractor": "Sous-traitant",
  "in progress": "en cours",
  "resolved": "r\u00e9solue",
  "unable to resolve": "non r\u00e9solue",
  // Home
  "All Other Sites": "Tous les autres sites",
  "Choose a Site to Start": "Choisissez un site pour commencer",
  "Drop requested": "Retrait demand\u00e9",
  "End Shift": "Terminer le service",
  "Ending...": "Fin en cours...",
  "Floor {n}": "\u00c9tage {n}",
  "Loading sites...": "Chargement des sites...",
  "No sites available yet.": "Aucun site disponible pour le moment.",
  "requested": "demand\u00e9",
  "Scheduled Today": "Pr\u00e9vu aujourd'hui",
  "Shift Open at {site}": "Service ouvert \u00e0 {site}",
  "Start Shift": "Commencer le service",
  "Start Shift at {site}": "Commencer le service \u00e0 {site}",
  "Started at {time}": "Commenc\u00e9 \u00e0 {time}",
  "Started {day} at {time}": "Commenc\u00e9 {day} \u00e0 {time}",
  "Starting...": "D\u00e9marrage...",
  "Time off": "Cong\u00e9",
  "Time on Site": "Temps sur place",
  "You asked to drop this shift. Waiting for a decision.": "Vous avez demand\u00e9 \u00e0 vous retirer de ce service. En attente d'une d\u00e9cision.",
  "Your Assigned Sites": "Vos sites attribu\u00e9s",
  "A shift is still open at {site}. End it before starting another.": "Un service est encore ouvert \u00e0 {site}. Terminez-le avant d'en commencer un autre.",
  "A shift is still open. End it before starting another.": "Un service est encore ouvert. Terminez-le avant d'en commencer un autre.",
  "Your sites did not load.": "Vos sites ne se sont pas charg\u00e9s.",
  // Schedule
  " (live)": " (en cours)",
  " ({h}h)": " ({h} h)",
  "Any additional details...": "Autres d\u00e9tails...",
  "approved": "approuv\u00e9",
  "Approved": "Approuv\u00e9e",
  "Asked": "Demand\u00e9 le",
  "Bereavement and personal": "Deuil et raisons personnelles",
  "Building": "B\u00e2timent",
  "Cancel": "Annuler",
  "Cancel request": "Annuler la demande",
  "Cancel this time off request?": "Annuler cette demande de cong\u00e9\u00a0?",
  "Cancelled": "Annul\u00e9e",
  "Choose a type": "Choisissez un type",
  "claimed": "pris",
  "Close": "Fermer",
  "Completed": "Termin\u00e9",
  "Dates": "Dates",
  "Decided": "D\u00e9cid\u00e9 le",
  "Denied": "Refus\u00e9e",
  "Describe the reason": "D\u00e9crivez la raison",
  "Drop Request": "Demande de retrait",
  "Drop request failed": "La demande n'a pas pu \u00eatre envoy\u00e9e",
  "Drop request sent. Your supervisor will review it.": "Demande envoy\u00e9e. Votre superviseur va l'examiner.",
  "Duration": "Dur\u00e9e",
  "Emergency": "Urgence",
  "End": "Fin",
  "First day": "Premier jour",
  "Floor": "\u00c9tage",
  "For example, 8": "Par exemple, 8",
  "From": "De",
  "Hours": "Heures",
  "Hours (optional)": "Heures (facultatif)",
  "Jury duty": "Devoir de jur\u00e9",
  "Keep it": "La garder",
  "Last day": "Dernier jour",
  "Military leave": "Cong\u00e9 militaire",
  "Month": "Mois",
  "My Schedule": "Mon planning",
  "My time off": "Mes cong\u00e9s",
  "N/A": "Non disponible",
  "No note": "Aucune note",
  "No reason given": "Aucune raison donn\u00e9e",
  "No time off requests yet.": "Aucune demande de cong\u00e9 pour le moment.",
  "Not given": "Non indiqu\u00e9",
  "Note": "Note",
  "Note: {note}": "Note\u00a0: {note}",
  "Notes": "Notes",
  "Notes (optional)": "Notes (facultatif)",
  "Nothing scheduled this day.": "Rien de pr\u00e9vu ce jour-l\u00e0.",
  "On Site": "Sur place",
  "Only you and the person who approves time off can read this.": "Seuls vous et la personne qui approuve les cong\u00e9s pouvez lire ceci.",
  "Other": "Autre",
  "Paid sick leave": "Cong\u00e9 maladie pay\u00e9",
  "Part of the day": "Une partie de la journ\u00e9e",
  "Personal": "Raison personnelle",
  "Pickup": "Services libres",
  "Pickup Shift": "Service pris",
  "Reason": "Raison",
  "Reason (optional)": "Raison (facultatif)",
  "Request time off": "Demander un cong\u00e9",
  "Request to Drop This Shift": "Demander le retrait de ce service",
  "Requested": "Demand\u00e9e",
  "Scheduled": "Pr\u00e9vu",
  "Scheduled Shift": "Service pr\u00e9vu",
  "Scheduling Conflict": "Conflit d'horaire",
  "Send request": "Envoyer la demande",
  "Service": "Prestation",
  "Shift End": "Fin du service",
  "Shift Start": "D\u00e9but du service",
  "Shifts on these days": "Services ces jours-l\u00e0",
  "Showing your latest 50 requests.": "Vos 50 demandes les plus r\u00e9centes sont affich\u00e9es.",
  "Sick": "Maladie",
  "Site": "Site",
  "Specify Reason": "Pr\u00e9cisez la raison",
  "Start": "D\u00e9but",
  "Status": "Statut",
  "Still on site": "Encore sur place",
  "Submit Request": "Envoyer la demande",
  "Time": "Heure",
  "Time off request": "Demande de cong\u00e9",
  "Time off request cancelled.": "Demande de cong\u00e9 annul\u00e9e.",
  "Time off requested. You get a notice when it is decided.": "Cong\u00e9 demand\u00e9. Vous recevrez un avis quand une d\u00e9cision sera prise.",
  "To": "\u00c0",
  "Today": "Aujourd'hui",
  "Type": "Type",
  "Unpaid time off": "Cong\u00e9 sans solde",
  "Week": "Semaine",
  "Worked": "Effectu\u00e9",
  "Worked Shift": "Service effectu\u00e9",
  "You get a notice in the app when your request is decided.": "Vous recevrez un avis dans l'application quand une d\u00e9cision sera prise sur votre demande.",
  "Your supervisor will review this request. If approved, the shift will be opened for pickup or reassigned.": "Votre superviseur va examiner cette demande. Si elle est approuv\u00e9e, le service sera propos\u00e9 \u00e0 quelqu'un d'autre ou r\u00e9attribu\u00e9.",
  "{h}h {m}m": "{h} h {m} min",
  "{n} hour": "{n} heure",
  "{n} hours": "{n} heures",
  "{start} to {end}": "{start} \u00e0 {end}",
  "{start} to {end}, {year}": "{start} \u00e0 {end} {year}",
  "Pending": "En attente",
  "Claimed": "Pris",
  "Filled": "Pourvu",
  // Tasks
  "Back to checklist": "Retour \u00e0 la liste",
  "Due Date:": "Date limite\u00a0:",
  "Instructions": "Instructions",
  "Loading tasks...": "Chargement des t\u00e2ches...",
  "Mark Complete": "Marquer comme termin\u00e9e",
  "Mark {name} done": "Marquer {name} comme termin\u00e9e",
  "Mark {name} not done": "D\u00e9cocher {name}",
  "No checklist is set up for this building yet.": "Aucune liste de t\u00e2ches n'est encore pr\u00e9vue pour ce b\u00e2timent.",
  "No tasks loaded. Start your shift at a site to see your checklist.": "Aucune t\u00e2che charg\u00e9e. Commencez votre service sur un site pour voir votre liste.",
  "PRIORITY": "PRIORIT\u00c9",
  "Reference Photo": "Photo de r\u00e9f\u00e9rence",
  "Reference Video": "Vid\u00e9o de r\u00e9f\u00e9rence",
  "Start your shift to see and check off your tasks.": "Commencez votre service pour voir et cocher vos t\u00e2ches.",
  "Task reference": "R\u00e9f\u00e9rence de la t\u00e2che",
  "Time:": "Heure\u00a0:",
  "Try again": "R\u00e9essayer",
  "Uncheck Task": "D\u00e9cocher la t\u00e2che",
  "Your Assignment": "Votre affectation",
  "Your tasks did not load.": "Vos t\u00e2ches ne se sont pas charg\u00e9es.",
  "General": "G\u00e9n\u00e9ral",
  "Which shift are you working?": "Quel service faites-vous\u00a0?",
  "Chosen by the time you started. Change it if it is wrong.": "Choisi selon l'heure o\u00f9 vous avez commenc\u00e9. Changez-le s'il n'est pas bon.",
  "Use this shift": "Choisir ce service",
  "Change shift": "Changer de service",
  "This week": "Cette semaine",
  "Every two weeks": "Toutes les deux semaines",
  "This month": "Ce mois-ci",
  "This quarter": "Ce trimestre",
  "This season": "Cette saison",
  "As needed": "Selon les besoins",
  "{done} of {total} done": "{done} sur {total} termin\u00e9es",
  "Done today by {firstName}": "Fait aujourd'hui par {firstName}",
  "Done yesterday by {firstName}": "Fait hier par {firstName}",
  "Done {weekday} by {firstName}": "Fait {weekday} par {firstName}",
  "Done {date} by {firstName}": "Fait le {date} par {firstName}",
  "Checked by {firstName}": "Coch\u00e9 par {firstName}",
  "Only the person who checked this can uncheck it.": "Seule la personne qui a coch\u00e9 cette t\u00e2che peut la d\u00e9cocher.",
  // Chat
  "Admin (Private)": "Administration (priv\u00e9)",
  "No chats are set up for you yet. Ask your supervisor.": "Aucune discussion n'est encore pr\u00e9vue pour vous. Demandez \u00e0 votre superviseur.",
  "No messages yet.": "Aucun message pour le moment.",
  "No one else can read this chat.": "Personne d'autre ne peut lire cette discussion.",
  "Only you and management can see these messages": "Seuls vous et la direction voyez ces messages",
  "Pick a chat at the top first.": "Choisissez d'abord une discussion en haut.",
  "Pick a chat to start.": "Choisissez une discussion pour commencer.",
  "Private chats": "Discussions priv\u00e9es",
  "Private conversation with admin.": "Conversation priv\u00e9e avec l'administration.",
  "Private message to admin...": "Message priv\u00e9 \u00e0 l'administration...",
  "Tag someone": "Mentionner quelqu'un",
  "Type a message...": "\u00c9crivez un message...",
  "You cannot send messages in this chat.": "Vous ne pouvez pas envoyer de messages dans cette discussion.",
  "Your chats did not load. Try again in a minute.": "Vos discussions ne se sont pas charg\u00e9es. R\u00e9essayez dans une minute.",
  "Your message was not sent.": "Votre message n'a pas \u00e9t\u00e9 envoy\u00e9.",
  "New message": "Nouveau message",
  "Office": "Bureau",
  "That chat did not open. Try again.": "Cette discussion ne s'est pas ouverte. R\u00e9essayez.",
  "Direct messages": "Messages directs",
  // Help
  "Add a photo": "Ajouter une photo",
  "Based on": "D'apr\u00e8s",
  "Picture of the screen": "Image de l'\u00e9cran",
  "Describe what happened": "D\u00e9crivez ce qui s'est pass\u00e9",
  "Discard": "Abandonner",
  "Discard this report? It will not be sent.": "Abandonner ce rapport\u00a0? Il ne sera pas envoy\u00e9.",
  "Fill in form": "Remplir le formulaire",
  "general cleaning guidance": "un guide g\u00e9n\u00e9ral de nettoyage",
  "Not sent.": "Non envoy\u00e9.",
  "Remove photo": "Retirer la photo",
  "Report discarded.": "Rapport abandonn\u00e9.",
  "Report in progress": "Rapport en cours",
  "Report submitted.": "Rapport envoy\u00e9.",
  "Resume": "Reprendre",
  "Retry": "R\u00e9essayer",
  "Send": "Envoyer",
  "Still needed before you can submit:": "Il manque ceci avant de pouvoir envoyer\u00a0:",
  "Submit report": "Envoyer le rapport",
  "Submitting...": "Envoi...",
  "Tell me what happened and I will tell you what to do.": "Dites-moi ce qui s'est pass\u00e9 et je vous dirai quoi faire.",
  "Thanks. This helps Help get better.": "Merci. Cela aide \u00e0 am\u00e9liorer l'Aide.",
  "the ADP guide": "le guide ADP",
  "the app guide": "le guide de l'application",
  "The connection dropped. Your answer is saved.": "La connexion a \u00e9t\u00e9 coup\u00e9e. Votre r\u00e9ponse est enregistr\u00e9e.",
  "This photo could not be read here. Choose a JPEG or PNG, or take a screenshot of it.": "Cette photo ne peut pas \u00eatre lue ici. Choisissez un JPEG ou un PNG, ou faites une capture d'\u00e9cran.",
  "Unfinished reports": "Rapports non termin\u00e9s",
  "Unfinished reports did not load.": "Les rapports non termin\u00e9s ne se sont pas charg\u00e9s.",
  "Uploading...": "Envoi du fichier...",
  "Was this helpful?": "Cela vous a aid\u00e9\u00a0?",
  "What was missing?": "Que manquait-il\u00a0?",
  "Working from the written procedure only right now.": "Pour l'instant, seule la proc\u00e9dure \u00e9crite est utilis\u00e9e.",
  "x": "x",
  "You can send up to 3 photos with one message.": "Vous pouvez envoyer jusqu'\u00e0 3 photos par message.",
  "{answered} of {total} answered": "R\u00e9ponses\u00a0: {answered} sur {total}",
  // Assigned
  "A photo of the completed task is required": "Une photo de la t\u00e2che termin\u00e9e est obligatoire",
  "Assigned": "Assign\u00e9es",
  "Assigned by": "Assign\u00e9e par",
  "Assigned Tasks": "T\u00e2ches assign\u00e9es",
  "at {time}": "\u00e0 {time}",
  "Attached Photo": "Photo jointe",
  "Attached Video": "Vid\u00e9o jointe",
  "Back to assigned tasks": "Retour aux t\u00e2ches assign\u00e9es",
  "Cannot Resolve": "Impossible de r\u00e9soudre",
  "Describe the issue preventing completion...": "D\u00e9crivez ce qui emp\u00eache de la terminer...",
  "Describe the steps you took...": "D\u00e9crivez les \u00e9tapes que vous avez suivies...",
  "Describe what you did to complete this task": "D\u00e9crivez ce que vous avez fait pour terminer cette t\u00e2che",
  "Description": "Description",
  "Due Date": "Date limite",
  "Due:": "\u00c9ch\u00e9ance\u00a0:",
  "Explain why this cannot be completed *": "Expliquez pourquoi elle ne peut pas \u00eatre termin\u00e9e *",
  "In Progress": "En cours",
  "ISSUE": "PROBL\u00c8ME",
  "Mark as Resolved": "Marquer comme r\u00e9solue",
  "No assigned tasks right now.": "Aucune t\u00e2che assign\u00e9e pour le moment.",
  "Photo must be under 10MB": "La photo doit faire moins de 10 Mo",
  "Photo of completed task *": "Photo de la t\u00e2che termin\u00e9e *",
  "Please provide a reason": "Indiquez une raison",
  "Preview": "Aper\u00e7u",
  "Reported by": "Signal\u00e9e par",
  "Required to verify completion": "N\u00e9cessaire pour v\u00e9rifier que c'est termin\u00e9",
  "Resolved": "R\u00e9solue",
  "Submit": "Envoyer",
  "Submit Resolution": "Envoyer la r\u00e9solution",
  "Take Photo of Completed Task": "Prendre une photo de la t\u00e2che termin\u00e9e",
  "Task": "T\u00e2che",
  "What did you do to complete this? *": "Qu'avez-vous fait pour la terminer\u00a0? *",
  "When a supervisor assigns a task to you, it will appear here.": "Quand un superviseur vous assigne une t\u00e2che, elle appara\u00eet ici.",
  "Unable to resolve": "Non r\u00e9solue",
  "Medium": "Moyenne",
  "Critical": "Critique",
  // Report
  "+ Report": "+ Signaler",
  "Additional details...": "Autres d\u00e9tails...",
  "Brief description": "Br\u00e8ve description",
  "Details": "D\u00e9tails",
  "e.g. Restroom, Lobby": "par exemple\u00a0: toilettes, hall d'entr\u00e9e",
  "Enter issue title": "Entrez un titre pour le probl\u00e8me",
  "High": "Haute",
  "Issues": "Probl\u00e8mes",
  "JPG, PNG up to 10MB": "JPG ou PNG, 10 Mo maximum",
  "Low": "Basse",
  "Med": "Moy.",
  "No issues reported yet.": "Aucun probl\u00e8me signal\u00e9 pour le moment.",
  "Photo": "Photo",
  "Photo attached:": "Photo jointe\u00a0:",
  "Report an Issue": "Signaler un probl\u00e8me",
  "Select a site": "Choisissez un site",
  "Select site...": "Choisissez un site...",
  "Severity": "Gravit\u00e9",
  "Submit Issue": "Envoyer le probl\u00e8me",
  "Take Photo or Choose from Gallery": "Prendre une photo ou choisir dans la galerie",
  "Title": "Titre",
  "Zone": "Zone",
  "Open": "Ouvert",
  "Closed": "Ferm\u00e9",
  // Supplies
  "+ Request": "+ Demander",
  "Damage Report": "Signalement de d\u00e9g\u00e2t",
  "Describe the request...": "D\u00e9crivez la demande...",
  "Enter the item name": "Entrez le nom de l'article",
  "Item": "Article",
  "Item Name": "Nom de l'article",
  "Log Usage": "Noter l'utilisation",
  "Log usage or submit a request": "Notez l'utilisation ou envoyez une demande",
  "LOW": "BAS",
  "New Gear": "Nouvel \u00e9quipement",
  "No supplies are set up for this site.": "Aucune fourniture n'est pr\u00e9vue pour ce site.",
  "One less": "Un de moins",
  "One more": "Un de plus",
  "New Supply": "Nouvelle fourniture",
  "Normal": "Normale",
  "QR": "QR",
  "Refill": "R\u00e9approvisionnement",
  "Request Type": "Type de demande",
  "Select a request type": "Choisissez un type de demande",
  "Start your shift to log usage. Requests can be submitted anytime.": "Commencez votre service pour noter l'utilisation. Les demandes peuvent \u00eatre envoy\u00e9es \u00e0 tout moment.",
  "Supplies": "Fournitures",
  "Supply Item": "Article",
  "Supply Tracking": "Suivi des fournitures",
  "Supply/Gear Request": "Demande de fournitures ou d'\u00e9quipement",
  "This Shift's Log": "Relev\u00e9 de ce service",
  "Urgency": "Urgence",
  "Urgent": "Urgente",
  "What do you need?": "De quoi avez-vous besoin\u00a0?",
  // Pickup
  "Approved. You are scheduled for this shift.": "Approuv\u00e9. Ce service est pr\u00e9vu pour vous.",
  "Available": "Disponibles",
  "Bldg:": "B\u00e2t.\u00a0:",
  "Callout": "Absence",
  "Check back later for available pickup shifts at your assigned sites.": "Revenez plus tard pour voir les services libres sur vos sites attribu\u00e9s.",
  "Claim This Shift": "Prendre ce service",
  "Claiming...": "Prise en cours...",
  "Extra Coverage": "Renfort",
  "Floor:": "\u00c9tage\u00a0:",
  "My Pickups": "Mes services",
  "New Shift": "Nouveau service",
  "No claimed shifts": "Aucun service pris",
  "No open shifts right now": "Aucun service libre pour le moment",
  "No-Show": "Absence sans pr\u00e9venir",
  "Release Shift": "Lib\u00e9rer le service",
  "Release this shift? It will go back to the open pool for someone else to claim.": "Lib\u00e9rer ce service\u00a0? Il retournera dans la liste des services libres pour qu'une autre personne le prenne.",
  "Shift claimed (overtime warning: {hours}h this week)": "Service pris (attention aux heures suppl\u00e9mentaires\u00a0: {hours} h cette semaine)",
  "Shift claimed successfully!": "Service pris\u00a0!",
  "Shift Pickup Board": "Services libres",
  "Shift released": "Service lib\u00e9r\u00e9",
  "Shifts you claim will appear here.": "Les services que vous prenez appara\u00eetront ici.",
  "Start your shift at the normal time and your daily tasks will load automatically.": "Commencez votre service \u00e0 l'heure habituelle et vos t\u00e2ches du jour se chargeront toutes seules.",
  "URGENT": "URGENT",
  "Voluntary Drop": "Retrait volontaire",
  "Waiting for manager approval": "En attente de l'accord du responsable",
  // Inspect
  "+ Schedule": "+ Planifier",
  "Attach Photo": "Joindre une photo",
  "General observations, follow-ups needed, etc.": "Observations g\u00e9n\u00e9rales, suivis n\u00e9cessaires, etc.",
  "Inspection scheduled": "Inspection planifi\u00e9e",
  "Inspection sent. {n} problem reported for fixing.": "Inspection envoy\u00e9e. {n} probl\u00e8me signal\u00e9 \u00e0 r\u00e9parer.",
  "Inspection sent. {n} problems reported for fixing.": "Inspection envoy\u00e9e. {n} probl\u00e8mes signal\u00e9s \u00e0 r\u00e9parer.",
  "Inspection submitted": "Inspection envoy\u00e9e",
  "Inspections assigned to you will appear here.": "Les inspections qui vous sont assign\u00e9es appara\u00eetront ici.",
  "My Inspections": "Mes inspections",
  "Needs a fix": "\u00c0 r\u00e9parer",
  "No inspections pending": "Aucune inspection en attente",
  "Not due yet": "Pas encore \u00e0 faire",
  "Not reported: {0}": "Non signal\u00e9\u00a0: {0}",
  "Notes for this item (optional)": "Notes pour ce point (facultatif)",
  "Overall Notes": "Notes g\u00e9n\u00e9rales",
  "Photo attached": "Photo jointe",
  "pts": "pts",
  "Running total": "Total en cours",
  "Say what needs fixing": "Dites ce qu'il faut r\u00e9parer",
  "Schedule Inspection": "Planifier une inspection",
  "Scheduled Date *": "Date pr\u00e9vue *",
  "Scheduling...": "Planification...",
  "Scored": "Not\u00e9s",
  "Select template...": "Choisissez un mod\u00e8le...",
  "Show {0} scored items": "Afficher les {0} points not\u00e9s",
  "Sections": "Sections",
  "{scored} of {total} scored": "{scored} sur {total} not\u00e9s",
  "Site *": "Site *",
  "Submit Inspection": "Envoyer l'inspection",
  "Photos": "Photos",
  "Photos of the whole inspection": "Photos de l'ensemble de l'inspection",
  "Below 70 percent: add a photo of it.": "Moins de 70 pour cent\u00a0: ajoutez une photo.",
  "Signed as {name}, {date}": "Sign\u00e9 par {name}, {date}",
  "Sign before you send.": "Signez avant d'envoyer.",
  "Tap an inspection to begin scoring.": "Appuyez sur une inspection pour commencer \u00e0 noter.",
  "Tap to start scoring": "Appuyez pour commencer \u00e0 noter",
  "Template *": "Mod\u00e8le *",
  "Template, site, and date are required": "Le mod\u00e8le, le site et la date sont obligatoires",
  // Speak Up
  "Anyone you pick below will not be able to see this report.": "Les personnes que vous choisissez ci-dessous ne pourront pas voir ce signalement.",
  "Is this about someone in management?": "Est-ce que cela concerne une personne de la direction\u00a0?",
  "No": "Non",
  "No one matches that name.": "Aucune personne ne correspond \u00e0 ce nom.",
  "Nothing you write here is kept. If you leave this screen before you send, it is gone.": "Rien de ce que vous \u00e9crivez ici n'est gard\u00e9. Si vous quittez cet \u00e9cran avant d'envoyer, tout est perdu.",
  "Optional": "Facultatif",
  "Please try again.": "Veuillez r\u00e9essayer.",
  "Quote this if you follow up.": "Donnez ce num\u00e9ro si vous faites un suivi.",
  "Report a problem with someone": "Signaler un probl\u00e8me avec une personne",
  "Search by name": "Rechercher par nom",
  "Someone will be in touch with you within 72 hours.": "Une personne vous contactera dans les 72 heures.",
  "The staff list did not load. Try again in a minute.": "La liste du personnel ne s'est pas charg\u00e9e. R\u00e9essayez dans une minute.",
  "We got your report.": "Nous avons re\u00e7u votre signalement.",
  "What happened": "Ce qui s'est pass\u00e9",
  "Who is involved?": "Qui est concern\u00e9\u00a0?",
  "Who is it about? Pick at least one person.": "De qui s'agit-il\u00a0? Choisissez au moins une personne.",
  "Write what happened in your own words. One sentence is enough.": "\u00c9crivez ce qui s'est pass\u00e9 avec vos propres mots. Une phrase suffit.",
  "Yes": "Oui",
  "You can write up to {max} characters.": "Vous pouvez \u00e9crire jusqu'\u00e0 {max} caract\u00e8res.",
  "Your reference": "Votre num\u00e9ro de r\u00e9f\u00e9rence",
  "Your report was not sent.": "Votre signalement n'a pas \u00e9t\u00e9 envoy\u00e9.",
  "{used} of {max} characters used.": "{used} caract\u00e8res utilis\u00e9s sur {max}.",
  // Forms
  "Add row": "Ajouter une ligne",
  "Answered": "R\u00e9pondu",
  "Back": "Retour",
  "Name": "Nom",
  "Clear": "Effacer",
  "Continue": "Continuer",
  "Done": "Termin\u00e9",
  "Edit": "Modifier",
  "Forms": "Formulaires",
  "Forms could not load. Check your signal and try again.": "Les formulaires ne se sont pas charg\u00e9s. V\u00e9rifiez votre r\u00e9seau et r\u00e9essayez.",
  "Keep filling": "Continuer \u00e0 remplir",
  "Leave": "Quitter",
  "Leave this report? Your saved answers stay, and you can continue from Forms or Help.": "Quitter ce rapport\u00a0? Vos r\u00e9ponses enregistr\u00e9es sont gard\u00e9es et vous pourrez continuer depuis Formulaires ou Aide.",
  "Loading forms": "Chargement des formulaires",
  "Next": "Suivant",
  "No forms to fill right now.": "Aucun formulaire \u00e0 remplir pour le moment.",
  "Not answered": "Sans r\u00e9ponse",
  "Not saved yet. Check your signal and tap Next again.": "Pas encore enregistr\u00e9. V\u00e9rifiez votre r\u00e9seau et appuyez de nouveau sur \u00ab\u00a0Suivant\u00a0\u00bb.",
  "Not sent yet. Check your signal and tap Submit report again.": "Pas encore envoy\u00e9. V\u00e9rifiez votre r\u00e9seau et appuyez de nouveau sur \u00ab\u00a0Envoyer le rapport\u00a0\u00bb.",
  "Not signed": "Non sign\u00e9",
  "Not signed yet. Check your signal and tap Sign again.": "Pas encore sign\u00e9. V\u00e9rifiez votre r\u00e9seau et appuyez de nouveau sur \u00ab\u00a0Signer\u00a0\u00bb.",
  "1 photo": "1 photo",
  "{n} photos": "{n} photos",
  "Not yet": "Pas encore",
  "Opening": "Ouverture",
  "Remove row": "Retirer la ligne",
  "Report": "Signaler",
  "Report sent. The people who handle these reports have been told.": "Rapport envoy\u00e9. Les personnes qui traitent ces rapports ont \u00e9t\u00e9 pr\u00e9venues.",
  "Required": "Obligatoire",
  "Review": "V\u00e9rification",
  "Role": "Fonction",
  "Row {n}": "Ligne {n}",
  "Save signature": "Enregistrer la signature",
  "Saving": "Enregistrement",
  "Section {n}": "Section {n}",
  "Section {n} of {total}": "Section {n} sur {total}",
  "Send it": "L'envoyer",
  "Send this report? You cannot change it after it is sent.": "Envoyer ce rapport\u00a0? Vous ne pourrez plus le modifier apr\u00e8s l'envoi.",
  "Sending": "Envoi",
  "Sign": "Signer",
  "Sign with your finger": "Signez avec le doigt",
  "Signed by {name} on {date} at {time}": "Sign\u00e9 par {name} le {date} \u00e0 {time}",
  "Start report": "Commencer le rapport",
  "Which site is this for?": "Pour quel site est ce rapport\u00a0?",
  "Take photo or choose from gallery": "Prendre une photo ou choisir dans la galerie",
  "These still need an answer": "Ces questions attendent encore une r\u00e9ponse",
  "This question cannot be answered here yet. Your supervisor will finish it.": "Cette question ne peut pas encore \u00eatre remplie ici. Votre superviseur la compl\u00e9tera.",
  "This photo could not be read. Try another one.": "Cette photo n'a pas pu \u00eatre lue. Essayez-en une autre.",
  "This question is full.": "Cette question a atteint sa limite.",
  "This report was already sent.": "Ce rapport a d\u00e9j\u00e0 \u00e9t\u00e9 envoy\u00e9.",
  "Thank you. {company} has your form.": "Merci. {company} a bien re\u00e7u votre formulaire.",
  "Thank you. We have your form.": "Merci. Nous avons bien re\u00e7u votre formulaire.",
  "Your name": "Votre nom",
  "Your role": "Votre fonction",
  "This table is full.": "Ce tableau est plein.",
  "Untitled report": "Rapport sans titre",
  "{answered} answered": "R\u00e9ponses\u00a0: {answered}",
  "{answered} answered, {remaining} to go": "R\u00e9ponses\u00a0: {answered}, il en reste {remaining}",
  // Acknowledgement page
  "Acknowledge this report": "Confirmer la r\u00e9ception de ce rapport",
  "Comments": "Commentaires",
  "Not sent yet. Check your signal and tap Acknowledge this report again.": "Pas encore envoy\u00e9. V\u00e9rifiez votre r\u00e9seau et appuyez de nouveau sur \u00ab\u00a0Confirmer la r\u00e9ception de ce rapport\u00a0\u00bb.",
  "Rate this month": "Notez ce mois-ci",
  "Signature": "Signature",
  "Thank you. Your acknowledgement is recorded.": "Merci. Votre confirmation est enregistr\u00e9e.",
  "This report could not load. Check your signal and try again.": "Ce rapport ne s'est pas charg\u00e9. V\u00e9rifiez votre r\u00e9seau et r\u00e9essayez.",
  // Shortcuts
  "Add to bar": "Ajouter \u00e0 la barre",
  "Add to bar {name}": "Ajouter {name} \u00e0 la barre",
  "Always first": "Toujours en premier",
  "Always last": "Toujours en dernier",
  "Everything is on your bar.": "Tout est dans votre barre.",
  "Home": "Accueil",
  "Move down": "Descendre",
  "Move down {name}": "Descendre {name}",
  "Move up": "Monter",
  "Move up {name}": "Monter {name}",
  "On your bar": "Dans votre barre",
  "Pick something for this spot": "Choisissez quelque chose pour cette place",
  "Put the bar back the way it came?": "Remettre la barre comme au d\u00e9part\u00a0?",
  "Remove": "Retirer",
  "Remove {name}": "Retirer {name}",
  "Replace which one?": "Lequel remplacer\u00a0?",
  "Reset": "R\u00e9initialiser",
  "Reset to default": "Revenir \u00e0 l'original",
  "Shortcuts": "Raccourcis",
  "Under More": "Dans Plus",
  "will take the place of the one you pick.": "prendra la place de celui que vous choisissez.",
  "Your bar is full.": "Votre barre est pleine.",
  "Remove {name} from the bar": "Retirer {name} de la barre",
  // Notifications
  "Announcement": "Annonce",
  "From {name}": "Envoy\u00e9 par {name}",
  "Load more": "Afficher plus",
  "Mark all read": "Tout marquer comme lu",
  "Marking...": "Marquage...",
  "Nothing yet.": "Rien pour le moment.",
  "Notifications did not load.": "Les notifications ne se sont pas charg\u00e9es.",
  "Open in the dashboard": "Ouvrir dans le tableau de bord",
  // Settings
  "A": "A",
  "Appearance": "Apparence",
  "Change PIN": "Changer le code PIN",
  "Choose how the app looks.": "Choisissez l'apparence de l'application.",
  "Choose what sits on your bottom bar. Everything else is under More.": "Choisissez ce qui se trouve dans votre barre du bas. Tout le reste est dans Plus.",
  "Confirm New PIN": "Confirmer le nouveau code PIN",
  "Could not update your PIN.": "Impossible de mettre \u00e0 jour votre code PIN.",
  "Current PIN": "Code PIN actuel",
  "Dark": "Sombre",
  "Light": "Clair",
  "Makes everything in the app bigger on this phone.": "Agrandit tout dans l'application sur ce t\u00e9l\u00e9phone.",
  "Makes everything in the app bigger.": "Agrandit tout dans l'application.",
  "New PIN": "Nouveau code PIN",
  "Text size": "Taille du texte",
  "The app, Help and report forms use this language.": "L'application, l'Aide et les formulaires utilisent cette langue.",
  "Update PIN": "Mettre \u00e0 jour le code PIN",
  "Your PIN is 4 digits. Choose one that only you know.": "Votre code PIN a 4 chiffres. Choisissez-en un que personne d'autre ne conna\u00eet.",
  "Enter your current 4-digit PIN.": "Entrez votre code PIN actuel \u00e0 4 chiffres.",
  // Phone alerts
  "Phone alerts": "Alertes sur le t\u00e9l\u00e9phone",
  "Turn on alerts on this phone": "Activer les alertes sur ce t\u00e9l\u00e9phone",
  "Alerts are on for this phone.": "Les alertes sont activ\u00e9es sur ce t\u00e9l\u00e9phone.",
  "Turn off on this phone": "D\u00e9sactiver sur ce t\u00e9l\u00e9phone",
  "This phone blocked alerts for this app. Turn them on in the phone's settings.": "Ce t\u00e9l\u00e9phone a bloqu\u00e9 les alertes de cette application. Activez-les dans les r\u00e9glages du t\u00e9l\u00e9phone.",
  "On an iPhone, add the app to your Home Screen first, then open it from there to turn on alerts.": "Sur un iPhone, ajoutez d'abord l'application \u00e0 votre \u00e9cran d'accueil, puis ouvrez-la depuis cet \u00e9cran pour activer les alertes.",
  "This phone cannot receive alerts.": "Ce t\u00e9l\u00e9phone ne peut pas recevoir d'alertes.",
  "Chat messages": "Messages des discussions",
  "Every message": "Chaque message",
  "Only when I'm tagged": "Seulement quand on me mentionne",
  "Off": "D\u00e9sactiv\u00e9",
  "Schedule and time off": "Planning et cong\u00e9s",
  "Shift pickups and drops": "Services libres et retraits",
  "Supply requests": "Demandes de fournitures",
  "Problems reported": "Probl\u00e8mes signal\u00e9s",
  "Forms filed": "Formulaires envoy\u00e9s",
  "Announcements from the office always come through.": "Les annonces du bureau arrivent toujours.",
  "Turn on": "Activer",
  "Your settings did not save.": "Vos param\u00e8tres n'ont pas \u00e9t\u00e9 enregistr\u00e9s.",
  // Profile
  "Address": "Adresse",
  "Address Line 1": "Adresse, ligne 1",
  "Address Line 2": "Adresse, ligne 2",
  "Apt, suite, etc.": "Appartement, bureau, etc.",
  "Birthday": "Date de naissance",
  "City": "Ville",
  "Emergency Contact": "Contact d'urgence",
  "Emergency Phone": "T\u00e9l\u00e9phone d'urgence",
  "Employee ID": "Matricule",
  "Employee ID not assigned. Ask your supervisor.": "Aucun matricule attribu\u00e9. Demandez \u00e0 votre superviseur.",
  "Full name": "Nom complet",
  "Loading profile...": "Chargement du profil...",
  "No shift": "Aucun service",
  "Not set": "Non renseign\u00e9",
  "Personal Information": "Informations personnelles",
  "Phone number": "Num\u00e9ro de t\u00e9l\u00e9phone",
  "Photo must be under 20MB": "La photo doit faire moins de 20 Mo",
  "Photo updated": "Photo mise \u00e0 jour",
  "Profile updated": "Profil mis \u00e0 jour",
  "Save": "Enregistrer",
  "Site Assignments": "Sites attribu\u00e9s",
  "Staff": "Personnel",
  "State": "\u00c9tat",
  "Street address": "Num\u00e9ro et rue",
  "Your profile did not load.": "Votre profil ne s'est pas charg\u00e9.",
  "Zip": "Code postal",
  // API message
  "A part day needs both a start and an end time, on one day": "Une partie de journ\u00e9e demande une heure de d\u00e9but et une heure de fin, le m\u00eame jour",
  "A photo could not be read. Upload it again and resend it.": "Une photo n'a pas pu \u00eatre lue. Ajoutez-la de nouveau et renvoyez-la.",
  "A photo is larger than 5 MB. Send a smaller photo.": "Une photo fait plus de 5 Mo. Envoyez une photo plus petite.",
  "A photo must be a JPEG, PNG or WebP image.": "Une photo doit \u00eatre une image JPEG, PNG ou WebP.",
  "A sign-off is made with its own button": "Une signature se fait avec son propre bouton",
  "Choose a type of time off": "Choisissez un type de cong\u00e9",
  "Dates must be YYYY-MM-DD": "Les dates doivent \u00eatre au format YYYY-MM-DD",
  "Draft not found": "Rapport introuvable",
  "Enter a real date": "Entrez une date valide",
  "Enter a real time": "Entrez une heure valide",
  "Form not found": "Formulaire introuvable",
  "Hours must be a number from 0 to 999.99": "Les heures doivent \u00eatre un nombre de 0 \u00e0 999,99",
  "Keep the reason to 1000 characters or fewer.": "\u00c9crivez la raison en 1000 caract\u00e8res au maximum.",
  "language must be en or es": "la langue doit \u00eatre en ou es",
  "One of the people picked is not on the staff list": "Une des personnes choisies n'est pas dans la liste du personnel",
  "Photo upload failed": "L'envoi de la photo a \u00e9chou\u00e9",
  "Pick at least one person this is about": "Choisissez au moins une personne concern\u00e9e",
  "Pick up to 10 people": "Choisissez jusqu'\u00e0 10 personnes",
  "Request failed": "La demande a \u00e9chou\u00e9",
  "Request not found": "Demande introuvable",
  "Required fields are unanswered": "Des champs obligatoires sont sans r\u00e9ponse",
  "Say whether this is about someone in management": "Indiquez si cela concerne une personne de la direction",
  "Send answers as an object of key and value": "Envoyez les r\u00e9ponses sous forme d'objet cl\u00e9 et valeur",
  "Send at most 3 photos with one message.": "Envoyez au maximum 3 photos par message.",
  "Server error": "Erreur du serveur",
  "Session expired": "Votre session a expir\u00e9",
  "shortcuts must be four different tab ids": "les raccourcis doivent \u00eatre quatre onglets diff\u00e9rents",
  "Some answers are not valid": "Certaines r\u00e9ponses ne sont pas valides",
  "textSize must be standard, large, xlarge or largest": "la taille du texte doit \u00eatre standard, large, xlarge ou largest",
  "That column is not on this form": "Cette colonne n'est pas dans ce formulaire",
  "That is not a sign-off on this form": "Ce n'est pas une signature de ce formulaire",
  "That photo was not uploaded by you. Upload the photo again and resend it.": "Cette photo n'a pas \u00e9t\u00e9 ajout\u00e9e par vous. Ajoutez-la de nouveau et renvoyez-la.",
  "That row is not on this form": "Cette ligne n'est pas dans ce formulaire",
  "That table has no room for another row": "Ce tableau n'a plus de place pour une autre ligne",
  "The last day cannot be before the first day": "Le dernier jour ne peut pas \u00eatre avant le premier jour",
  "theme must be light or dark": "le th\u00e8me doit \u00eatre light ou dark",
  "These fields cannot be answered here": "Ces champs ne peuvent pas \u00eatre remplis ici",
  "This form is not available to you": "Ce formulaire n'est pas disponible pour vous",
  "This part is already signed": "Cette partie est d\u00e9j\u00e0 sign\u00e9e",
  "This report is already submitted": "Ce rapport est d\u00e9j\u00e0 envoy\u00e9",
  "This report was already submitted": "Ce rapport avait d\u00e9j\u00e0 \u00e9t\u00e9 envoy\u00e9",
  "This report was voided": "Ce rapport a \u00e9t\u00e9 annul\u00e9",
  "This request was already approved": "Cette demande \u00e9tait d\u00e9j\u00e0 approuv\u00e9e",
  "This request was already cancelled": "Cette demande \u00e9tait d\u00e9j\u00e0 annul\u00e9e",
  "This request was already denied": "Cette demande \u00e9tait d\u00e9j\u00e0 refus\u00e9e",
  "Time off can start at most 30 days ago": "Un cong\u00e9 peut commencer au plus t\u00f4t il y a 30 jours",
  "Time off can start at most one year ahead": "Un cong\u00e9 peut commencer au plus tard dans un an",
  "Times must be HH:MM, from 00:00 to 23:59": "Les heures doivent \u00eatre au format HH:MM, de 00:00 \u00e0 23:59",
  "Upload failed": "L'envoi a \u00e9chou\u00e9",
  "User not found": "Utilisateur introuvable",
  "You already have time off requested or approved for those days": "Vous avez d\u00e9j\u00e0 un cong\u00e9 demand\u00e9 ou approuv\u00e9 pour ces jours",
  "You cannot pick yourself": "Vous ne pouvez pas vous choisir vous-m\u00eame",
  "You cannot sign this part of the form": "Vous ne pouvez pas signer cette partie du formulaire",
  // Bar and More
  "Chat": "Messages",
  "Help": "Aide",
  "Inspect": "Inspection",
  // notifAgo
  "now": "\u00e0 l'instant",
  // Bar and More
  "Schedule": "Planning",
  "Speak Up": "Confiance",
  "Tasks": "T\u00e2ches",
  // notifAgo
  "{n}d": "{n} j",
  "{n}h": "{n} h",
  "{n}m": "{n} min",
  // Safety data sheets
  "Safety data sheets": "Fiches de donn\u00e9es de s\u00e9curit\u00e9",
  "Search by product": "Rechercher par produit",
  "Revised {date}": "R\u00e9vis\u00e9e le {date}",
  "No sheet matches that product.": "Aucune fiche ne correspond \u00e0 ce produit.",
  "Every sheet is also in the printed binder at the site.": "Chaque fiche se trouve aussi dans le classeur imprim\u00e9 du site.",
  "Saved on this phone. Last read {when}.": "Enregistr\u00e9e sur ce t\u00e9l\u00e9phone. Derni\u00e8re lecture\u00a0: {when}.",
  "All sheets": "Toutes les fiches",
  "This sheet is in English, as the manufacturer wrote it. Help in the app can explain any part of it in your language.": "Cette fiche est en anglais, telle que le fabricant l'a r\u00e9dig\u00e9e. L'Aide de l'application peut vous en expliquer chaque partie dans votre langue.",
  "Safety data sheets are not available here yet. The printed binder at the site holds every sheet.": "Les fiches de donn\u00e9es de s\u00e9curit\u00e9 ne sont pas encore disponibles ici. Le classeur imprim\u00e9 du site contient toutes les fiches.",
  "This sheet did not open. Check your signal, or use the printed binder at the site.": "Cette fiche ne s'est pas ouverte. V\u00e9rifiez votre r\u00e9seau ou utilisez le classeur imprim\u00e9 du site.",
  // Workspace
  "Workspace": "Espace de travail",
  "My assignments": "Mes affectations",
  "Projects": "Projets",
  "Nothing is assigned to you right now.": "Rien ne vous est assign\u00e9 pour le moment.",
  "You are not in any project yet.": "Vous ne faites encore partie d'aucun projet.",
  "Mark this to-do done?": "Marquer cette t\u00e2che comme faite\u00a0?",
  "Marked done.": "Marqu\u00e9e comme faite.",
  "That to-do was not marked done. Try again.": "Cette t\u00e2che n'a pas \u00e9t\u00e9 marqu\u00e9e comme faite. R\u00e9essayez.",
  "Due {date}": "\u00c9ch\u00e9ance\u00a0: {date}",
  "Overdue. Due {date}": "En retard. \u00c9ch\u00e9ance\u00a0: {date}",
  "Message Board": "Tableau de messages",
  "To-dos": "\u00c0 faire",
  "Files": "Fichiers",
  "This project did not open. Try again.": "Ce projet ne s'est pas ouvert. R\u00e9essayez.",
  "This project's chat is not on your chat list yet. Try again in a minute.": "La discussion de ce projet n'est pas encore dans votre liste. R\u00e9essayez dans une minute.",
  "Post a message": "Publier un message",
  "No messages on the board yet.": "Aucun message sur le tableau pour le moment.",
  "Pinned": "\u00c9pingl\u00e9",
  "1 comment": "1 commentaire",
  "{n} comments": "{n} commentaires",
  "Message": "Message",
  "Post": "Publier",
  "Posting...": "Publication...",
  "Give the message a title.": "Donnez un titre au message.",
  "Write the message first.": "\u00c9crivez d'abord le message.",
  "Message posted.": "Message publi\u00e9.",
  "Your message was not posted. Try again.": "Votre message n'a pas \u00e9t\u00e9 publi\u00e9. R\u00e9essayez.",
  "All messages": "Tous les messages",
  "This message did not open. Try again.": "Ce message ne s'est pas ouvert. R\u00e9essayez.",
  "No comments yet.": "Aucun commentaire pour le moment.",
  "Add a comment": "Ajouter un commentaire",
  "Your comment was not added. Try again.": "Votre commentaire n'a pas \u00e9t\u00e9 ajout\u00e9. R\u00e9essayez.",
  "This project has no to-do lists yet.": "Ce projet n'a pas encore de liste de t\u00e2ches.",
  "Nothing on this list yet.": "Rien sur cette liste pour le moment.",
  "Add a to-do": "Ajouter une t\u00e2che",
  "Assigned to": "Assign\u00e9e \u00e0",
  "Nobody yet": "Personne pour l'instant",
  "Due date": "\u00c9ch\u00e9ance",
  "Give the to-do a title.": "Donnez un titre \u00e0 la t\u00e2che.",
  "To-do added.": "T\u00e2che ajout\u00e9e.",
  "That to-do was not added. Try again.": "Cette t\u00e2che n'a pas \u00e9t\u00e9 ajout\u00e9e. R\u00e9essayez.",
  "Opened again.": "Rouverte.",
  "That to-do was not opened again. Try again.": "Cette t\u00e2che n'a pas \u00e9t\u00e9 rouverte. R\u00e9essayez.",
  "No files in this project yet.": "Aucun fichier dans ce projet pour le moment.",
  "Open file": "Ouvrir le fichier",
  "Opening...": "Ouverture...",
  "That file did not open. Try again.": "Ce fichier ne s'est pas ouvert. R\u00e9essayez.",
  "{n} KB": "{n} Ko",
  "{n} MB": "{n} Mo",
  // Workspace, part two
  "All to-dos": "Toutes les t\u00e2ches",
  "Not done yet": "Pas encore faite",
  "No due date": "Pas d'\u00e9ch\u00e9ance",
  "This to-do did not open. Try again.": "Cette t\u00e2che ne s'est pas ouverte. R\u00e9essayez.",
  "New list": "Nouvelle liste",
  "Give the list a name.": "Donnez un nom \u00e0 la liste.",
  "List added.": "Liste ajout\u00e9e.",
  "That list was not added. Try again.": "Cette liste n'a pas \u00e9t\u00e9 ajout\u00e9e. R\u00e9essayez.",
  "Add a file": "Ajouter un fichier",
  "Choose a document": "Choisir un document",
  "Documents, images and PDFs, up to 25 MB each.": "Documents, images et PDF, jusqu'\u00e0 25 Mo chacun.",
  "Note (optional)": "Note (facultative)",
  "Add file": "Ajouter le fichier",
  "Choose a file first.": "Choisissez d'abord un fichier.",
  "{0} is over 25 MB. Files up to 25 MB can be added.": "{0} d\u00e9passe 25 Mo. Les fichiers peuvent faire jusqu'\u00e0 25 Mo.",
  "File added.": "Fichier ajout\u00e9.",
  "That file was not added. Try again.": "Ce fichier n'a pas \u00e9t\u00e9 ajout\u00e9. R\u00e9essayez.",
  "Saved on this phone. It sends when you have signal.": "Enregistr\u00e9 sur ce t\u00e9l\u00e9phone. Il part d\u00e8s que vous avez du r\u00e9seau.",
  "Weekly": "Hebdomadaire",
  "Monthly": "Mensuel",
  "Quarterly": "Trimestriel",
  "Seasonal": "Saisonnier",
  "Equipment": "\u00c9quipement",
  "In service": "En service",
  "Out of service": "Hors service",
  "Retired": "Retir\u00e9",
  "Next service": "Prochain entretien",
  "None set": "Aucune date",
  "Recent": "R\u00e9cent",
  "Nothing recorded yet.": "Rien n'est encore enregistr\u00e9.",
  "Checked, all good": "V\u00e9rifi\u00e9, tout va bien",
  "Serviced": "Entretien fait",
  "Repaired": "R\u00e9par\u00e9",
  "Tagged out": "Mis hors service",
  "Back in service": "Remis en service",
  "Moved": "D\u00e9plac\u00e9",
  "Tag out": "Mettre hors service",
  "What is wrong": "Ce qui ne va pas",
  "Record that this item was checked and is in good order?": "Enregistrer que cet \u00e9quipement a \u00e9t\u00e9 v\u00e9rifi\u00e9 et est en bon \u00e9tat\u00a0?",
  "Check recorded.": "V\u00e9rification enregistr\u00e9e.",
  "Say what is wrong before you tag it out.": "Dites ce qui ne va pas avant de le mettre hors service.",
  "Tag this item out of service? The office is told.": "Mettre cet \u00e9quipement hors service\u00a0? Le bureau est pr\u00e9venu.",
  "Tagged out. The office has been told.": "Mis hors service. Le bureau a \u00e9t\u00e9 pr\u00e9venu.",
  "The photo did not upload. Try again, or tag it out without it.": "La photo n'a pas \u00e9t\u00e9 envoy\u00e9e. R\u00e9essayez, ou mettez-le hors service sans elle.",
  "That was not recorded. Try again.": "Rien n'a \u00e9t\u00e9 enregistr\u00e9. R\u00e9essayez.",
  "This item did not open. Try again.": "Cet \u00e9quipement ne s'est pas ouvert. R\u00e9essayez.",
  "This label does not match any equipment.": "Cette \u00e9tiquette ne correspond \u00e0 aucun \u00e9quipement.",
  "This item is retired. Nothing can be recorded on it.": "Cet \u00e9quipement est retir\u00e9. Rien ne peut y \u00eatre enregistr\u00e9.",
  "This item is tagged out. Do not use it until the office puts it back in service.": "Cet \u00e9quipement est hors service. Ne l'utilisez pas avant que le bureau le remette en service.",
  "Thank you. Your reference is {ref}.": "Merci. Votre r\u00e9f\u00e9rence est {ref}.",
  "We will reply within five working days.": "Nous vous r\u00e9pondrons sous cinq jours ouvr\u00e9s.",
  "A copy is on its way to your email.": "Une copie vous est envoy\u00e9e par e-mail.",
  "Sent by {who}.": "Envoy\u00e9 par {who}.",
  "Field kit": "Kit de terrain",
  "You have no sites to work on.": "Vous n'avez aucun site sur lequel travailler.",
  "Issue PPE": "Remettre un EPI",
  "Periodic work": "Travail p\u00e9riodique",
  "Awaiting review": "\u00c0 v\u00e9rifier",
  "Hand out protective gear. The person signs for it here.": "Remettez un \u00e9quipement de protection. La personne signe ici pour le recevoir.",
  "What is overdue, due and done at this site.": "Ce qui est en retard, \u00e0 faire et fait sur ce site.",
  "Each item's status and next service.": "L'\u00e9tat et le prochain entretien de chaque \u00e9quipement.",
  "Inspections waiting for a review signature.": "Inspections en attente d'une signature de v\u00e9rification.",
  "Person": "Personne",
  "Choose a person": "Choisissez une personne",
  "No one is assigned to this site.": "Personne n'est affect\u00e9 \u00e0 ce site.",
  "Choose an item": "Choisissez un article",
  "{item}, {n} in stock": "{item}, {n} en stock",
  "Something else (type it)": "Autre chose (\u00e0 saisir)",
  "What you are handing out": "Ce que vous remettez",
  "This site has no PPE in stock. Type what you are handing out.": "Ce site n'a pas d'EPI en stock. Saisissez ce que vous remettez.",
  "Size": "Taille",
  "Quantity": "Quantit\u00e9",
  "Fits well?": "Bien ajust\u00e9\u00a0?",
  "Signature of the person receiving it": "Signature de la personne qui le re\u00e7oit",
  "Hand the phone to the person. They sign with a finger.": "Tendez le t\u00e9l\u00e9phone \u00e0 la personne. Elle signe avec le doigt.",
  "Choose the person you are handing it to.": "Choisissez la personne \u00e0 qui vous le remettez.",
  "Choose an item, or type what you are handing out.": "Choisissez un article ou saisissez ce que vous remettez.",
  "The person signs before you send.": "La personne signe avant l'envoi.",
  "PPE issued. The signature is kept with it.": "EPI remis. La signature est conserv\u00e9e avec la remise.",
  "This form did not load.": "Ce formulaire ne s'est pas charg\u00e9.",
  "This was not sent. Try again.": "Rien n'a \u00e9t\u00e9 envoy\u00e9. R\u00e9essayez.",
  "Issued at this site": "Remis sur ce site",
  "No PPE has been issued at this site yet.": "Aucun EPI n'a encore \u00e9t\u00e9 remis sur ce site.",
  "{item} x{n}": "{item} x{n}",
  "Size {size}": "Taille {size}",
  "Fits well": "Bien ajust\u00e9",
  "Does not fit": "Mal ajust\u00e9",
  "Signed": "Sign\u00e9",
  "Issued by {name}, {when}": "Remis par {name}, {when}",
  "Show more": "Afficher plus",
  "This site has no periodic work.": "Ce site n'a aucun travail p\u00e9riodique.",
  "Read only. Work is checked off on the checklist.": "Lecture seule. Le travail se coche sur la liste.",
  "Overdue": "En retard",
  "Due": "\u00c0 faire",
  "On the checklist under {section}": "Sur la liste, dans {section}",
  "Last done {date} by {name}": "Fait pour la derni\u00e8re fois le {date} par {name}",
  "Last done {date}": "Fait pour la derni\u00e8re fois le {date}",
  "Was due by {date}": "\u00c9tait \u00e0 faire au plus tard le {date}",
  "Due by {date}": "\u00c0 faire au plus tard le {date}",
  "Next due by {date}": "La prochaine fois, au plus tard le {date}",
  "This site has no equipment on the register.": "Ce site n'a aucun \u00e9quipement au registre.",
  "Service due {date}": "Entretien \u00e0 faire depuis le {date}",
  "Next service {date}": "Prochain entretien le {date}",
  "No service date": "Aucune date d'entretien",
  "Last: {what}, {when}": "Dernier\u00a0: {what}, {when}",
  "No inspection at this site is waiting for review.": "Aucune inspection de ce site n'attend de v\u00e9rification.",
  "Inspection": "Inspection",
  "{score} of {max}, {pct}%": "{score} sur {max}, {pct}\u00a0%",
  "This inspection did not open. Try again.": "Cette inspection ne s'est pas ouverte. R\u00e9essayez.",
  "Read only. Only the review lines can be signed here.": "Lecture seule. Seules les lignes de v\u00e9rification peuvent \u00eatre sign\u00e9es ici.",
  "Scores": "Notes",
  "{score} of {max}": "{score} sur {max}",
  "Not scored": "Non not\u00e9",
  "Signatures": "Signatures",
  "Inspector": "Inspecteur",
  "Signed by {name}, {when}": "Sign\u00e9 par {name}, {when}",
  "Waiting for the person who signs this line.": "En attente de la personne qui signe cette ligne.",
  "Sign this line": "Signer cette ligne",
  "Photo {n} of {count}": "Photo {n} sur {count}",
  "Signed.": "Sign\u00e9.",
  "This was not signed. Try again.": "Rien n'a \u00e9t\u00e9 sign\u00e9. R\u00e9essayez.",
  "Touchpoint": "Point de contact",
  "Critical touchpoint": "Point de contact critique",
  // The request page, Client requests and the supply page (Step 252)
  "Where in the building?": "O\u00f9 dans le b\u00e2timent\u00a0?",
  "Say where in the building.": "Indiquez o\u00f9 dans le b\u00e2timent.",
  "Pick one of these first.": "Choisissez d'abord l'une de ces options.",
  "Anything we should know?": "Quelque chose \u00e0 nous signaler\u00a0?",
  "Add photos": "Ajouter des photos",
  "Email me updates (optional)": "Recevoir des nouvelles par e-mail (facultatif)",
  "Report a concern instead": "Signaler plut\u00f4t un probl\u00e8me",
  "Call the office": "Appeler le bureau",
  "This page could not load. Check your signal and try again.": "Cette page n'a pas pu se charger. V\u00e9rifiez votre signal et r\u00e9essayez.",
  "Your request was not sent. Check your signal and tap Try again.": "Votre demande n'a pas \u00e9t\u00e9 envoy\u00e9e. V\u00e9rifiez votre signal et touchez R\u00e9essayer.",
  "Client requests": "Demandes des clients",
  "Waiting for approval": "En attente d'approbation",
  "Yours": "Les v\u00f4tres",
  "{ago} ago": "il y a {ago}",
  "Respond by {when}": "R\u00e9pondre avant {when}",
  "Due {when}": "\u00c9ch\u00e9ance\u00a0: {when}",
  "Due soon": "Bient\u00f4t \u00e0 \u00e9ch\u00e9ance",
  "Approve and assign": "Approuver et attribuer",
  "Assign": "Attribuer",
  "Me": "Moi",
  "On shift": "En service",
  "No one can be assigned at this site.": "Personne ne peut \u00eatre attribu\u00e9 sur ce site.",
  "Approved and assigned.": "Approuv\u00e9e et attribu\u00e9e.",
  "{name} already took care of this.": "{name} s'en est d\u00e9j\u00e0 occup\u00e9.",
  "Someone": "Quelqu'un",
  "Decline": "Refuser",
  "Decline this request": "Refuser cette demande",
  "This reason is emailed to the person who asked, if they left an email.": "Ce motif est envoy\u00e9 par e-mail \u00e0 la personne qui a demand\u00e9, si elle a laiss\u00e9 un e-mail.",
  "Declined.": "Refus\u00e9e.",
  "I'm on it": "Je m'en occupe",
  "Started.": "Commenc\u00e9e.",
  "Mark this request done": "Marquer cette demande comme termin\u00e9e",
  "Done. The person who asked is told if they left an email.": "Termin\u00e9. La personne qui a demand\u00e9 est pr\u00e9venue si elle a laiss\u00e9 un e-mail.",
  "Can't finish": "Impossible de terminer",
  "Can't finish this request": "Impossible de terminer cette demande",
  "Say what is in the way. Your supervisors are told.": "Dites ce qui bloque. Vos superviseurs sont pr\u00e9venus.",
  "Your supervisors have been told.": "Vos superviseurs ont \u00e9t\u00e9 pr\u00e9venus.",
  "Write a note first.": "\u00c9crivez d'abord une note.",
  "That did not go through. Try again.": "Cela n'a pas abouti. R\u00e9essayez.",
  "The photo did not upload. Try again, or send without it.": "La photo n'a pas \u00e9t\u00e9 envoy\u00e9e. R\u00e9essayez, ou envoyez sans elle.",
  "1 client request needs you": "1 demande client vous attend",
  "{n} client requests need you": "{n} demandes clients vous attendent",
  "Safety sheet": "Fiche de s\u00e9curit\u00e9",
  "No safety sheet on file. Ask your supervisor.": "Aucune fiche de s\u00e9curit\u00e9 enregistr\u00e9e. Demandez \u00e0 votre superviseur.",
  "Sign in to record use": "Se connecter pour enregistrer l'utilisation",
  "Record use": "Enregistrer l'utilisation",
  "None of your sites stocks this supply.": "Aucun de vos sites ne stocke cette fourniture.",
  "Used one": "J'en ai utilis\u00e9 un",
  "Used {n}": "J'en ai utilis\u00e9 {n}",
  "Recorded.": "Enregistr\u00e9.",
  "Running low": "Stock bas",
  "This did not load. Try again.": "Le chargement a \u00e9chou\u00e9. R\u00e9essayez.",
  "That was not sent. Try again.": "Rien n'a \u00e9t\u00e9 envoy\u00e9. R\u00e9essayez.",
  // Inspection findings (Step 255)
  "This will open a finding.": "Ceci ouvrira un constat.",
  "Owner": "Responsable",
  "No owner yet": "Pas encore de responsable",
  "Meets the standard.": "Atteint la norme.",
  "Below standard. Corrected within three working days.": "Sous la norme. Correction sous trois jours ouvrables.",
  "Failed. A corrective action and a re-inspection within ten working days.": "Non conforme. Mesure corrective et nouvelle inspection sous dix jours ouvrables.",
  "Serious. The officers are told the same day.": "Grave. Les dirigeants sont pr\u00e9venus le jour m\u00eame.",
  "Inspection sent. 1 finding opened.": "Inspection envoy\u00e9e. 1 constat ouvert.",
  "Inspection sent. {n} findings opened.": "Inspection envoy\u00e9e. {n} constats ouverts.",
  "A corrective action is required. The office has been told.": "Une mesure corrective est requise. Le bureau a \u00e9t\u00e9 pr\u00e9venu.",
  "Owner: {name}": "Responsable\u00a0: {name}",
  "Inspection findings": "Constats d'inspection",
  "Fixed": "R\u00e9par\u00e9",
  "Mark this finding fixed": "Marquer ce constat comme r\u00e9par\u00e9",
  "A second person checks the fix before the finding closes.": "Une autre personne v\u00e9rifie la r\u00e9paration avant la cl\u00f4ture du constat.",
  "Fixed. A second person checks it.": "R\u00e9par\u00e9. Une autre personne le v\u00e9rifie.",
  "Waiting for a check": "En attente de v\u00e9rification",
  // My training (Step 258)
  "My training": "Ma formation",
  "To do": "\u00c0 faire",
  "Coming due": "Bient\u00f4t \u00e0 renouveler",
  "History": "Historique",
  "Expired {date}": "Expir\u00e9e le {date}",
  "Refresher due at {site}": "Rappel \u00e0 faire \u00e0 {site}",
  "Refresher due": "Rappel \u00e0 faire",
  "Expires {date}": "Expire le {date}",
  "Done {date}": "Faite le {date}",
  "Nothing is required for your role yet.": "Rien n'est encore requis pour votre poste.",
  "Given in English": "Donn\u00e9e en anglais",
  "Given in Spanish": "Donn\u00e9e en espagnol",
  "Given in French": "Donn\u00e9e en fran\u00e7ais",
  "Take the course online": "Suivre le cours en ligne",
  "When you finish, give your certificate to the office.": "Quand vous avez termin\u00e9, remettez votre certificat au bureau.",
  // Online lessons and their sign-off (Step 261).
  "In progress": "En cours",
  "Waiting for your trainer": "En attente de votre formateur",
  "Waiting for your trainer. Show them you can do it, and they sign it off.": "En attente de votre formateur. Montrez-lui que vous savez le faire et il signera.",
  "Start the lesson": "Commencer la le\u00e7on",
  "Continue the lesson": "Continuer la le\u00e7on",
  "Ask your supervisor for an in-person session.": "Demandez une s\u00e9ance en personne \u00e0 votre superviseur.",
  "This lesson did not open. Try again.": "Cette le\u00e7on ne s'est pas ouverte. R\u00e9essayez.",
  "Your answers were not sent. Try again.": "Vos r\u00e9ponses n'ont pas \u00e9t\u00e9 envoy\u00e9es. R\u00e9essayez.",
  "Question {n} of {count}": "Question {n} sur {count}",
  "Your score: {score}%": "Votre score\u00a0: {score}\u00a0%",
  "Pass mark: {pass}%": "Seuil de r\u00e9ussite\u00a0: {pass}\u00a0%",
  "Not passed.": "Non r\u00e9ussi.",
  "You missed 1 question.": "Vous avez manqu\u00e9 1 question.",
  "You missed {n} questions.": "Vous avez manqu\u00e9 {n} questions.",
  "Read it again": "La relire",
  "You passed.": "Vous avez r\u00e9ussi.",
  "Your signature": "Votre signature",
  "Done. It is on your record.": "Termin\u00e9. C'est dans votre dossier.",
  "Back to My training": "Retour \u00e0 Ma formation",
  "Sign off training": "Valider des formations",
  "Lessons passed and signed, waiting for your sign-off after a demonstration.": "Le\u00e7ons r\u00e9ussies et sign\u00e9es, en attente de votre validation apr\u00e8s une d\u00e9monstration.",
  "Nobody is waiting for a sign-off at this site.": "Personne n'attend de validation sur ce site.",
  "Score {score}%, signed {when}": "Score {score}\u00a0%, sign\u00e9 {when}",
  "Signed {when}": "Sign\u00e9 {when}",
  "Sign off": "Valider",
  "I watched them do it": "Je l'ai vu le faire",
  "Tick I watched them do it first.": "Cochez d'abord Je l'ai vu le faire.",
  "Signed off. It is on their record.": "Valid\u00e9. C'est dans son dossier.",
  "1 training to do": "1 formation \u00e0 faire",
  "{n} trainings to do": "{n} formations \u00e0 faire",
  "1 training to sign off": "1 formation \u00e0 valider",
  "{n} trainings to sign off": "{n} formations \u00e0 valider",
  // Sessions, watched sign-offs, Your first trainings and documents to sign (Step 264).
  "Join a session": "Rejoindre une s\u00e9ance",
  "Session code": "Code de la s\u00e9ance",
  "The code on your trainer's screen": "Le code affich\u00e9 sur l'\u00e9cran de votre formateur",
  "Join": "Rejoindre",
  "Training session": "S\u00e9ance de formation",
  "Trainer: {name}": "Formateur\u00a0: {name}",
  "Topics": "Sujets",
  "This session is not open.": "Cette s\u00e9ance n'est pas ouverte.",
  "Ask your trainer for the code on their screen.": "Demandez \u00e0 votre formateur le code affich\u00e9 sur son \u00e9cran.",
  "I understood this training": "J'ai compris cette formation",
  "Tick I understood this training first.": "Cochez d'abord J'ai compris cette formation.",
  "Sign in to this session": "Signer ma pr\u00e9sence \u00e0 cette s\u00e9ance",
  "You are signed in. Your trainer closes the session.": "Vous \u00eates inscrit. Votre formateur cl\u00f4ture la s\u00e9ance.",
  "You already signed in to this session.": "Vous avez d\u00e9j\u00e0 sign\u00e9 votre pr\u00e9sence \u00e0 cette s\u00e9ance.",
  "Version {n}": "Version {n}",
  "You signed version {n}. This one is new.": "Vous avez sign\u00e9 la version {n}. Celle-ci est nouvelle.",
  "Read and sign": "Lire et signer",
  "Certificate on file": "Certificat au dossier",
  "This document changed while you read it. Read it again.": "Ce document a chang\u00e9 pendant votre lecture. Relisez-le.",
  "This document is shown in English.": "Ce document est affich\u00e9 en anglais.",
  "Signed. It is on your record.": "Sign\u00e9. C'est dans votre dossier.",
  "Contents": "Sommaire",
  "Section {n} of {count}": "Section {n} sur {count}",
  "Documents to sign": "Documents \u00e0 signer",
  "Your first trainings": "Vos premi\u00e8res formations",
  "Type a title.": "Saisissez un titre.",
  "Choose at least one topic.": "Choisissez au moins un sujet.",
  "Choose 1 to 10 topics.": "Choisissez de 1 \u00e0 10 sujets.",
  "The training list did not load. Try again.": "La liste des formations ne s'est pas charg\u00e9e. R\u00e9essayez.",
  "Start the session": "D\u00e9marrer la s\u00e9ance",
  "Code": "Code",
  "Scan the QR, or type the code under My training, Join a session.": "Scannez le QR ou saisissez le code dans Ma formation, Rejoindre une s\u00e9ance.",
  "Signed in so far": "Pr\u00e9sences sign\u00e9es",
  "Nobody has signed in yet.": "Personne n'a encore sign\u00e9.",
  "Remove {name} from this session?": "Retirer {name} de cette s\u00e9ance\u00a0?",
  "Close the session": "Cl\u00f4turer la s\u00e9ance",
  "Your signature closes the session and writes a record for each person signed in, on each topic.": "Votre signature cl\u00f4ture la s\u00e9ance et enregistre une formation pour chaque personne inscrite, sur chaque sujet.",
  "Cancel the session": "Annuler la s\u00e9ance",
  "Cancel this session? Nothing is written for it.": "Annuler cette s\u00e9ance\u00a0? Rien n'est enregistr\u00e9.",
  "Session cancelled. Nothing was written.": "S\u00e9ance annul\u00e9e. Rien n'a \u00e9t\u00e9 enregistr\u00e9.",
  "Closed. 1 record saved.": "Cl\u00f4tur\u00e9e. 1 enregistrement sauvegard\u00e9.",
  "Closed. {n} records saved.": "Cl\u00f4tur\u00e9e. {n} enregistrements sauvegard\u00e9s.",
  "Already had it today: {names}": "L'avaient d\u00e9j\u00e0 aujourd'hui\u00a0: {names}",
  "{n} already had it today.": "{n} l'avaient d\u00e9j\u00e0 aujourd'hui.",
  "Start another session": "D\u00e9marrer une autre s\u00e9ance",
  "Start a session on this site, show its code, and close it with your signature.": "D\u00e9marrez une s\u00e9ance sur ce site, affichez son code et cl\u00f4turez-la avec votre signature.",
  "Watch and sign off": "Observer et valider",
  "Watch a person do a task, tick each step, and sign it off with them.": "Observez une personne faire une t\u00e2che, cochez chaque \u00e9tape et validez-la avec elle.",
  "Checklist": "Liste d'\u00e9tapes",
  "No checklist is waiting for {name}.": "Aucune liste d'\u00e9tapes n'attend {name}.",
  "Start the checklist": "Commencer la liste",
  "Steps": "\u00c9tapes",
  "Hand the phone to {name} to sign": "Passez le t\u00e9l\u00e9phone \u00e0 {name} pour signer",
  "Not ticked yet: {steps}": "Pas encore coch\u00e9\u00a0: {steps}",
  "Signature of {name}": "Signature de {name}",
  "Now you sign.": "\u00c0 vous de signer.",
  "Watch another": "Observer une autre",
  // The training portal (Step 267).
  "{done} of {required} trainings done": "{done} formations faites sur {required}",
  "Continue where you left off": "Reprendre o\u00f9 vous en \u00e9tiez",
  "{done} of {required} done": "{done} faites sur {required}",
  "Up next: {name}": "\u00c0 suivre\u00a0: {name}",
  "Needs an in-person session": "N\u00e9cessite une s\u00e9ance en personne",
  "Expires soon": "Expire bient\u00f4t",
  "Your trainer signs this off in the {name} checklist.": "Votre formateur le valide dans la liste {name}.",
  "Your trainer goes over this one with you.": "Votre formateur voit celle-ci avec vous.",
  // Sign on your own phone, and My company property (Step 271).
  "1 to sign": "1 \u00e0 signer",
  "{n} to sign": "{n} \u00e0 signer",
  "To sign": "\u00c0 signer",
  "Nothing is waiting for your signature.": "Rien n'attend votre signature.",
  "Waiting for your signature": "En attente de votre signature",
  "Not right": "Pas correct",
  "Open the warning": "Ouvrir l'avertissement",
  "This is not right": "Ce n'est pas correct",
  "Tell the office what is not right. They will follow up with you.": "Dites au bureau ce qui n'est pas correct. Il fera le suivi avec vous.",
  "Send to the office": "Envoyer au bureau",
  "Sent back to the office. They will follow up.": "Renvoy\u00e9 au bureau. Il fera le suivi.",
  "I will not sign": "Je ne signerai pas",
  "You can say why. The office is told either way, and the warning stays on file.": "Vous pouvez dire pourquoi. Le bureau est inform\u00e9 dans tous les cas, et l'avertissement reste au dossier.",
  "Yes, I will not sign": "Oui, je ne signerai pas",
  "Your answer was sent to the office.": "Votre r\u00e9ponse a \u00e9t\u00e9 envoy\u00e9e au bureau.",
  "This is already done.": "C'est d\u00e9j\u00e0 fait.",
  "This request did not open. Try again.": "Cette demande ne s'est pas ouverte. R\u00e9essayez.",
  "Your note was not sent. Try again.": "Votre note n'a pas \u00e9t\u00e9 envoy\u00e9e. R\u00e9essayez.",
  "My company property": "Mes biens de l'entreprise",
  "You hold": "Ce que vous avez",
  "Returned": "Rendu",
  "Returned {date}": "Rendu le {date}",
  "Issued {date}": "Remis le {date}",
  "You hold no company property.": "Vous n'avez aucun bien de l'entreprise.",
  "Key": "Cl\u00e9",
  "Badge": "Badge",
  "Fob": "Badge de proximit\u00e9",
  "Uniform shirt": "Chemise d'uniforme",
  "Other uniform": "Autre uniforme",
  "The warning did not open. Try again.": "L'avertissement ne s'est pas ouvert. R\u00e9essayez.",
  "Written warning": "Avertissement \u00e9crit",
  "Final written warning": "Avertissement final \u00e9crit",
  // Step 281: a supply request with many items
  "Fulfilled": "Livr\u00e9e",
  "My requests": "Mes demandes",
  "Quantity: {n}": "Quantit\u00e9\u00a0: {n}",
  "Approved {n} of {m}": "Approuv\u00e9\u00a0: {n} sur {m}",
  "Showing your latest 10 requests.": "Vos 10 demandes les plus r\u00e9centes sont affich\u00e9es.",
  "Item {n}": "Article {n}",
  "Remove item {n}": "Retirer l'article {n}",
  "Add item": "Ajouter un article",
  "Choose a supply": "Choisissez un article",
  "Enter a quantity from 1 to 999.": "Entrez une quantit\u00e9 de 1 \u00e0 999.",
  "A request holds up to 30 items.": "Une demande compte au plus 30 articles.",
  // Step 285: sign-in made simple and closed
  "Choose a PIN that is not repeated digits, a sequence, or your badge number": "Choisissez un code PIN qui ne soit pas un chiffre r\u00e9p\u00e9t\u00e9, une suite ou votre num\u00e9ro de badge",
  "New PIN must be different from your current PIN": "Le nouveau code PIN doit \u00eatre diff\u00e9rent de votre code PIN actuel",
  "You are still signed in. The app tries again on its own.": "Votre session reste ouverte. L'application r\u00e9essaie d'elle-m\u00eame.",
  // Step 290: the round
  "Part {n}: {title}": "Partie {n}\u00a0: {title}",
  "Part {n}": "Partie {n}",
  "Start reading": "Commencer la lecture",
  "See the designed version": "Voir la version mise en page",
  "The designed version did not open. Try again.": "La version mise en page ne s'est pas ouverte. R\u00e9essayez.",
  "Document number": "Num\u00e9ro du document",
  "Version": "Version",
  "Download my signed page": "T\u00e9l\u00e9charger ma page sign\u00e9e",
  "Your signed page did not open. Try again.": "Votre page sign\u00e9e ne s'est pas ouverte. R\u00e9essayez.",
  "Position and job class": "Poste et cat\u00e9gorie d'emploi",
  "Assigned sites": "Sites assign\u00e9s",
  "Date": "Date",
  "Signed documents": "Documents sign\u00e9s",
  "Signed {date}": "Sign\u00e9 le {date}",
  "Signed in English": "Sign\u00e9 en anglais",
  "Signed in Spanish": "Sign\u00e9 en espagnol",
  "Signed in French": "Sign\u00e9 en fran\u00e7ais",
  "You signed version {version} on {date}.": "Vous avez sign\u00e9 la version {version} le {date}.",
  "A new version is waiting for you under Documents to sign.": "Une nouvelle version vous attend dans Documents \u00e0 signer.",
  "No supply matches that name.": "Aucun article ne correspond \u00e0 ce nom.",
  "App support": "Assistance de l'application",
  "Tell the office about a problem with the app, an idea, or information that is wrong.": "Signalez au bureau un probl\u00e8me avec l'application, une id\u00e9e ou une information erron\u00e9e.",
  "What is it about?": "De quoi s'agit-il\u00a0?",
  "Something is not working": "Quelque chose ne fonctionne pas",
  "An idea for the app": "Une id\u00e9e pour l'application",
  "Information that is wrong": "Une information erron\u00e9e",
  "Help could not answer": "L'aide n'a pas pu r\u00e9pondre",
  "Trouble signing in": "Probl\u00e8me de connexion",
  "Choose what it is about.": "Choisissez le sujet.",
  "Write what happened.": "\u00c9crivez ce qui s'est pass\u00e9.",
  "Say what you were doing and what you saw.": "Dites ce que vous faisiez et ce que vous avez vu.",
  "Screenshot": "Capture d'\u00e9cran",
  "Add a screenshot": "Ajouter une capture d'\u00e9cran",
  "Sent with your ticket": "Envoy\u00e9 avec votre ticket",
  "Screen": "\u00c9cran",
  "App version": "Version de l'application",
  "Phone": "T\u00e9l\u00e9phone",
  "Send to App support": "Envoyer \u00e0 l'assistance de l'application",
  "Sent. The office will look at it.": "Envoy\u00e9. Le bureau va l'examiner.",
  "Your ticket was not sent. Try again.": "Votre ticket n'a pas \u00e9t\u00e9 envoy\u00e9. R\u00e9essayez.",
  "My tickets": "Mes tickets",
  "You have not sent a ticket yet.": "Vous n'avez encore envoy\u00e9 aucun ticket.",
  "New": "Nouveau",
  "Working on it": "En cours",
  "Won't do": "Ne sera pas fait",
  "Note from the office: {note}": "Note du bureau\u00a0: {note}",
  "Ticket for App support": "Ticket pour l'assistance de l'application",
  "Sent to App support. You can follow it under My tickets.": "Envoy\u00e9 \u00e0 l'assistance de l'application. Suivez-le dans Mes tickets.",
  "Can't sign in? Email {email}": "Impossible de vous connecter\u00a0? \u00c9crivez \u00e0 {email}",
  "PTO (paid time off)": "PTO (cong\u00e9 pay\u00e9)",
  "Choose your 4-digit PIN and pick your language.": "Choisissez votre code PIN \u00e0 4 chiffres et votre langue.",
  "4 digits. Not all the same, not in a row like 1234, and not your badge number.": "4 chiffres. Pas tous identiques, pas \u00e0 la suite comme 1234, et pas votre num\u00e9ro de badge.",
  "Get an alert when someone messages you or assigns you an inspection?": "Recevoir une alerte quand quelqu'un vous \u00e9crit ou vous attribue une inspection\u00a0?",
  "This inspection was cancelled.": "Cette inspection a \u00e9t\u00e9 annul\u00e9e.",
  "This inspection is already done.": "Cette inspection est d\u00e9j\u00e0 faite.",
  "This inspection is not on your list anymore.": "Cette inspection n'est plus dans votre liste.",
  "Assigned to you": "Assign\u00e9 \u00e0 vous",
  "{n} assigned to you": "Assign\u00e9s \u00e0 vous\u00a0: {n}",
  "Issue": "Probl\u00e8me",
  "Inspection finding": "Constat d'inspection",
  "Client request": "Demande de client",
  "Not assigned yet": "Pas encore assign\u00e9",
  "What was done": "Ce qui a \u00e9t\u00e9 fait",
  "Reassign": "R\u00e9attribuer",
  "Assigned to {name}.": "Attribu\u00e9 \u00e0 {name}.",
  "Escalated": "Remont\u00e9",
  "This issue did not load.": "Ce probl\u00e8me ne s'est pas charg\u00e9.",
  "Unfinished forms ({n})": "Formulaires non termin\u00e9s ({n})",
  "Save and finish later": "Enregistrer et terminer plus tard",
  "Saved. You will get a reminder until it is sent.": "Enregistr\u00e9. Vous recevrez un rappel jusqu'\u00e0 son envoi.",
  "Library": "Biblioth\u00e8que",
  "Search by number, title or words in the text": "Rechercher par num\u00e9ro, titre ou mots du texte",
  "Also in Spanish": "Aussi en espagnol",
  "In English only": "En anglais seulement",
  "The library is loading. Check back soon.": "La biblioth\u00e8que est en cours de chargement. Revenez bient\u00f4t.",
  "No document matches that search.": "Aucun document ne correspond \u00e0 cette recherche.",
  "All folders": "Tous les dossiers",
  "This document is waiting for your signature.": "Ce document attend votre signature.",
  "Open in Documents to sign": "Ouvrir dans Documents \u00e0 signer",
  "Open {docCode}": "Ouvrir {docCode}",
  "Approve supplies": "Approuver les fournitures",
  "Supply requests to approve ({n})": "Demandes de fournitures \u00e0 approuver ({n})",
  "Waiting for a decision": "En attente d'une d\u00e9cision",
  "Signed, not sent yet": "Sign\u00e9es, pas encore envoy\u00e9es",
  "Ordered": "Command\u00e9es",
  "Asked by {name}, {when}": "Demand\u00e9 par {name}, {when}",
  "Ready to sign": "Pr\u00eate \u00e0 signer",
  "No supply requests are waiting for you.": "Aucune demande de fournitures ne vous attend.",
  "This request did not load.": "Cette demande ne s'est pas charg\u00e9e.",
  "Quantity to approve": "Quantit\u00e9 \u00e0 approuver",
  "Approve": "Approuver",
  "Deny": "Refuser",
  "Approve all": "Tout approuver",
  "Deny all": "Tout refuser",
  "Change": "Modifier",
  "Only the people who approve supply requests can decide this.": "Seules les personnes qui approuvent les demandes de fournitures peuvent en d\u00e9cider.",
  "You asked for these supplies, so someone else decides them.": "Vous avez demand\u00e9 ces fournitures, donc quelqu'un d'autre d\u00e9cide.",
  "Sign and order": "Signer et commander",
  "Vendor": "Fournisseur",
  "Choose a vendor": "Choisissez un fournisseur",
  "Choose a vendor first.": "Choisissez d'abord un fournisseur.",
  "Contact": "Contact",
  "Email": "Courriel",
  "Deliver to": "Livrer \u00e0",
  "Left blank, the site's address is used.": "Laiss\u00e9 vide, l'adresse du site est utilis\u00e9e.",
  "Ask the office to add the vendor and set it to approved.": "Demandez au bureau d'ajouter le fournisseur et de le marquer comme approuv\u00e9.",
  "The vendor list did not load.": "La liste des fournisseurs ne s'est pas charg\u00e9e.",
  "Purchase order": "Bon de commande",
  "Signed by {name}, {date}": "Sign\u00e9 par {name}, {date}",
  "Open the purchase order": "Ouvrir le bon de commande",
  "The purchase order did not open. Try again.": "Le bon de commande ne s'est pas ouvert. R\u00e9essayez.",
  "Send to {email}": "Envoyer \u00e0 {email}",
  "Email to send it to": "Courriel auquel l'envoyer",
  "Enter a valid email address.": "Saisissez une adresse courriel valide.",
  "Sent to {email}": "Envoy\u00e9 \u00e0 {email}",
  "Ordered {date}, sent to {email}": "Command\u00e9 le {date}, envoy\u00e9 \u00e0 {email}",
  "Send again": "Renvoyer",
  "Site checklist": "Liste du site",
  "Safety walk": "Tourn\u00e9e de s\u00e9curit\u00e9",
  "Next: Safety walk": "Suivant\u00a0: tourn\u00e9e de s\u00e9curit\u00e9",
  "Sign and send": "Signer et envoyer",
  "This signature signs the site checklist and the safety walk.": "Cette signature signe la liste du site et la tourn\u00e9e de s\u00e9curit\u00e9.",
  "Severity {s}": "Gravit\u00e9 {s}",
  "Now": "Maintenant",
  "Residents' meal": "Repas des r\u00e9sidents",
  "Empty, full access": "Vide, acc\u00e8s complet",
  "When there is free time": "Quand il y a du temps libre",
  "Start of shift": "D\u00e9but du service",
  "End of shift": "Fin du service",
  "The space is in use. No steps in this time.": "L'espace est occup\u00e9. Aucune \u00e9tape pendant ce cr\u00e9neau.",
  "Start an inspection": "Commencer une inspection",
  "For today, assigned to you.": "Pour aujourd'hui, \u00e0 votre nom.",
  "Template": "Mod\u00e8le",
  "Which template?": "Quel mod\u00e8le\u00a0?",
  "Include the safety walk": "Inclure la tourn\u00e9e de s\u00e9curit\u00e9",
  "Start the inspection": "Commencer l'inspection",
  "No site matches that name.": "Aucun site ne correspond \u00e0 ce nom.",
  "No inspection template is set up for this site yet. Ask the office.": "Aucun mod\u00e8le d'inspection n'est encore pr\u00e9vu pour ce site. Demandez au bureau.",
  "Choose a site first.": "Choisissez d'abord un site.",
  "Choose a template first.": "Choisissez d'abord un mod\u00e8le.",
  "Inspection started": "Inspection commenc\u00e9e",
  "Task list for this site": "Liste des t\u00e2ches de ce site",
  "Nothing to check off here. This list shows the work our staff do at this site.": "Rien \u00e0 cocher ici. Cette liste montre le travail que notre personnel effectue sur ce site.",
  "This session did not open. Try again.": "Cette s\u00e9ance ne s'est pas ouverte. R\u00e9essayez.",
  "This document did not open. Try again.": "Ce document ne s'est pas ouvert. R\u00e9essayez.",
  "This session did not start. Try again.": "Cette s\u00e9ance n'a pas d\u00e9marr\u00e9. R\u00e9essayez.",
  "The session was not closed. Try again.": "La s\u00e9ance n'a pas \u00e9t\u00e9 cl\u00f4tur\u00e9e. R\u00e9essayez.",
  "This checklist did not start. Try again.": "Cette liste n'a pas commenc\u00e9. R\u00e9essayez.",
  "Issue reported. Its photo did not attach.": "Probl\u00e8me signal\u00e9. Sa photo n'a pas \u00e9t\u00e9 jointe.",
  "Already done by {firstName}": "D\u00e9j\u00e0 fait par {firstName}",
  "Already done": "D\u00e9j\u00e0 fait",
  "The PIN the office gave you has expired. Call the office for a new one, or tap Forgot your PIN? to choose your own by email.": "Le code PIN donn\u00e9 par le bureau a expir\u00e9. Appelez le bureau pour en avoir un nouveau, ou appuyez sur Code PIN oubli\u00e9\u00a0? pour choisir le v\u00f4tre par courriel.",
  "Too many tries today, so your account is locked until the office unlocks it. Call the office, or tap Forgot your PIN? to reset it by email.": "Trop d'essais aujourd'hui\u00a0: votre compte est bloqu\u00e9 jusqu'\u00e0 ce que le bureau le d\u00e9bloque. Appelez le bureau, ou appuyez sur Code PIN oubli\u00e9\u00a0? pour le r\u00e9initialiser par courriel.",
  "OCSA could not check your sign-in just now. Try again in a moment.": "OCSA n'a pas pu v\u00e9rifier votre connexion pour le moment. R\u00e9essayez dans un instant.",
  "This photo could not be saved. Try again, and tell the office if it happens again.": "Cette photo n'a pas pu \u00eatre enregistr\u00e9e. R\u00e9essayez, et pr\u00e9venez le bureau si cela se reproduit.",
  "This report could not be sent as it was. Tap Submit Issue again.": "Ce signalement n'a pas pu partir tel quel. Appuyez de nouveau sur Envoyer le probl\u00e8me.",
  "This task is already finished or marked as unable to finish, so there is nothing more to send.": "Cette t\u00e2che est d\u00e9j\u00e0 termin\u00e9e ou marqu\u00e9e comme impossible \u00e0 terminer\u00a0: il n'y a plus rien \u00e0 envoyer.",
  "The problem behind this task is already closed, so there is nothing more to send.": "Le probl\u00e8me li\u00e9 \u00e0 cette t\u00e2che est d\u00e9j\u00e0 ferm\u00e9\u00a0: il n'y a plus rien \u00e0 envoyer.",
  "You have time off approved that day, so this shift cannot be yours. Pick a shift on another day.": "Vous avez un cong\u00e9 approuv\u00e9 ce jour-l\u00e0\u00a0: ce service ne peut pas \u00eatre le v\u00f4tre. Choisissez un service un autre jour.",
  "Your shift end is saved on this phone, with the time you tapped it. It sends when you have signal.": "La fin de votre service est enregistr\u00e9e sur ce t\u00e9l\u00e9phone, \u00e0 l'heure o\u00f9 vous l'avez touch\u00e9e. Elle part d\u00e8s que vous avez du r\u00e9seau.",
  "Case": "Dossier",
  "Read only. The case is worked on the dashboard.": "Lecture seule. Le dossier se traite sur le tableau de bord.",
  "This case did not open. Try again.": "Ce dossier ne s'est pas ouvert. R\u00e9essayez.",
  "Case open": "Dossier ouvert",
  "Case in review": "Dossier en cours d'examen",
  "Case escalated": "Dossier transmis",
  "Case resolved": "Dossier r\u00e9solu",
  "Case closed": "Dossier ferm\u00e9",
  "Opened": "Ouvert le",
  "Filed by": "D\u00e9pos\u00e9 par",
  "About": "Concerne",
  "Escalated to": "Transmis \u00e0",
  "What was reported": "Ce qui a \u00e9t\u00e9 signal\u00e9",
  "Resolution": "R\u00e9solution",
  "Updates": "Suivi",
  "Client concern": "Pr\u00e9occupation du client",
  "Read only. The office answers the client from the dashboard.": "Lecture seule. Le bureau r\u00e9pond au client depuis le tableau de bord.",
  "This report did not open. Try again.": "Ce rapport ne s'est pas ouvert. R\u00e9essayez.",
  // Part 356: the others on shift at this site now, at the top of the checklist.
  "Also here: {names}": "Aussi ici : {names}",
  // Part 359: My reports on Speak Up (API-63).
  "My reports": "Mes signalements",
  "Each report you sent here, or the office opened for you, and where it stands.": "Chaque signalement que vous avez envoy\u00e9 ici, ou que le bureau a ouvert pour vous, et o\u00f9 il en est.",
  "Your reports did not load.": "Vos signalements ne se sont pas charg\u00e9s.",
  "You have no reports yet.": "Vous n'avez encore aucun signalement.",
  "Sent {date}": "Envoy\u00e9 le {date}",
  "Updated {date}": "Mis \u00e0 jour le {date}",
  "Report received": "Signalement re\u00e7u",
  "Being looked into": "En cours d'examen",
  "Report resolved": "Signalement r\u00e9solu",
  "Report closed": "Signalement ferm\u00e9",
  // Part 359: a filed report opened from its notice (API-83).
  "Filed report": "Rapport envoy\u00e9",
  "Read only. The report is worked on the dashboard.": "Lecture seule. Le rapport est trait\u00e9 depuis le tableau de bord.",
  // Part 359: Register (API-117).
  "Thank you. A supervisor reviews each registration, and OCSA emails you once you can sign in.": "Merci. Un superviseur examine chaque inscription, et OCSA vous envoie un courriel d\u00e8s que vous pouvez vous connecter.",
  // Part 359: the refusals new since Round 2.
  "This shift already has a drop request, or it is already posted for pickup. There is nothing more to send.": "Ce service a d\u00e9j\u00e0 une demande de retrait, ou il est d\u00e9j\u00e0 propos\u00e9 comme service libre. Il n'y a rien de plus \u00e0 envoyer.",
  "What you sent is too large. Use fewer or smaller photos, or shorter text, then try again.": "Ce que vous avez envoy\u00e9 est trop volumineux. Utilisez moins de photos ou des photos plus petites, ou un texte plus court, puis r\u00e9essayez.",
  "What you sent could not be read. Try again, and tell the office if it happens again.": "Ce que vous avez envoy\u00e9 n'a pas pu \u00eatre lu. R\u00e9essayez, et pr\u00e9venez le bureau si cela se reproduit.",
  "This file is over {mb} MB. Choose a smaller photo or file.": "Ce fichier d\u00e9passe {mb} Mo. Choisissez une photo ou un fichier plus petit.",
  "This file is too large. Choose a smaller photo or file.": "Ce fichier est trop volumineux. Choisissez une photo ou un fichier plus petit.",
  "This question takes {max} photos at most. Remove one to add another.": "Cette question accepte au plus {max} photos. Retirez-en une pour en ajouter une autre.",
  "This photo was already removed. The question now shows the photos that are kept.": "Cette photo a d\u00e9j\u00e0 \u00e9t\u00e9 retir\u00e9e. La question montre maintenant les photos gard\u00e9es.",
  "Your shift did not load.": "Votre service ne s'est pas charg\u00e9.",
  "Time off did not load.": "Les cong\u00e9s ne se sont pas charg\u00e9s.",
  "Your time off requests did not load.": "Vos demandes de cong\u00e9 ne se sont pas charg\u00e9es.",
  "Your latest answers did not save. Keep filling to try again, or leave without them.": "Vos derni\u00e8res r\u00e9ponses n'ont pas \u00e9t\u00e9 enregistr\u00e9es. Continuez \u00e0 remplir pour r\u00e9essayer, ou quittez sans elles.",
  "Leave without saving": "Quitter sans enregistrer",
  "Alerts need iOS 16.4 or later. Update this iPhone in Settings, General, Software Update.": "Les alertes demandent iOS 16.4 ou une version plus r\u00e9cente. Mettez cet iPhone \u00e0 jour dans R\u00e9glages, G\u00e9n\u00e9ral, Mise \u00e0 jour logicielle.",
  "On an iPhone, alerts come only to the app on your Home Screen. Add it with Share, then Add to Home Screen, and open it from there to turn alerts on.": "Sur un iPhone, les alertes n'arrivent qu'\u00e0 l'application de votre \u00e9cran d'accueil. Ajoutez-la avec \u00ab\u00a0Partager\u00a0\u00bb, puis \u00ab\u00a0Sur l'\u00e9cran d'accueil\u00a0\u00bb, et ouvrez-la depuis cet \u00e9cran pour activer les alertes.",
  "Alerts on an iPhone": "Les alertes sur un iPhone",
  "Got it": "Compris",
  "Alerts on an iPhone come only to the app added this way.": "Sur un iPhone, les alertes n'arrivent qu'\u00e0 l'application ajout\u00e9e de cette fa\u00e7on.",
  "You have a new notice. Tap the bell to read it.": "Vous avez un nouvel avis. Appuyez sur la cloche pour le lire.",
  "This shift is no longer on your schedule.": "Ce service n'est plus dans votre planning.",
  "This photo could not be sent with the task. Take the photo again, then send it.": "Cette photo n'a pas pu \u00eatre envoy\u00e9e avec la t\u00e2che. Reprenez la photo, puis envoyez-la.",
  "The note is too long. Keep it to {max} characters.": "La note est trop longue. Limitez-la \u00e0 {max} caract\u00e8res.",
  "This request was not sent.": "Cette demande n'a pas \u00e9t\u00e9 envoy\u00e9e.",
  "Sent to the building as {reference}.": "Envoy\u00e9e au b\u00e2timent sous le num\u00e9ro {reference}.",
  "Send to building maintenance": "Envoyer \u00e0 l'entretien du b\u00e2timent",
  "Floor {floor}": "\u00c9tage {floor}",
  "Sent today": "Envoy\u00e9e aujourd'hui",
  "Sent 1 day ago": "Envoy\u00e9e il y a 1 jour",
  "Sent {n} days ago": "Envoy\u00e9e il y a {n} jours",
  "The photos kept with this request are no longer on this phone. Take them again, then send it.": "Les photos gard\u00e9es avec cette demande ne sont plus sur ce t\u00e9l\u00e9phone. Reprenez-les, puis envoyez-la.",
  "Sent to the building.": "Envoy\u00e9e au b\u00e2timent.",
  "New request": "Nouvelle demande",
  "This request was not sent. {reason}": "Cette demande n'a pas \u00e9t\u00e9 envoy\u00e9e. {reason}",
  "Change and send": "Modifier et envoyer",
  "Requests at this site": "Demandes pour ce site",
  "No requests sent to the building yet.": "Aucune demande envoy\u00e9e au b\u00e2timent pour l'instant.",
  "No answer yet": "Pas encore de r\u00e9ponse",
  "A request takes {max} photos at most.": "Une demande accepte au plus {max} photos.",
  "Choose what kind of problem it is.": "Choisissez le type de probl\u00e8me.",
  "Choose how soon it needs fixing.": "Choisissez dans quel d\u00e9lai il faut le r\u00e9parer.",
  "Say what is wrong.": "Dites ce qui ne va pas.",
  "New request to building maintenance": "Nouvelle demande \u00e0 l'entretien du b\u00e2timent",
  "This issue was already sent to the building as {reference}.": "Ce probl\u00e8me a d\u00e9j\u00e0 \u00e9t\u00e9 envoy\u00e9 au b\u00e2timent sous le num\u00e9ro {reference}.",
  "Open the request": "Ouvrir la demande",
  "The request form did not load. It needs signal the first time.": "Le formulaire de demande ne s'est pas charg\u00e9. Il a besoin du r\u00e9seau la premi\u00e8re fois.",
  "Area": "Zone",
  "Kind of problem": "Type de probl\u00e8me",
  "Choose": "Choisir",
  "Send the issue's photos too:": "Envoyer aussi les photos du probl\u00e8me\u00a0:",
  "{n} of {max} photos": "{n} sur {max} photos",
  "It goes to": "Elle sera envoy\u00e9e \u00e0",
  "Who it goes to did not load.": "Les destinataires ne se sont pas charg\u00e9s.",
  "This site has no maintenance contact yet. Ask the office to add one before sending.": "Ce site n'a pas encore de contact d'entretien. Demandez au bureau d'en ajouter un avant d'envoyer.",
  "This list is the one this phone last read. The request goes to whoever is on the site's list when it is sent.": "Cette liste est celle que ce t\u00e9l\u00e9phone a lue la derni\u00e8re fois. La demande part aux personnes inscrites sur la liste du site au moment de l'envoi.",
  "Choose how the building answered.": "Choisissez comment le b\u00e2timent a r\u00e9pondu.",
  "Choose where the request stands now.": "Choisissez o\u00f9 en est la demande maintenant.",
  "Write the note first.": "\u00c9crivez d'abord la note.",
  "Nothing changed. The request is already at that status.": "Rien n'a chang\u00e9. La demande a d\u00e9j\u00e0 ce statut.",
  "Saved.": "Enregistr\u00e9.",
  "Follow-up sent to the building.": "Relance envoy\u00e9e au b\u00e2timent.",
  "Sent to the building again.": "Envoy\u00e9e de nouveau au b\u00e2timent.",
  "Maintenance request": "Demande d'entretien",
  "Sent {date} by {name}": "Envoy\u00e9e le {date} par {name}",
  "No answer from the building yet. It was asked to answer by {date}.": "Le b\u00e2timent n'a pas encore r\u00e9pondu. Il devait r\u00e9pondre au plus tard le {date}.",
  "The building is asked to answer by {date}.": "Le b\u00e2timent doit r\u00e9pondre au plus tard le {date}.",
  "Scheduled for {date}": "Pr\u00e9vue le {date}",
  "Emailed to the building {when}.": "Envoy\u00e9e par courriel au b\u00e2timent {when}.",
  "The email to the building did not go.": "Le courriel au b\u00e2timent n'est pas parti.",
  "Sent to": "Envoy\u00e9e \u00e0",
  "Record a response": "Noter une r\u00e9ponse",
  "Change the status": "Changer le statut",
  "Add a note": "Ajouter une note",
  "Send the email again": "Renvoyer le courriel",
  "Send a follow-up": "Envoyer une relance",
  "How the building answered": "Comment le b\u00e2timent a r\u00e9pondu",
  "Where it stands now": "O\u00f9 elle en est",
  "The day they will come": "Le jour de leur passage",
  "What they said": "Ce qu'ils ont dit",
  "Building maintenance": "Entretien du b\u00e2timent",
  "Send the building a repair request, and keep track of what it answers.": "Envoyez au b\u00e2timent une demande de r\u00e9paration et suivez ses r\u00e9ponses.",
  "That did not save. Try again.": "Cela ne s'est pas enregistr\u00e9. R\u00e9essayez.",
  "{n} day": "{n} jour",
  "{n} days": "{n} jours",
  "{kind}: {hours} went back when the office took back the approval.": "{kind}\u00a0: {hours} ont \u00e9t\u00e9 rendues quand le bureau a retir\u00e9 l'accord.",
  "{kind} balance now: {hours}.": "Solde de {kind} maintenant\u00a0: {hours}.",
  "{kind}: this time off took {hours}.": "{kind}\u00a0: ce cong\u00e9 a utilis\u00e9 {hours}.",
  "{kind}: this request is {hours}, and would leave {left}.": "{kind}\u00a0: cette demande est de {hours} et laisserait {left}.",
  "Your request is sent. The person who approves time off decides.": "Votre demande est envoy\u00e9e. La personne qui approuve les cong\u00e9s d\u00e9cide.",
  "Leave balances": "Soldes de cong\u00e9s",
  "You can use it from {date}.": "Vous pouvez l'utiliser \u00e0 partir du {date}.",
  "Your leave balances did not load.": "Vos soldes de cong\u00e9s ne se sont pas charg\u00e9s.",
  "Once you send it, the request shows what it would leave.": "Une fois envoy\u00e9e, la demande indique ce qu'elle laisserait.",
};

// {name} is replaced after the language is chosen, so the
// Spanish decides where a value lands rather than the English
// word order.
function fill(text, vars) {
  if (!vars) return text;
  return String(text).replace(/\{(\w+)\}/g, function (whole, key) {
    return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : whole;
  });
}

// The one call every screen makes. Anything that is not a string
// comes back untouched, so a null or a number passed by mistake
// renders the way it did before.
export function tr(text, vars) {
  if (typeof text !== "string") return text;
  const table = WORDS[current];
  const out = table && Object.prototype.hasOwnProperty.call(table, text) ? table[text] : text;
  return fill(out, vars);
}

// What every date and time formatter passes. French reads its
// dates in French and keeps the 12-hour clock OCSA writes every
// shift in: jeudi 1 octobre 2026, 9:30 PM.
export function dateLocale() {
  if (current === "fr") return "fr-FR-u-hc-h12";
  return current === "es" ? "es-US" : "en-US";
}
